/**
 * ============================================================
 *  GOOP ZONE — weaponSystem.ts
 *  Runtime Weapon & Projectile Management
 * ============================================================
 *
 *  Handles all weapon firing, projectile movement, collision
 *  detection with enemies, and rendering.
 *
 *  Called every frame by engine.ts update() and render().
 *
 *  Drop into: client/src/game/weaponSystem.ts
 * ============================================================
 */

import {
  type WeaponDefinition,
  type WeaponInstance,
  type WeaponRarity,
  type ProjectileShape,
  getWeaponDef,
  computeWeaponStats,
  createWeaponInstance,
  RARITY_MULTIPLIERS,
} from './weapons';
import { resolveAsset } from './uiAssets';
// BETA 22.21 — multishot mod: extra simultaneous projectiles for an armed gun.
import { weaponModMultishot } from './weaponModStats';
// BETA 22.21 — in-world MODDED sprite. The combo art is normalized to the base
// revolver's framing (512×286, barrel tip @ x500), so the base barrelLength
// calibration lands on the barrel and the kitted gun you built shows in the run.
import { isArmed } from './weaponArmed';
import { equippedCodes } from './weaponModLoadout';
import { modComboImage } from './weaponMods';
import { REVOLVER_MOD_MUZZLE } from './weaponModMuzzles';
// BETA 22.69 — Weapon Feel Beta A: per-weapon sockets + aim/recoil/bob feel.
import { resolveWeaponVisual, type ResolvedWeaponVisual } from './weaponVisualConfig';
import { debugWeaponSockets } from './debugState';
// BETA 22.70 — Weapon Feel Beta B: per-weapon attack visuals (custom bodies,
// trail suppression). Leaf module, no cycle.
import { getWeaponVfx, drawFlameCone, spawnFireSignature } from './weaponVfx';
import { tuneOf } from './bunchaBullshiiii';   // BETA 21.81 — editable Drac's Gas Mask lifesteal boost
// BETA 15.26 — character passive hooks called from the firing/bounce paths.
// onPlayerFireProjectile: Killa lastFireTime + Cowboy Quick Draw flag.
// onProjectileBounce: LeBron King's Court — compounding 1.25× per bounce.
import { onPlayerFireProjectile, onProjectileBounce, creditCowboyQuickDraw } from './characterPassives';
// BETA 15.74 — Dylan-provided cat fighting sound for kitten attacks +
// Ultra Beam fire audio (plays at charge-up start so BLAM at end of audio
// aligns with projectile spawn 0.737s later).
// BETA 15.75 — Dylan-provided poop sling hit sound.
// BETA 19.67 — playPoopSlingHit kept in import in case Dylan reverses the
// hit-sound removal. Currently unused (hit sound dropped per Dylan).
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { playKittensAttack, playUltraBeamFire, playPoopSlingHit } from './gameSfx';

// ─── Constants ────────────────────────────────────────────────
const PROJECTILE_MAX = 500;           // Max active projectiles (performance cap)
// BETA 21.41 — Dylan: "no cap on fire rate, upgrade to infinity." Was 0.1s (10/sec)
// which silently ate all attack-speed past the wall. Now ~1 frame (60/sec) — effectively
// uncapped (you can't fire faster than the frame anyway), but NOT 0 so timing can't break.
const MIN_WEAPON_COOLDOWN = 0.016;    // ~1 frame @60fps — effectively uncapped fire rate
const TRAIL_SEGMENTS = 5;             // Number of trail segments per projectile
const TRAIL_FADE_RATE = 0.15;         // Alpha reduction per trail segment
const MUZZLE_FLASH_DURATION = 0.12;   // BETA 20.69 — was 0.06: blink-and-miss (VFX audit: brain never registered the shot). Two-layer ring render makes it read as a discharge.
const CRIT_FLASH_DURATION = 0.15;     // Seconds of crit number display

// BETA 18.18 — Hard cap on simultaneous ground effects (SHIT puddles, banana
// traps, whoopie cushions, weapon AoE pools). At 5-stack SHIT (drops every
// ~0.5s, 8s lifetime) you could have 80 puddles alive. Each frame loops every
// puddle × every enemy for slow/DoT checks (~180 enemies × 80 puddles = 14K
// distance checks PER FRAME, before damage tick logic). Dylan reported FPS
// dropping to 1 in dominating runs — this is the smoking gun. FIFO eviction
// at 60 keeps the worst case manageable while still letting full builds feel
// dense. New spawns push the oldest effect out.
const MAX_GROUND_EFFECTS = 60;

// Orbit weapon visuals
// BETA 16.35 — 3-ring orbit system. Dylan: "drone outside of wepons,
// bungulators inside of wepons make sens. like 3 rins 1 2 3."
//   Inner ring  — Bungulator minions (closer to player, melee).
//   Middle ring — Held weapons (the rings the player sees as their kit).
//   Outer ring  — Karen Drone orbital weapon (orbital flag in weapons.ts).
// Each radius gives a clear visual lane — bungulators no longer get
// covered by weapons from above (they sit inside the weapon ring).
export const BUNGULATOR_ORBIT_RADIUS = 56;
export const ORBIT_RADIUS = 86;          // Held weapon ring (was 78 in 16.33; +8 for room around the inner bung ring)
export const KAREN_DRONE_ORBIT_RADIUS = 122; // Karen Drones (overrides def.orbitRadius default)
const ORBIT_DRIFT_SPEED = 0.50;
// BETA 22.72 — Firestarter flame cone geometry (hitbox AND visual share these
// so what burns is exactly what you see).
export const FLAME_CONE_LEN = 190;
export const FLAME_CONE_HALF_ANGLE = 0.38;       // Radians/sec — BETA 16.77: doubled per Dylan ("weapons around character should spin around my charaacter 2x faster"). Was 0.25.

// ─── Weapon Sprite Cache ──────────────────────────────────────
// Lazy-loaded HTMLImageElements keyed by sprite URL.
const weaponSpriteCache: Map<string, HTMLImageElement> = new Map();

// BETA 20.02 — Preload ALL weapon sprites at game start (called from initGame).
// Was lazy-loaded on first weapon fire = stutter on first projectile of each
// weapon. Cycles WEAPON_DATABASE and pre-warms every sprite URL it references.
export function preloadAllWeaponSprites(): void {
  // Lazy import to avoid circular dep at module load.
  // BETA 20.09 — FIX: the 20.02 version read def.sprite / def.iconUrl /
  // def.projectileSprite — NONE of which exist on WeaponDefinition. The real
  // fields are def.spriteUrl, def.firedSpriteUrl, and def.projectile.spriteUrl
  // (+ projectileAnimFrames). So weapon sprites were never actually preloading.
  // Now reads the correct fields. This is why the Karen purse + others still
  // stuttered on first fire.
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { WEAPON_DATABASE } = require('./weapons') as {
    WEAPON_DATABASE: Array<{
      spriteUrl?: string;
      firedSpriteUrl?: string;
      projectile?: { spriteUrl?: string };
      projectileAnimFrames?: string[];
    }>
  };
  for (const def of WEAPON_DATABASE) {
    if (def.spriteUrl) getWeaponSprite(def.spriteUrl);
    if (def.firedSpriteUrl) getWeaponSprite(def.firedSpriteUrl);
    if (def.projectile?.spriteUrl) getWeaponSprite(def.projectile.spriteUrl);
    if (def.projectileAnimFrames) {
      for (const frame of def.projectileAnimFrames) getWeaponSprite(frame);
    }
  }
}
// Caches natural pixel dimensions once an image loads (for barrel-tip math).
const spriteDimCache: Map<string, { w: number; h: number }> = new Map();

function getWeaponSprite(url: string): HTMLImageElement | null {
  if (!url) return null;
  const resolved = resolveAsset(url);
  let img = weaponSpriteCache.get(resolved);
  if (!img) {
    img = new Image();
    img.onload = () => {
      if (img) {
        // BETA 17.28 — pre-decode weapon sprites off-thread (was part
        // of 1604ms Image decode in Dylan's DevTools profile).
        img.decode?.().catch(() => { /* fallback to lazy decode */ });
        spriteDimCache.set(resolved, { w: img.naturalWidth, h: img.naturalHeight });
      }
    };
    img.src = resolved;
    weaponSpriteCache.set(resolved, img);
  }
  if (img.complete && img.naturalWidth > 0) {
    if (!spriteDimCache.has(resolved)) {
      spriteDimCache.set(resolved, { w: img.naturalWidth, h: img.naturalHeight });
    }
    return img;
  }
  return null;
}

/**
 * BETA 22.44 — MODDED-gun barrel tip. When a gun goes into the run as its
 * kitted combo, the combo art has DIFFERENT barrel geometry than the base
 * (e.g. the multishot mod adds a low triple-barrel cluster; a scope rides up
 * top), so the base's hand-calibrated barrelLength can't point at the visible
 * muzzle — that's why the flash landed mid-gun. Instead we read THIS combo's
 * own muzzle (auto-measured into REVOLVER_MOD_MUZZLE as a [fx,fy] fraction of
 * the 1024×576 sprite) and run it through the SAME transform the held-weapon
 * renderer uses to draw the gun (anchor -sprW*0.35 / -sprH/2, flipY on the
 * left half of the orbit, then rotate by aim). The result is the exact world
 * point of the drawn muzzle, so the bullet + flash + LoS origin all leave the
 * barrel you can see. Returns null when the gun isn't running modded → caller
 * falls back to def.barrelLength (base + every non-mod weapon untouched).
 */
// BETA 22.78 — Dylan: held weapons need to be bigger on the player. One knob
// scales BOTH the held-weapon render AND the muzzle socket/barrel offset
// together, so the guns grow but the flash + projectile still leave the barrel
// tip (the calibration stays glued). Visual/feel only — range is player-
// centered, so zero balance/telemetry impact.
// BETA 22.80 — bumped 1.25 → 1.7 (Dylan picked "noticeably bigger": gun ≈ the
// character's height, so the detailed new art reads clearly in-game).
const HELD_WEAPON_SCALE = 1.7;

// BETA 22.78 — Dylan: "weapons need some dropshadow in game on them." Perf-safe
// (no shadowBlur — the 13.70 rule): bake a flat BLACK silhouette of each weapon
// sprite ONCE (source-in fill), cache by url, then blit it offset + translucent
// behind the gun every frame. Cheap (one drawImage), reads as depth.
const _weaponShadowCache = new Map<string, HTMLCanvasElement | null>();
function getWeaponShadow(url: string, img: HTMLImageElement): HTMLCanvasElement | null {
  let s = _weaponShadowCache.get(url);
  if (s === undefined) {
    try {
      const c = document.createElement('canvas');
      c.width = img.naturalWidth; c.height = img.naturalHeight;
      const g = c.getContext('2d');
      if (!g) { _weaponShadowCache.set(url, null); return null; }
      g.drawImage(img, 0, 0);
      g.globalCompositeOperation = 'source-in';
      g.fillStyle = '#000';
      g.fillRect(0, 0, c.width, c.height);
      s = c;
    } catch { s = null; }
    _weaponShadowCache.set(url, s);
  }
  return s;
}

function moddedBarrelTipOffset(
  def: WeaponDefinition,
  aimAngle: number,
  weapon: WeaponInstance,
): { dx: number; dy: number } | null {
  let codes: string[];
  try {
    if (!isArmed(weapon.definitionId)) return null;
    codes = equippedCodes(weapon.definitionId);
  } catch { return null; }
  if (!codes || codes.length === 0) return null;
  const m = REVOLVER_MOD_MUZZLE[[...codes].sort().join('_')];
  if (!m) return null;
  const [fx, fy] = m;
  // Mirror the render: sprH is the world height (spriteSize × spriteScale),
  // width follows the combo art aspect — 1024×576 for the BETA 23.63 cowboy
  // repaint set (all 63 files verified uniform). Must match the render's
  // naturalWidth/naturalHeight aspect or the muzzle drifts off the drawn tip.
  // (revolver has no spriteRotationOffset.)
  const sprH = def.spriteSize * (def.spriteScale ?? 1) * HELD_WEAPON_SCALE;
  const sprW = sprH * (1024 / 576);
  const flipX = def.spriteFacesLeft ? -1 : 1;
  const flipY = Math.cos(aimAngle) < 0 ? -1 : 1; // gun flips upright on the left half
  const lx = flipX * sprW * (fx - 0.35);
  const ly = flipY * sprH * (fy - 0.5);
  const cos = Math.cos(aimAngle), sin = Math.sin(aimAngle);
  return { dx: lx * cos - ly * sin, dy: lx * sin + ly * cos };
}

/**
 * BETA 22.69 — THE one true socket transform. Converts a socket stored as a
 * PNG fraction [fx,fy] of the weapon sprite into a world-space offset from
 * the orbit slot, mirroring EXACTLY the held-weapon render transform
 * (renderOrbitWeapons): anchor -sprW*0.35 / -sprH/2, flipX for
 * spriteFacesLeft, flipY on the left half of the orbit, counter-rotation by
 * spriteRotationOffset, then rotate by aim. Both the projectile spawn AND
 * the debug overlay call this, so the muzzle math and the drawn pixels can
 * never drift apart (the BETA 22.44 "flash mid-gun" bug class).
 * Returns null while the sprite's natural dimensions haven't loaded yet
 * (first frames) — callers fall back to the legacy barrelLength path.
 */
function socketFractionToWorldOffset(
  def: WeaponDefinition,
  fx: number,
  fy: number,
  aimAngle: number,
): { dx: number; dy: number } | null {
  const dims = spriteDimCache.get(resolveAsset(def.spriteUrl));
  if (!dims || dims.h <= 0) return null;
  const sprH = def.spriteSize * (def.spriteScale ?? 1) * HELD_WEAPON_SCALE;
  const sprW = sprH * (dims.w / dims.h);
  const flipX = def.spriteFacesLeft ? -1 : 1;
  const flipY = Math.cos(aimAngle) < 0 ? -1 : 1;
  const lx = flipX * sprW * (fx - 0.35);
  const ly = flipY * sprH * (fy - 0.5);
  const ang = aimAngle - (def.spriteRotationOffset ?? 0);
  const cos = Math.cos(ang), sin = Math.sin(ang);
  return { dx: lx * cos - ly * sin, dy: lx * sin + ly * cos };
}

/**
 * Returns the world-space offset from the orbit slot to the barrel tip.
 * Priority (BETA 22.69):
 *   1. Modded combo's measured muzzle (REVOLVER_MOD_MUZZLE, BETA 22.44).
 *   2. WEAPON_VISUALS muzzle socket (PNG fraction, gen-weapon-sockets.mjs).
 *   3. Legacy def.barrelLength + def.barrelOffsetY (world units) — untuned
 *      weapons + first frames before sprite dims load.
 */
function getBarrelTipOffset(
  def: WeaponDefinition,
  aimAngle: number,
  weapon?: WeaponInstance,
): { dx: number; dy: number } {
  if (weapon) {
    const modded = moddedBarrelTipOffset(def, aimAngle, weapon);
    if (modded) return modded;
  }
  const vis = resolveWeaponVisual(def.id);
  if (vis.muzzleFx !== undefined && vis.muzzleFy !== undefined) {
    const socket = socketFractionToWorldOffset(def, vis.muzzleFx, vis.muzzleFy, aimAngle);
    if (socket) return socket;
  }
  const perpAngle = aimAngle + Math.PI / 2;
  const _bl = def.barrelLength * HELD_WEAPON_SCALE;
  const offY = (def.barrelOffsetY ?? 0) * HELD_WEAPON_SCALE;
  return {
    dx: Math.cos(aimAngle) * _bl + Math.cos(perpAngle) * offY,
    dy: Math.sin(aimAngle) * _bl + Math.sin(perpAngle) * offY,
  };
}

// BETA 22.70 — Firestarter continuous-hose ember clock (rate limiter).
let _flameEmberLast = 0;

// ─── Math Helpers ─────────────────────────────────────────────
/** Shortest-path angle interpolation. */
function lerpAngle(from: number, to: number, t: number): number {
  let diff = to - from;
  while (diff > Math.PI) diff -= Math.PI * 2;
  while (diff < -Math.PI) diff += Math.PI * 2;
  return from + diff * t;
}

/** Slot angle for weapon at index `idx` of `total` slots (drifts slowly over time). */
function slotAngle(idx: number, total: number, time: number): number {
  return (idx / Math.max(1, total)) * Math.PI * 2 + time * ORBIT_DRIFT_SPEED;
}

/** Compute world position of a weapon's orbit slot. */
// ─── BETA 23.01 · WEAPON DEPTH & SMART PLACEMENT (Dylan spec) ───────────────
// Solid props register VISUAL blocking zones each frame (engine feeds them
// from nearby Wild Cards machines; P5 adds every prop). If a weapon's orbit
// point lands inside a zone, the slide steers it out toward the nearest edge
// — capped, lerped, and decaying back to the pure orbit in open space. The
// slide is baked into getWeaponSlotPosition, so RENDER and FIRING both see
// the displaced gun: bullets always leave the drawn barrel.
export interface WeaponOcclusionZone { x0: number; y0: number; x1: number; y1: number }
let _occlusionZones: WeaponOcclusionZone[] = [];
export function setWeaponOcclusionZones(zones: WeaponOcclusionZone[]): void {
  _occlusionZones = zones;
}

const WEAPON_HALF = 24;      // weapon sprite half-extent (wu) for overlap tests
const MAX_SLIDE = 48;        // rule: limited max offset — beyond this, partial hide is fine

/** Per-frame slide targeting + smoothing (called from updateWeapons). */
function _updateWeaponSlides(state: WeaponSystemState, dt: number, player: { x: number; y: number }): void {
  const k = Math.min(1, 10 * dt);    // rule 5: smooth, no snapping
  for (const weapon of state.weapons) {
    const def = getWeaponDef(weapon.definitionId);
    if (!def || def.orbital || def.aura || def.arc) continue;   // slots only
    const raw = _rawSlotPosition(state, weapon, player);
    let tx = 0, ty = 0;
    for (const z of _occlusionZones) {
      if (raw.x > z.x0 - WEAPON_HALF && raw.x < z.x1 + WEAPON_HALF &&
          raw.y > z.y0 - WEAPON_HALF && raw.y < z.y1 + WEAPON_HALF) {
        // shortest exit from the expanded zone
        const exits = [
          { dx: (z.x0 - WEAPON_HALF) - raw.x, dy: 0 },
          { dx: (z.x1 + WEAPON_HALF) - raw.x, dy: 0 },
          { dx: 0, dy: (z.y0 - WEAPON_HALF) - raw.y },
          { dx: 0, dy: (z.y1 + WEAPON_HALF) - raw.y },
        ];
        exits.sort((a, b) => (Math.abs(a.dx) + Math.abs(a.dy)) - (Math.abs(b.dx) + Math.abs(b.dy)));
        tx = exits[0].dx; ty = exits[0].dy;
        break;   // POI spacing guarantees at most one relevant zone
      }
    }
    const len = Math.hypot(tx, ty);
    if (len > MAX_SLIDE) { tx *= MAX_SLIDE / len; ty *= MAX_SLIDE / len; }   // rule 6
    weapon.slideX = (weapon.slideX ?? 0) + (tx - (weapon.slideX ?? 0)) * k;
    weapon.slideY = (weapon.slideY ?? 0) + (ty - (weapon.slideY ?? 0)) * k;  // rule 7: decays to 0
  }
}

export function getWeaponSlotPosition(
  state: WeaponSystemState,
  weapon: WeaponInstance,
  player: { x: number; y: number },
): { x: number; y: number; angle: number } {
  // BETA 23.01 — public position = raw orbit + smart-slide offset (render
  // AND firing both read this, so the muzzle stays on the drawn gun).
  const raw = _rawSlotPosition(state, weapon, player);
  const sx = weapon.slideX ?? 0, sy = weapon.slideY ?? 0;
  if (sx === 0 && sy === 0) return raw;
  return { x: raw.x + sx, y: raw.y + sy, angle: raw.angle };
}

function _rawSlotPosition(
  state: WeaponSystemState,
  weapon: WeaponInstance,
  player: { x: number; y: number },
): { x: number; y: number; angle: number } {
  const idx = state.weapons.indexOf(weapon);
  const total = state.weapons.length;
  // BETA 13.96 — orbital weapons (Karen Drones) fly out at their own radius + speed.
  // They don't share the slot system with normal weapons; each orbital has its own
  // independent angle based on its slot index plus elapsed time.
  const def = getWeaponDef(weapon.definitionId);
  if (def?.orbital) {
    // BETA 16.35 — outer ring of the 3-ring orbit system. Even if the
    // weapon def hardcodes a smaller orbitRadius, force orbitals out
    // past the weapon ring so they read as their own concentric circle.
    const radius = Math.max(KAREN_DRONE_ORBIT_RADIUS, def.orbitRadius ?? 90);
    const speed = def.orbitSpeed ?? 1.6;
    const baseAngle = (idx / Math.max(1, total)) * Math.PI * 2;
    const a = baseAngle + state.elapsedTime * speed;
    return {
      x: player.x + Math.cos(a) * radius,
      y: player.y + Math.sin(a) * radius,
      angle: a,
    };
  }
  const a = slotAngle(idx, total, state.elapsedTime);
  return {
    x: player.x + Math.cos(a) * ORBIT_RADIUS,
    y: player.y + Math.sin(a) * ORBIT_RADIUS,
    angle: a,
  };
}

// BETA 16.24 — PSBOT cycle-25 P0.1 + P0.2: Karen Drones multi-drone +
// attack-speed-scaled spin. PSBOT cowboy run audit confirmed via
// pickEvents that Dylan had finalProjectileCount=4 but only 1 drone was
// hitting (85 hits / 12593 active frames = ~1 drone's worth). Plus Dylan
// requested attack speed should accelerate orbit.
//
// Behavior:
//   - Total drones = 1 + Math.floor(player.bonusProjectileCount)
//   - Drones distributed evenly around the orbit (2π / total spacing)
//   - Spin speed = def.orbitSpeed × (1 + player.bonusAttackSpeedPercent/100)
//
// Called from contact-damage tick + render. Does NOT replace
// getWeaponSlotPosition for non-orbital weapons or single-drone cases —
// it's an opt-in helper used only for `def.orbital === true`.
export function getOrbitalDronePositions(
  state: WeaponSystemState,
  weapon: WeaponInstance,
  def: { orbital?: boolean; orbitRadius?: number; orbitSpeed?: number },
  player: {
    x: number;
    y: number;
    bonusProjectileCount?: number;
    bonusAttackSpeedPercent?: number;
  },
): Array<{ x: number; y: number; angle: number }> {
  if (!def.orbital) {
    return [getWeaponSlotPosition(state, weapon, player)];
  }
  // BETA 16.35 — match the single-drone path: force out past the
  // weapon ring so multi-drone Karen Drones still hit the OUTER orbit.
  const radius = Math.max(KAREN_DRONE_ORBIT_RADIUS, def.orbitRadius ?? 90);
  const baseSpinSpeed = def.orbitSpeed ?? 1.6;
  // P0.2: attack-speed scaling on orbit speed.
  const atkMult = 1 + Math.max(0, player.bonusAttackSpeedPercent ?? 0) / 100;
  const speed = baseSpinSpeed * atkMult;

  const slotIdx = state.weapons.indexOf(weapon);
  const totalSlots = Math.max(1, state.weapons.length);
  // P0.1: drone count = 1 + bonusProjectileCount.
  const droneCount = Math.max(1, 1 + Math.floor(Math.max(0, player.bonusProjectileCount ?? 0)));

  // Base angle anchored to the weapon's slot index so the ring of drones
  // stays in the orbital "lane" assigned to this weapon (matters when
  // multiple orbital weapons share the orbit pool).
  const slotBaseAngle = (slotIdx / totalSlots) * Math.PI * 2;
  const spinPhase = state.elapsedTime * speed;

  const out: Array<{ x: number; y: number; angle: number }> = [];
  for (let i = 0; i < droneCount; i++) {
    const droneOffset = (i / droneCount) * Math.PI * 2;
    const a = slotBaseAngle + droneOffset + spinPhase;
    out.push({
      x: player.x + Math.cos(a) * radius,
      y: player.y + Math.sin(a) * radius,
      angle: a,
    });
  }
  return out;
}

// ─── Projectile Instance ────────────────────────────────────
export interface Projectile {
  id: number;
  weaponId: string;          // Which weapon fired this

  x: number;
  y: number;
  /** BETA 15.40 — position at start of current frame, before movement.
   *  Used by weaponAdapter wall-kill to do an anti-tunneling raycast
   *  from prev → current so fast projectiles can't skip through walls. */
  prevX: number;
  prevY: number;
  velocityX: number;
  velocityY: number;
  speed: number;
  size: number;

  damage: number;
  isCrit: boolean;
  knockback: number;
  knockbackAngle: number;    // Direction of knockback (radians)

  piercingLeft: number;      // Enemies remaining to pierce through
  bouncesLeft: number;       // Bounces remaining
  bounceRange: number;       // Max distance to find next bounce target
  /** BETA 20.72 — render-only ricochet tally (never read by gameplay).
   *  Drives the every-3rd-bounce brighter star-burst on Tactical Basketball
   *  so the King's Court 1.25×-per-bounce ramp is visible. */
  bounceCount: number;

  lifetime: number;          // Seconds remaining before despawn
  age: number;               // Seconds since spawned

  // ── Visual ──
  trailEffect: boolean;
  trailColor: string;
  trailPositions: { x: number; y: number }[];
  shape: ProjectileShape;
  bodyColor: string;
  glowColor: string;
  length: number;
  spriteUrl: string;       // If non-empty, render sprite instead of procedural shape
  spinRate: number;        // Radians/sec the sprite spins while flying (0 = orient along velocity)
  spriteDisplaySize: number; // Render height in world-units (0 = auto from proj.size)
  /** BETA 22.86 — proj.age at which a bounce squash-pulse ends (stamped on
   *  bounce; the sprite render flattens toward this age then eases back). */
  squashUntilAge?: number;

  // ── Tracking ──
  hitEnemyIds: Set<number>;  // Enemies already hit (for piercing — don't double-hit)
  alive: boolean;

  // ── BETA 13.96 — Boomerang return mechanic (Terminator) ──
  /** When true, projectile reverses toward player after halfLifetime AND clears
   *  hitEnemyIds so enemies it already hit can be hit AGAIN on the return path. */
  boomerangReturn: boolean;
  /** 'outbound' = original launch direction, 'returning' = homing toward player. */
  boomerangPhase: 'outbound' | 'returning';

  // ── BETA 14.05 — Trap projectiles (Ugly Kittens) ──
  /** True when an enemy has touched this projectile. Trap-mode projectiles
   *  (speed=0) use this to gate the projectileAnimFrames cycle: idle = static
   *  base sprite, hit = oscillate atk frames. User: "the cat sprite should
   *  not be the oscillating 3 images when placed on the ground. they should
   *  just be the 3 cats. they don't start the oscillating 3 photos sprites
   *  of the fighting until it is touched by an enemy." */
  wasHit: boolean;

  // ── BETA 20.63 — Ugly Kittens chasing-pet redesign (Dylan spec) ──
  /** While proj.age < this, render the claw (attack) frames; otherwise the idle
   *  cat sprite. Set on each enemy contact so the claw flashes ON HIT only, then
   *  reverts to idle as the cat runs to its next target. */
  attackFlashUntilAge?: number;
  /** Cats re-claw the same enemy after this age (hitEnemyIds is cleared) so a pet
   *  chasing one tanky enemy keeps damaging it instead of orbiting harmlessly. */
  nextRehitAge?: number;
  /** BETA 20.89 — set true on this projectile's FIRST contact, never cleared.
   *  The hit/crit counters used `hitEnemyIds.size === 0`, but the kitten re-claw
   *  clears hitEnemyIds every 1s → each clear re-counted a "hit" → 464% hit rate.
   *  everHit decouples the counter from the gameplay re-claw clear. */
  everHit?: boolean;
  /** Next age at which the cat re-picks its chase target. 8Hz retargeting —
   *  per-frame nearest-enemy scans were ~162K dist-checks/sec at 9 cats × 150
   *  enemies × 120fps (perf audit #2) with zero visible behavior benefit. */
  catRetargetAge?: number;
  /** BETA 21.70 — Ugly Kittens "claw budget": how many DISTINCT enemies one cat
   *  may bite per re-claw cycle. Seeded from spawn piercingLeft (base pierce +
   *  global pierce buffs) + 1, so a base cat = 1 enemy. Refilled every 1s on the
   *  re-claw clear; once spent it chases without biting until the next refill.
   *  BETA 22.04 — this same number is now the cat's KILL budget: after it kills this
   *  many enemies it vanishes (see kittenKills). Death is governed by kill-budget OR
   *  the 6s lifetime, whichever comes first. Replaces the old "claw the whole crowd". */
  kittenClawBudget?: number;
  kittenClawLeft?: number;
  /** BETA 22.04 — Ugly Kittens "kill budget" (Dylan: a cat exists to score a KILL,
   *  not to lawnmower). Counts enemies THIS cat has killed; when it reaches the budget
   *  (= kittenClawBudget = base 1 + pierce buffs) the cat vanishes immediately. */
  kittenKills?: number;
}

// ─── BETA 13.97 — Ground Effect (smoke cloud, slow puddle) ───
/** Persistent AoE entity painted on the ground at projectile impact.
 *  Ticks slow + DoT to enemies inside its radius for `lifetime` seconds. */
export interface GroundEffect {
  id: number;
  weaponId: string;
  x: number;
  y: number;
  radius: number;
  lifetime: number;          // seconds remaining
  initialLifetime: number;   // for fade-out alpha calc
  slowMult: number;          // 1.0 = no slow
  slowDurationOnTick: number; // seconds of slow refresh applied each frame inside
  dotPerSec: number;         // damage/second
  color: string;             // CSS color (with alpha) for rendering
  /** Damage tick accumulator — applies whole damage each time it crosses 1.0 */
  dotAccum: number;
  /** Per-enemy DoT cooldowns so we don't hammer damageEnemy 60×/sec */
  enemyTickCooldowns: Map<number, number>;
  /** BETA 13.100 — Optional sprite to render in place of the procedural circle.
   *  Used by SHIT trail (item_poop_deck_ground.png), Asmon's Banana ground prop,
   *  Whoopie Cushion ground prop. Drawn scaled to fit (radius * 2.4) world units. */
  groundSpriteUrl?: string;
  /** BETA 13.102 — Optional second-stage sprite shown after wasSteppedOn flips
   *  to true. Used by SHIT trail (BETA 14.25: stage 1 = item_poop_deck.png
   *  fresh poop pile, stage 2 = item_shit_puddle.png splashed puddle) and
   *  Whoopie Cushion / Asmon's Banana floor traps for their pristine→splat
   *  transitions. */
  groundSpriteUrlAfterStep?: string;
  /** BETA 13.102 — Runtime flag set when an enemy first enters the radius.
   *  Causes the renderer to swap to groundSpriteUrlAfterStep. Once set, stays
   *  set for the rest of the effect's lifetime. */
  wasSteppedOn?: boolean;
  /** BETA 13.102 — Optional callback fired ONCE when wasSteppedOn first
   *  becomes true. Used by trap-style effects (Whoopie Cushion stun, Asmon's
   *  Banana knockback) that need to apply a one-shot effect on first contact. */
  onFirstStep?: (effect: GroundEffect, enemyId: number) => void;
}

// ─── Damage Number (floating text) ──────────────────────────
export interface DamageNumber {
  x: number;
  y: number;
  value: number;
  isCrit: boolean;
  timer: number;             // Seconds remaining
  velocityY: number;         // Float upward
  isBuff?: boolean;
  label?: string;
  // BETA 20.27 — optional text color for label popups (XP gain = cyan, gold
  // gain = amber). Plain damage / crit ignore this and use their fixed colors.
  color?: string;
  // BETA 16.94 — pool flag. PSBOT cycle-30i §2B: damage numbers were
  // 44% of frame GC pressure on flood density (each hit = new object,
  // splice on expire). Now: pre-allocated pool of POOL_SIZE slots,
  // spawn finds an inactive slot and mutates in-place; expire marks
  // active=false; render skips inactive. Zero per-spawn allocation,
  // O(1) expire.
  active: boolean;
}

// BETA 16.94 — damage-number pool size. ~500 covers flood density
// at ~9000 kills/run (cycle-30i epic Killa baseline). Active slots
// are reused as old ones expire. If overflow — pool exhausted —
// spawnDamageNumber falls through silently (acceptable on screen
// already saturated with text).
const DAMAGE_NUMBER_POOL_SIZE = 500;
let _dnSpawnCursor = 0;

/**
 * Pool-friendly damage-number spawn. Pass any subset of fields; defaults
 * fill the rest. Call this instead of `state.damageNumbers.push({...})`.
 *
 * BETA 16.96 — Coalescing (PSBOT cycle-30i §7B Visual LOD): when a
 * numeric damage spawn lands within 25px of a recent active spawn
 * (≤ ~150ms old), the existing number's value is incremented in-
 * place instead of allocating a new slot. Cuts visual spam from
 * piercing / AoE / multi-hit bursts and saves render cost. Buff
 * popups (Mystery Stew labels, etc.) are NEVER coalesced — each one
 * is informational and must be visible.
 */
export function spawnDamageNumber(
  state: WeaponSystemState,
  props: { x: number; y: number; value: number; isCrit?: boolean; timer?: number; velocityY?: number; isBuff?: boolean; label?: string; color?: string },
): void {
  const arr = state.damageNumbers;
  const len = arr.length;

  // Coalescing pass — only for plain numeric damage (skip buff/label spawns).
  if (!props.isBuff && !props.label) {
    for (let i = 0; i < len; i++) {
      const dn = arr[i];
      if (!dn.active) continue;
      if (dn.isBuff || dn.label) continue;
      // Recently-spawned heuristic: dn.timer is seconds-remaining.
      // Crit spawns start at 0.7-0.8s, normal at 0.45-0.5s. timer > 0.4
      // captures the first ~50-150ms post-spawn.
      if (dn.timer < 0.4) continue;
      const dx = dn.x - props.x;
      const dy = dn.y - props.y;
      if (dx * dx + dy * dy > 625) continue; // 25px coalesce radius
      // Merge: add value, escalate crit if either is, refresh timer.
      dn.value += props.value;
      if (props.isCrit) dn.isCrit = true;
      const incomingTimer = props.timer ?? 1.0;
      if (incomingTimer > dn.timer) dn.timer = incomingTimer;
      return;
    }
  }

  // Fresh spawn — find first inactive slot via cursor.
  for (let probe = 0; probe < len; probe++) {
    const idx = (_dnSpawnCursor + probe) % len;
    const dn = arr[idx];
    if (!dn.active) {
      dn.x = props.x;
      dn.y = props.y;
      dn.value = props.value;
      dn.isCrit = props.isCrit ?? false;
      dn.timer = props.timer ?? 1.0;
      dn.velocityY = props.velocityY ?? -40;
      dn.isBuff = props.isBuff;
      dn.label = props.label;
      dn.color = props.color;
      dn.active = true;
      _dnSpawnCursor = (idx + 1) % len;
      // BETA 20.72 — spark burst through the popup (engine-registered, see
      // registerDamageNumberVfx). FRESH spawns only — the coalesce branch
      // above returns early, so multi-hit spam can't multiply sparks.
      _damageNumberVfx?.(props.x, props.y, dn.isCrit === true, props.isBuff === true, props.color);
      return;
    }
  }
  // Pool full. Drop the spawn — the screen is already saturated with text.
}

// ─── Muzzle Flash ───────────────────────────────────────────
interface MuzzleFlash {
  x: number;
  y: number;
  timer: number;
  angle: number;
}

// ─── Arc Slash Visual + Hitbox ──────────────────────────────
// BETA 19.44 — Dylan spec live audit: arc weapons (Spatula, Drac's Fang)
// now use SPRITE-COLLISION hit detection. The swinging sprite IS the
// hitbox — no more invisible projectile rays carrying damage. Each tick,
// engine code (tickArcSlashHits, called from updateWeapons) computes the
// current sprite position and checks every enemy circle against it. A
// per-slash `hitEnemyIds` Set prevents double-hitting the same enemy on
// a single swing.
interface ArcSlash {
  x: number;            // BETA 15.54: now used as fallback only — sprite tracks live player.x/y in renderArcSlashes
  y: number;
  centerAngle: number;  // center of arc in radians
  halfSpread: number;   // half the arc width in radians
  radius: number;       // reach of the slash in world units (= speed × lifetime)
  timer: number;        // seconds remaining
  maxTimer: number;     // total flash duration
  spriteUrl: string;    // weapon sprite to sweep through the arc
  spriteSize: number;   // sprite height in world units (BASE — multiplied by sizeMult at render)
  /** BETA 15.54 — Captured at slash spawn from `1 + player.attackAreaBonus / 100`.
   *  Multiplies both the sprite radius (where sprite is drawn) AND the sprite
   *  height. Locks in the buff state at swing-time so the visual scales with
   *  the same percent as the hitbox. */
  sizeMult: number;
  // BETA 13.101 — Per-weapon arc render variants
  noFlip?: boolean;     // skip the X-mirror that corrects spatula's left-facing sprite
  rampUp?: boolean;     // ease swing slow→fast instead of linear sweep
  noFade?: boolean;     // sprite stays at full alpha for the entire swing

  // ── BETA 19.44 — Damaging-sprite fields ──────────────────────────────────
  /** Which weapon spawned this slash — used for crit-tally + onEnemyHit
   *  attribution back to the right WeaponInstance. */
  weaponId: string;
  /** Reference to the WeaponInstance (so we can bump totalShotsHit/totalCrits
   *  when the sprite hits an enemy). Not strictly required for damage
   *  application, but keeps stats consistent with projectile weapons. */
  weaponInstance: WeaponInstance | null;
  /** Pre-rolled per-hit damage (with player.bonusDamagePercent baked in). */
  damage: number;
  /** Per-swing crit-roll chance (0..1). Each enemy hit re-rolls. */
  critChance: number;
  /** Crit multiplier (e.g. 0.6 = +60% on crit). */
  critDamage: number;
  /** Knockback magnitude applied on hit. With aoeKnockback=true every hit
   *  enemy gets this full value (spatula spec). */
  knockback: number;
  /** Per-hit lifesteal — heals the player by `damage * lifestealPct`. Used
   *  by Drac's Fang (0.08 base, 0.20 with Drac's Gas Mask equipped). */
  lifestealPct: number;
  /** Set of enemy IDs already hit by THIS swing. Prevents double-hits as
   *  the sprite sweeps past an enemy that's still inside its hitbox. In
   *  orbit mode this Set is cleared every `orbitHitResetSec` so enemies
   *  can be hit again as the sprite rotates back around. */
  hitEnemyIds: Set<number>;
  /** BETA 19.44 — Spatula spec: when true, re-aim centerAngle to the live
   *  player.facingAngle every frame. Without this, the angle is sampled at
   *  swing-start and locked (fang behavior). */
  liveDirection?: boolean;
  /** BETA 19.44 — Spatula spec: every hit enemy gets the FULL knockback
   *  value, no falloff. Cosmetic for now (we already apply full kb) — the
   *  flag is wired through for future per-enemy weighting. */
  aoeKnockback?: boolean;
  /** BETA 19.44 — Continuous-orbit mode. Set when the weapon's effective
   *  spread reaches `arcMaxSpreadDeg` (default 360°). In orbit mode:
   *    - sprite spins at a constant angular velocity around the player
   *    - timer is auto-extended (one full rotation per fire interval)
   *    - hitEnemyIds resets every `orbitHitResetSec` so the same enemy can
   *      be re-hit as the sprite swings back around */
  mode: 'fan' | 'orbit';
  /** Seconds remaining until the hitEnemyIds Set clears (orbit mode only). */
  orbitHitResetTimer: number;
  /** Reset interval for hitEnemyIds in orbit mode. */
  orbitHitResetSec: number;
}

// ─── Weapon System State ────────────────────────────────────
export interface WeaponSystemState {
  weapons: WeaponInstance[];       // Player's equipped weapons (max 4 slots)
  projectiles: Projectile[];
  damageNumbers: DamageNumber[];
  muzzleFlashes: MuzzleFlash[];
  arcSlashes: ArcSlash[];          // Visual flash effects for arc/melee weapons
  nextProjectileId: number;
  totalDamageDealt: number;
  totalCrits: number;
  totalShotsFired: number;
  // BETA 20.43 — bounce-event telemetry (Dylan: "track every bounce + if a bounce
  // should have happened and didn't"). bouncesPerformed = successful bounces;
  // bounceMissedNoTarget = projectile hit an enemy WITH bounces left but found no
  // other enemy in bounceRange to chain to (the "should have bounced but didn't").
  bouncesPerformed: number;
  bounceMissedNoTarget: number;
  elapsedTime: number;             // Seconds since system created (for orbit drift)
  /** BETA 13.95 — accumulated lifesteal heal pending for the engine to apply
   *  to player.hp this frame. Reset to 0 each frame after engine consumes it. */
  pendingLifestealHeal: number;
  /** BETA 13.97 — persistent AoE ground effects (Shitty Cannon smoke cloud,
   *  Poop Sling slow puddle). Tick down + apply slow + DoT each frame. */
  groundEffects: GroundEffect[];
  nextGroundEffectId: number;
  /** BETA 16.08 — Line-of-sight callback set by weaponAdapter on each
   *  update. Returns true if the line ax,ay → bx,by is clear of walls.
   *  Used by aura/arc/orbital weapons (which apply damage instantly,
   *  not via projectiles) to avoid hitting enemies through walls.
   *  Per Dylan: "check every weapon. they are not allowed to shoot
   *  through or over walls of rooms." If unset (e.g., during init
   *  before the first update), defaults to true (allow hit). */
  hasLineOfSight?: (ax: number, ay: number, bx: number, by: number) => boolean;
}

// BETA 20.72 — post-step burst windows. MUST mirror the engine.ts onFirstStep
// lifetime clamps (whoopie: effect.lifetime = min(lifetime, 0.6); banana:
// min(lifetime, 0.4)) — they're the clock for the trigger FX below.
const _WHOOPIE_STEP_BURST = 0.6;
const _BANANA_STEP_BURST = 0.4;
// BETA 20.72 — roiling-puff alpha stagger, hoisted out of the per-effect loop.
const _PUFF_ALPHAS = [0.5, 0.35, 0.25, 0.18];
// BETA 20.78 — Shitty Cannon birth-blast dirt tones, hoisted (PERF law: no
// per-frame array literals in the ground-effect render loop).
const _DIRT_SHARD_COLORS = ['#4a3826', '#6b5436', '#8a7a5a'];

/** BETA 13.97 — Render all ground effects (smoke clouds, slow puddles) as
 *  translucent circles in world space. Alpha fades over the last 30% of lifetime.
 *
 *  BETA 13.99 — exported so engine.ts can call this BEFORE enemies render
 *  (so ground effects are drawn underneath enemies + player, not on top). */
export function renderGroundEffects(
  state: WeaponSystemState,
  ctx: CanvasRenderingContext2D,
  camera: CameraForWeapon,
): void {
  for (const ge of state.groundEffects) {
    const screenX = (ge.x - camera.x) * camera.zoom + camera.viewportWidth / 2;
    const screenY = (ge.y - camera.y) * camera.zoom + camera.viewportHeight / 2;
    const screenR = ge.radius * camera.zoom;
    // Frustum cull
    // BETA 17.37 — cycle-30aq audit: was `< 0` / `> viewport` with no
    // buffer beyond the screen-radius itself. Ground effects (SHIT trail,
    // banana traps, whoopie cushions) popped in mid-screen when player
    // walked over them. Adding 80px screen-space buffer so the radius
    // edge fades in 80px outside the visible area.
    const _geMargin = 80;
    if (screenX + screenR < -_geMargin || screenX - screenR > camera.viewportWidth + _geMargin) continue;
    if (screenY + screenR < -_geMargin || screenY - screenR > camera.viewportHeight + _geMargin) continue;

    // Fade alpha during the last 30% of lifetime (and gentle pulse always)
    const lifeFrac = ge.lifetime / Math.max(0.001, ge.initialLifetime);
    const fadeAlpha = lifeFrac < 0.3 ? lifeFrac / 0.3 : 1.0;
    const pulse = 0.85 + 0.15 * Math.sin(state.elapsedTime * 6 + ge.id);

    // BETA 20.69 — SPAWN SHOCKWAVE. BETA 20.78 — split per weapon (Dylan:
    // "still seeing circles... i think you missed many things" — the 20.69
    // birth blast was the SAME white+color double-ring on EVERY ground
    // effect, i.e. one more shared circle). Identity law: each birth gets
    // its OWN look:
    //   wpn_poop_sling    → 6 brown GLOP BLOBS arcing out + squashing flat
    //   wpn_shitty_cannon → 7 heavy DIRT SHARDS punched out, slammed down
    //   whoopie / banana / SHIT slam → NO birth flash at all (identities
    //     live in their floor sprites + 20.72 step-bursts; ring was noise)
    //   unrecognized ids  → 20.69 double-ring kept as honest fallback.
    // All motion derives from _st + ge.id — deterministic, zero RNG, zero
    // per-frame allocations (PERF law: this loop runs hot).
    const _geAge = ge.initialLifetime - ge.lifetime;
    if (_geAge >= 0 && _geAge < 0.25) {
      const _st = _geAge / 0.25;
      if (ge.weaponId === 'wpn_poop_sling') {
        // GLOP SPLAT — blobs fly out on a flattened ground ellipse, hop up
        // then drop (sin arc = cheap gravity), landing wider + flatter
        // (squash) as the puddle sprite takes over underneath.
        ctx.save();
        for (let _k = 0; _k < 6; _k++) {
          const _a = _k * 1.0472 + ge.id * 0.7;
          const _v = 0.75 + 0.3 * Math.sin(ge.id * 2.3 + _k * 4.1);
          const _r = screenR * (0.12 + 0.82 * _st) * _v;
          const _hop = Math.sin(_st * Math.PI) * screenR * 0.28 * _v;
          const _size = Math.max(1.5, screenR * (0.15 - _k * 0.01));
          ctx.fillStyle = (_k & 1) ? '#5b4123' : '#7a5a2e';
          ctx.globalAlpha = (1 - _st * _st) * 0.9;
          ctx.beginPath();
          ctx.ellipse(
            screenX + Math.cos(_a) * _r,
            screenY + Math.sin(_a) * _r * 0.6 - _hop,
            _size * (1 + _st * 1.3),
            _size * Math.max(0.35, 1 - _st * 0.55),
            0, 0, Math.PI * 2,
          );
          ctx.fill();
        }
        ctx.restore();
      } else if (ge.weaponId === 'wpn_shitty_cannon') {
        // DIRT SHARD BLAST — each shard is a stroke between its position
        // now and a beat ago (so it self-orients along its own motion):
        // launched up hard, then slammed PAST ground level by heavy
        // gravity. Reads as a cannonball crater, not a ring.
        ctx.save();
        for (let _k = 0; _k < 7; _k++) {
          const _a = _k * 0.8976 + ge.id * 1.13;
          const _v = 0.85 + 0.2 * Math.sin(ge.id * 1.7 + _k * 3.3);
          const _ca = Math.cos(_a);
          const _sa = Math.sin(_a) * 0.6; // ground-plane perspective
          const _st0 = Math.max(0, _st - 0.1);
          const _e1 = 1 - (1 - _st) * (1 - _st);
          const _e0 = 1 - (1 - _st0) * (1 - _st0);
          const _r1 = screenR * (0.18 + 0.95 * _e1) * _v;
          const _r0 = screenR * (0.18 + 0.95 * _e0) * _v;
          const _y1 = screenR * (-1.1 * _st + 1.35 * _st * _st) * _v;
          const _y0 = screenR * (-1.1 * _st0 + 1.35 * _st0 * _st0) * _v;
          ctx.strokeStyle = _DIRT_SHARD_COLORS[_k % 3];
          ctx.globalAlpha = (1 - _st * _st) * 0.95;
          ctx.lineWidth = Math.max(1.5, screenR * 0.055) * (1 - _st * 0.4);
          ctx.beginPath();
          ctx.moveTo(screenX + _ca * _r0, screenY + _sa * _r0 + _y0);
          ctx.lineTo(screenX + _ca * _r1, screenY + _sa * _r1 + _y1);
          ctx.stroke();
        }
        ctx.restore();
      } else if (
        ge.weaponId === 'item_whoopie_cushion_of_doom' ||
        ge.weaponId === 'item_asmons_desk_banana' ||
        ge.weaponId === 'wpn_banana_bombs' ||   // BETA 22.76 — the WEAPON reuses the banana slip identity (audit: was on generic ring spam)
        ge.weaponId === 'item_shit'
      ) {
        // No birth flash — these carry full identities elsewhere (trap sprites +
        // 20.72 step-bursts; SHIT's pile→puddle swap). The generic ring on top
        // was exactly the circle spam Dylan called out.
      } else {
        // Fallback — unrecognized ground effects keep the 20.69 double-ring
        // so nothing ships invisible.
        const _gc = (ge as unknown as { color?: string }).color ?? '#ffffff';
        ctx.save();
        ctx.globalAlpha = (1 - _st) * 0.85;
        ctx.strokeStyle = _gc;
        ctx.lineWidth = Math.max(2, 5 * (1 - _st)) * camera.zoom;
        ctx.beginPath();
        ctx.arc(screenX, screenY, Math.max(2, screenR * (0.2 + 0.85 * _st)), 0, Math.PI * 2);
        ctx.stroke();
        ctx.globalAlpha = (1 - _st) * 0.5;
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2 * camera.zoom;
        ctx.beginPath();
        ctx.arc(screenX, screenY, Math.max(1, screenR * (0.1 + 0.6 * _st)), 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }
    }

    // BETA 13.100 — if the ground effect has a sprite override, draw it instead
    // of (or behind) the procedural circle. Used by SHIT trail (item_poop_deck_ground),
    // Asmon's Banana, Whoopie Cushion.
    // BETA 13.102 — 2-stage sprite swap. If wasSteppedOn AND
    // groundSpriteUrlAfterStep is set, render the post-step sprite instead.
    // BETA 14.25 — SHIT trail: stage 1 = item_poop_deck.png (fresh poop pile,
    // also the SHIT card icon), stage 2 = item_shit_puddle.png (splashed
    // puddle when an enemy steps on it). Whoopie Cushion + Asmon's Banana
    // also use this for their pristine→splat transitions.
    const _activeSprite = (ge.wasSteppedOn && ge.groundSpriteUrlAfterStep)
      ? ge.groundSpriteUrlAfterStep
      : ge.groundSpriteUrl;
    if (_activeSprite) {
      const img = getWeaponSprite(_activeSprite);
      if (img) {
        const drawSize = screenR * 2.4;
        ctx.save();
        ctx.globalAlpha = fadeAlpha;
        ctx.drawImage(img, screenX - drawSize / 2, screenY - drawSize / 2, drawSize, drawSize);
        ctx.restore();

        // BETA 20.72 — WHOOPIE CUSHION: the AoE freeze-stun was INVISIBLE
        // (engine onFirstStep ran 30 brown circles + pure logic). Now: 3
        // staggered CYAN shockwave rings expand across the stun radius over
        // the 0.6s post-step burst window. Cyan = the game's freeze color.
        if (ge.wasSteppedOn && ge.weaponId === 'item_whoopie_cushion_of_doom') {
          const _wt = 1 - ge.lifetime / _WHOOPIE_STEP_BURST;
          if (_wt >= 0 && _wt <= 1) {
            ctx.save();
            ctx.strokeStyle = '#00E5FF';
            for (let _k = 0; _k < 3; _k++) {
              const _kt = _wt - _k * 0.18;
              if (_kt <= 0) continue;
              ctx.globalAlpha = Math.max(0, (1 - _wt) * 0.8);
              ctx.lineWidth = Math.max(1, (3.5 - _k) * (1 - _kt) * camera.zoom + 1);
              ctx.beginPath();
              ctx.arc(screenX, screenY, Math.max(2, ge.radius * (0.3 + 1.1 * _kt) * camera.zoom), 0, Math.PI * 2);
              ctx.stroke();
            }
            ctx.restore();
          }
        }

        // BETA 20.72 — ASMON'S DESK BANANA: 4 curved YELLOW banana arcs whip
        // outward from the splat (short arc strokes, deterministic angles
        // seeded by ge.id), each tip trailing 2 brown peel motes. Reads as a
        // slip-and-fling, not another expanding circle. BETA 22.76 — the
        // Banana Bomb WEAPON shares this identity (audit: was generic rings).
        if (ge.wasSteppedOn && (ge.weaponId === 'item_asmons_desk_banana' || ge.weaponId === 'wpn_banana_bombs')) {
          const _bt = 1 - ge.lifetime / _BANANA_STEP_BURST;
          if (_bt > 0 && _bt <= 1) {
            ctx.save();
            const _bAlpha = (1 - _bt) * 0.85;
            const _arcR = Math.max(2, (0.25 + 1.05 * _bt) * screenR);
            const _peelR = Math.max(1, _arcR - screenR * 0.12);
            for (let _k = 0; _k < 4; _k++) {
              const _baseA = _k * (Math.PI / 2) + 0.4 + ge.id;
              ctx.globalAlpha = _bAlpha;
              ctx.strokeStyle = '#FFE135';
              ctx.lineWidth = 3 * camera.zoom;
              ctx.beginPath();
              ctx.arc(screenX, screenY, _arcR, _baseA - 0.3, _baseA + 0.3);
              ctx.stroke();
              // peel motes lag just behind each arc's tips
              ctx.fillStyle = '#8b6f47';
              ctx.globalAlpha = _bAlpha * 0.8;
              for (let _m = 0; _m < 2; _m++) {
                const _ma = _baseA + (_m === 0 ? -0.34 : 0.34);
                ctx.beginPath();
                ctx.arc(screenX + Math.cos(_ma) * _peelR, screenY + Math.sin(_ma) * _peelR, Math.max(1, 1.6 * camera.zoom), 0, Math.PI * 2);
                ctx.fill();
              }
            }
            ctx.restore();
          }
        }
        continue;
      }
      // sprite not yet loaded — fall through to procedural circle
    }

    // BETA 20.72 — ROILING SMOKE PUFFS (Batch C: kill the flat disc). Every
    // spriteless ground effect used to be the SAME translucent pulsing
    // circle + inner ring. Now: 4 stacked jitter-edged ellipses in the
    // effect's own color that drift UP and shrink as life burns down — gas
    // rises, edges roil. All offsets are sin/cos of elapsedTime + ge.id, so
    // zero allocs and zero stored state. 20.69 spawn shockwave above kept.
    ctx.save();
    ctx.fillStyle = ge.color;
    const _puffT = state.elapsedTime * 1.7 + ge.id * 2.3;
    const _puffRise = Math.min(1, _geAge * 0.3) * screenR * 0.22; // climbs over first ~3.3s
    for (let _pi = 0; _pi < 4; _pi++) {
      const _jx = Math.sin(_puffT + _pi * 1.9) * screenR * 0.16;
      const _jy = Math.cos(_puffT * 0.8 + _pi * 2.6) * screenR * 0.10 - _pi * screenR * 0.14 - _puffRise;
      const _pr = Math.max(2, screenR * (0.62 - _pi * 0.09) * (0.55 + 0.45 * lifeFrac));
      ctx.globalAlpha = fadeAlpha * pulse * _PUFF_ALPHAS[_pi];
      ctx.beginPath();
      ctx.ellipse(screenX + _jx, screenY + _jy, _pr * 1.25, _pr * 0.85, 0, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }
}

/** BETA 13.98 — Spawn an arbitrary ground effect at (x, y) without going
 *  through a weapon def. Used by item effects (SHIT trail, Asmon's Banana,
 *  Whoopie Cushion) that paint AoE zones outside the weapon-impact path. */
export function spawnGroundEffectAt(
  state: WeaponSystemState,
  x: number,
  y: number,
  opts: {
    radius: number;
    lifetime: number;
    slowMult?: number;          // 1.0 = no slow
    dotPerSec?: number;          // 0 = no DoT
    color?: string;              // CSS color with alpha
    weaponId?: string;           // for telemetry; defaults to ''
    groundSpriteUrl?: string;    // BETA 13.100 — sprite override (e.g. item_poop_deck_ground.png)
    /** BETA 13.102 — Stage-2 sprite shown after first enemy contact (SHIT
     *  trail emoji→squish, floor trap pre→post-trigger). */
    groundSpriteUrlAfterStep?: string;
    /** BETA 13.102 — One-shot callback when first enemy enters the radius. */
    onFirstStep?: (effect: GroundEffect, enemyId: number) => void;
  },
): void {
  // BETA 18.18 — FIFO cap. If at MAX_GROUND_EFFECTS, evict the oldest
  // (index 0) before pushing. Keeps the per-frame O(effects × enemies)
  // slow/DoT loop bounded at ~60 × ~180 = 10,800 ops/frame worst case
  // instead of the unbounded 14,000+ that tanked Dylan's runs to 1 FPS.
  if (state.groundEffects.length >= MAX_GROUND_EFFECTS) {
    state.groundEffects.shift();
  }
  state.groundEffects.push({
    id: state.nextGroundEffectId++,
    weaponId: opts.weaponId ?? '',
    x,
    y,
    radius: opts.radius,
    lifetime: opts.lifetime,
    initialLifetime: opts.lifetime,
    slowMult: opts.slowMult ?? 1.0,
    slowDurationOnTick: 0.4,
    dotPerSec: opts.dotPerSec ?? 0,
    color: opts.color ?? 'rgba(110,75,40,0.55)',
    dotAccum: 0,
    enemyTickCooldowns: new Map(),
    groundSpriteUrl: opts.groundSpriteUrl,
    groundSpriteUrlAfterStep: opts.groundSpriteUrlAfterStep,
    onFirstStep: opts.onFirstStep,
    wasSteppedOn: false,
  });
  // BETA 14.55 — floorEffectTracker spawn hook
  _floorEffectTracker?.onSpawn(opts.weaponId ?? 'unknown', opts.lifetime);
}

/** BETA 13.97 — Spawn a ground effect at (x, y) if the weapon has aoeOnImpact config.
 *  Called by the impact paths (enemy collision, wall collision). */
export function spawnImpactAoe(
  state: WeaponSystemState,
  weaponId: string,
  x: number,
  y: number,
): void {
  const def = getWeaponDef(weaponId);
  if (!def?.projectile.aoeOnImpactRadius || !def.projectile.aoeOnImpactDuration) return;
  // BETA 18.18 — same FIFO cap as spawnGroundEffectAt. Weapon-impact AoE
  // pools (Poop Sling, Shitty Cannon, etc.) compound with SHIT puddles and
  // can blow past the 60-effect ceiling on heavy attack-speed builds.
  if (state.groundEffects.length >= MAX_GROUND_EFFECTS) {
    state.groundEffects.shift();
  }
  state.groundEffects.push({
    id: state.nextGroundEffectId++,
    weaponId,
    x,
    y,
    radius: def.projectile.aoeOnImpactRadius,
    lifetime: def.projectile.aoeOnImpactDuration,
    initialLifetime: def.projectile.aoeOnImpactDuration,
    slowMult: def.projectile.aoeOnImpactSlowMult ?? 1.0,
    slowDurationOnTick: 0.4, // refreshes slow each frame the enemy is inside (lasts 0.4s after leaving)
    dotPerSec: def.projectile.aoeOnImpactDotPerSec ?? 0,
    color: def.projectile.aoeOnImpactColor ?? 'rgba(120,120,120,0.4)',
    dotAccum: 0,
    enemyTickCooldowns: new Map(),
    // BETA 13.101 — pipe sprite override through (Shitty Cannon + Poop Sling
    // use item_poop_deck_ground.png instead of the procedural color disc).
    groundSpriteUrl: def.projectile.aoeOnImpactSpriteUrl,
    // BETA 14.60 — Stage-2 sprite swap on first enemy contact. Poop weapons
    // start with a clean ground sprite then swap to a splattered/flies sprite
    // when stepped on. Renders via the existing wasSteppedOn → groundSpriteUrlAfterStep
    // path in renderGroundEffects.
    groundSpriteUrlAfterStep: def.projectile.aoeOnImpactSpriteUrlAfterStep,
    wasSteppedOn: false,
  });
  // BETA 14.60 — fire floorEffectTracker.onSpawn hook for direct AoE-impact
  // ground effects (was missing — only spawnGroundEffectAt fired the hook).
  // Without this, wpn_poop_sling and wpn_shitty_cannon impacts were invisible
  // to floorEffectTracker telemetry.
  _floorEffectTracker?.onSpawn(weaponId, def.projectile.aoeOnImpactDuration);
}

/** BETA 22.56 — instant blast at the impact point (Beer Grenade; RPG later).
 *  Damages every alive enemy within explodeOnImpactRadius (excluding the
 *  directly-struck enemy, which already took projectile damage) through the
 *  SAME onEnemyHit pipeline as a normal hit, so crits/knockback/telemetry
 *  all behave. Called from all 3 projectile-death impact sites. */
export function spawnImpactExplosion(
  weaponId: string,
  x: number,
  y: number,
  projDamage: number,
  isCrit: boolean,
  enemies: EnemyForWeapon[],
  onEnemyHit: OnEnemyHitCallback,
  excludeEnemyId?: number,
): void {
  const def = getWeaponDef(weaponId);
  const radius = def?.projectile.explodeOnImpactRadius ?? 0;
  if (!radius) return;
  const dmg = projDamage * (def?.projectile.explodeOnImpactDamageMult ?? 1);
  for (const e of enemies) {
    if (!e.alive || e.id === excludeEnemyId) continue;
    const dx = e.x - x, dy = e.y - y;
    const reach = radius + e.hitboxRadius;
    if (dx * dx + dy * dy <= reach * reach) {
      onEnemyHit(e.id, dmg, def?.baseKnockback ?? 0, Math.atan2(dy, dx), isCrit, weaponId);
    }
  }
  // BETA 22.76 — per-weapon impact identity (22.75 audit: this foam was
  // firing for EVERY explodeOnImpact weapon, so the RPG detonated with the
  // Beer Grenade's cream-gold foam). Branch by weaponId. Reaching here:
  // voogoo_rager_toss (r90), wpn_rpg (r130), wpn_poop_sling (r60, added 22.84).
  if (_weaponParticleSpawner) {
    if (weaponId === 'wpn_rpg') {
      // RPG-7: layered military blast — white flash core, orange fireball
      // puffs, black smoke, expanding shockwave ring, dark debris shards.
      _weaponParticleSpawner(x, y, '#FFF4D6', 2, { shape: 'mote', speedMin: 0, speedMax: 40, lifeMin: 0.08, lifeMax: 0.14, sizeMin: 5, sizeMax: 8 });
      _weaponParticleSpawner(x, y, '#ff8a2c', 18, { shape: 'spark', speedMin: 90, speedMax: 260, lifeMin: 0.2, lifeMax: 0.5, sizeMin: 2.5, sizeMax: 5 });
      _weaponParticleSpawner(x, y, '#3a2a20', 9, { shape: 'mote', gravity: -30, drag: 0.9, speedMin: 30, speedMax: 120, lifeMin: 0.4, lifeMax: 0.8, sizeMin: 3, sizeMax: 6 });
      _weaponParticleSpawner(x, y, '#4a3d2e', 6, { shape: 'spark', speedMin: 160, speedMax: 340, lifeMin: 0.15, lifeMax: 0.3, sizeMin: 1.5, sizeMax: 3, stretch: 3 });
      _weaponParticleSpawner(x, y, '#ffd08a', 1, { shape: 'ring', speedMin: 0, speedMax: 0, sizeMin: Math.max(16, radius * 0.3), sizeMax: Math.max(20, radius * 0.42), lifeMin: 0.22, lifeMax: 0.34 });
    } else if (weaponId === 'wpn_poop_sling') {
      // BETA 22.84 — The Thumper: a gross brown/olive grenade blast — chunky
      // sludge debris + a grey stink smoke ring (NOT the Beer Grenade's foam).
      _weaponParticleSpawner(x, y, '#6b4a24', 14, { shape: 'spark', speedMin: 90, speedMax: 250, lifeMin: 0.22, lifeMax: 0.5, sizeMin: 2.5, sizeMax: 5 });
      _weaponParticleSpawner(x, y, '#4a5a28', 7, { shape: 'mote', gravity: -20, drag: 0.9, speedMin: 30, speedMax: 110, lifeMin: 0.4, lifeMax: 0.8, sizeMin: 3, sizeMax: 6 });
      _weaponParticleSpawner(x, y, '#8a8a7a', 1, { shape: 'ring', speedMin: 0, speedMax: 0, sizeMin: Math.max(12, radius * 0.28), sizeMax: Math.max(16, radius * 0.4), lifeMin: 0.3, lifeMax: 0.42 });
    } else {
      // Beer Grenade owns the CREAM-GOLD FOAM pop — fizzing droplet spray +
      // expanding foam ring (nothing else uses this palette).
      _weaponParticleSpawner(x, y, '#F5E9C8', 14, { shape: 'spark', speedMin: 120, speedMax: 300, lifeMin: 0.25, lifeMax: 0.5, sizeMin: 2, sizeMax: 4.5 });
      _weaponParticleSpawner(x, y, '#D9A62E', 6, { shape: 'spark', speedMin: 60, speedMax: 160, lifeMin: 0.3, lifeMax: 0.55, sizeMin: 1.5, sizeMax: 3 });
      _weaponParticleSpawner(x, y, '#FFFFFF', 1, { shape: 'ring', speedMin: 0, speedMax: 0, sizeMin: Math.max(12, radius * 0.25), sizeMax: Math.max(16, radius * 0.32), lifeMin: 0.3, lifeMax: 0.4 });
    }
  }
}

/** Create a fresh weapon system state for a new run */
export function createWeaponSystemState(): WeaponSystemState {
  return {
    weapons: [],
    projectiles: [],
    // BETA 16.94 — pre-allocated pool. All slots start inactive.
    damageNumbers: Array.from({ length: DAMAGE_NUMBER_POOL_SIZE }, () => ({
      x: 0, y: 0, value: 0, isCrit: false, timer: 0, velocityY: 0, active: false,
    })),
    muzzleFlashes: [],
    arcSlashes: [],
    nextProjectileId: 1,
    totalDamageDealt: 0,
    totalCrits: 0,
    totalShotsFired: 0,
    bouncesPerformed: 0,
    bounceMissedNoTarget: 0,
    elapsedTime: 0,
    pendingLifestealHeal: 0,
    groundEffects: [],
    nextGroundEffectId: 1,
  };
}

// ─── Player Interface (what we need from engine.ts) ─────────
export interface PlayerForWeapon {
  x: number;
  y: number;
  facingAngle: number;              // Radians — direction player is currently AIMING (mouse cursor on KB+M, right-stick on gamepad)
  // BETA 19.44 — PSBOT cycle-30at-ADDENDUM-2 spec: live movement velocity so
  // arc weapons with arcLiveDirection (Spatula) sweep along where the player
  // is RUNNING, not where they're aiming. Per Dylan: "spatula naturally
  // pointing where direction you are running." For KB+M kiting (mouse forward,
  // running backward), aim vs movement diverge — facingAngle was wrong for
  // this case. Both fields default to 0 if the engine hasn't populated them.
  vx: number;
  vy: number;
  // BETA 19.44 (PSBOT alignment + live fix) — Dylan: bot needs to be "smart
  // as fuck" with the new spatula. Movement-vector aim is correct for humans
  // but disastrous for the BOT — it kites AWAY from enemies, so velocity-
  // based aim sweeps the spatula away from threats. The engine already
  // computes a smart aim-assist for arc weapons when bot is active
  // (weaponAdapter.ts:219 — picks nearest enemy in arc reach). When that
  // assist fires, it stores the chosen angle here so getMovementAimAngle
  // can prefer it over raw velocity. Null = bot inactive or no enemy in
  // reach (human play, or bot exploring) → fall through to velocity-based
  // PSBOT spec.
  arcAimOverride?: number | null;
  /** BETA 13.98 — When true, player has the Drac's Gas Mask item equipped.
   *  Multiplies the Drac's Fang lifestealPct from 8% → 20% on hit. */
  hasDracsGasMask?: boolean;
  // Stat bonuses from Peanut Percs and Buncha Bullshiiii
  bonusDamagePercent: number;      // integer percentage points, e.g. 20 = +20% (divided by 100 at use)
  bonusAttackSpeedPercent: number;  // integer percentage points, e.g. 15 = +15% (divided by 100 at use)
  bonusCritChance: number;          // integer percentage points, e.g. 10 = +10% (divided by 100 at use)
  bonusCritDamage: number;          // integer percentage points, e.g. 50 = +50% crit dmg (divided by 100 at use)
  bonusProjectileSpeed: number;     // Flat added to projectile speed
  bonusProjectileSize: number;      // Flat added to projectile size
  bonusKnockback: number;           // % bonus (0 = no bonus, 15 = 15% more knockback)
  bonusProjectileCount: number;     // Flat extra projectiles fired per shot (from shrines)
  bonusRange: number;               // Flat world-units added to lock-on targeting range
  // BETA 13.82 — flat extra projectile bounces from shrines/items, added on top of weapon.projectileBounces.
  bonusProjectileBounces: number;
  // BETA 19.98 — flat extra projectile pierce from N08 Not Ritalin perc, added on top of weapon.projectilePiercing.
  bonusProjectilePiercing: number;
  // BETA 16.27 — PSBOT cycle-29 P0: arc weapons (Spatula, Drac's Fang)
  // were reading `bonusProjectileSize` for their swing sprite scale, but
  // size buffs (Not Viagra T13, Wizard Surge, etc.) feed `attackAreaBonus`
  // not bonusProjectileSize. Plumb attackAreaBonus through so the arc
  // sprite + hit sector both scale with size buffs as intended. Integer
  // percent points (e.g. 50 = +50%).
  attackAreaBonus: number;
}

// ─── Camera Interface ───────────────────────────────────────
export interface CameraForWeapon {
  x: number;
  y: number;
  viewportWidth: number;
  viewportHeight: number;
  zoom: number;
}

// ─── Enemy Interface (what we need from enemySystem.ts) ─────
export interface EnemyForWeapon {
  id: number;
  type: string;     // BETA 14.47 — needed for weaponEnemyMatrix attribution
  x: number;
  y: number;
  hitboxRadius: number;
  alive: boolean;
  variant?: string; // BETA 22.08 — 'normal' | 'elite' | 'boss', so auto-aim can prioritize crowns/bosses
}

// ─── Damage Callback ────────────────────────────────────────
// Called when a projectile hits an enemy. The engine wires this
// to enemySystem.damageEnemy().
export type OnEnemyHitCallback = (
  enemyId: number,
  damage: number,
  knockbackForce: number,
  knockbackAngle: number,
  isCrit: boolean,
  weaponDefId?: string,   // BETA — which weapon landed the hit, for per-weapon impact SFX
) => void;

/** BETA 13.95 — Callback to apply a status effect to an enemy on hit.
 *  Wired by the engine to enemySystem.applyEnemyStatus. */
export type OnApplyStatusCallback = (
  enemyId: number,
  effect: 'freeze' | 'slow' | 'wet',
  duration: number,
  slowMultiplier?: number,
) => void;

/** BETA 13.97 — Callback to strip armor / shield from an enemy on hit.
 *  Wired by the engine to enemySystem.applyEnemyArmorShieldStrip. */
export type OnApplyArmorStripCallback = (
  enemyId: number,
  shieldStrip: number,
  armorStrip: number,
) => void;

// ═══════════════════════════════════════════════════════════════
//  EQUIP / MANAGE WEAPONS
// ═══════════════════════════════════════════════════════════════

const MAX_WEAPON_SLOTS = 4;

/** Equip a weapon (add to player's weapon slots) */
export function equipWeapon(
  state: WeaponSystemState,
  definitionId: string,
  rarity: WeaponRarity = 'common',
): boolean {
  // BETA 20.86 — NEVER duplicate an owned weapon. A "new weapon" pick for a
  // weapon you already have was pushing a second level-1 instance — Dylan's
  // level-5 RPK kept offering "Lv 1 → 2" because the upgrade landed on the
  // duplicate. Owners should level up (caller routes that); here we just
  // refuse the dupe as a hard backstop.
  if (state.weapons.some(w => w.definitionId === definitionId)) return false;
  if (state.weapons.length >= MAX_WEAPON_SLOTS) return false;

  const instance = createWeaponInstance(definitionId, rarity);
  if (!instance) return false;

  // BETA 19.45 — Drac's Fang converted to throwable boomerang (Dylan: "make
  // drakes fang a throwable that gives you lifesteal... only one slash
  // weapon"). The BETA 19.44 anti-sync block (offset second arc weapon's
  // reload) is no longer needed — spatula is the only arc weapon left.
  // Single-weapon equip path runs straight through.

  state.weapons.push(instance);
  return true;
}

/** Check if player has a specific weapon equipped */
export function hasWeapon(state: WeaponSystemState, definitionId: string): boolean {
  return state.weapons.some(w => w.definitionId === definitionId);
}

/** BETA 13.71: Unequip a weapon by definitionId (used by Weapon Lab toggle UI). */
export function unequipWeapon(state: WeaponSystemState, definitionId: string): boolean {
  const idx = state.weapons.findIndex(w => w.definitionId === definitionId);
  if (idx < 0) return false;
  state.weapons.splice(idx, 1);
  return true;
}

/** Get a weapon instance by definition ID */
export function getEquippedWeapon(
  state: WeaponSystemState,
  definitionId: string,
): WeaponInstance | undefined {
  return state.weapons.find(w => w.definitionId === definitionId);
}

// ═══════════════════════════════════════════════════════════════
//  UPDATE (called every frame)
// ═══════════════════════════════════════════════════════════════

/**
 * Update all weapons and projectiles.
 * Called once per frame from engine.ts update().
 *
 * @param dt - Delta time in seconds
 * @param enemies - Array of alive enemies (from enemySystem)
 * @param onEnemyHit - Callback when a projectile hits an enemy
 */
export function updateWeapons(
  state: WeaponSystemState,
  dt: number,
  player: PlayerForWeapon,
  enemies: EnemyForWeapon[],
  onEnemyHit: OnEnemyHitCallback,
  viewHalfMin?: number,  // min(halfW, halfH) in world units — the shorter viewport dimension
  onApplyStatus?: OnApplyStatusCallback,  // BETA 13.95 — apply freeze/slow/wet on hit
  onApplyArmorStrip?: OnApplyArmorStripCallback,  // BETA 13.97 — strip shield/armor (Ultra Beam)
): void {
  state.elapsedTime += dt;

  // BETA 23.01 — smart-slide targeting/smoothing (before firing logic so
  // this frame's shots originate from the displaced positions).
  _updateWeaponSlides(state, dt, player);

  // ── Update each equipped weapon's firing logic ──
  for (const weapon of state.weapons) {
    updateWeaponFiring(state, weapon, dt, player, enemies, onEnemyHit, viewHalfMin, onApplyArmorStrip);
  }

  // BETA 23.03 — REST-POSE SAFETY NET: some firing-flow paths early-return
  // before the no-target rest easing (e.g. perpetual reload with zero
  // targets — a state the shooting range exposed: guns froze at their spawn
  // angle, "pointing at me"). Idle weapons ALWAYS ease to outward here.
  for (const weapon of state.weapons) {
    if (weapon.hasTarget) continue;
    const def = getWeaponDef(weapon.definitionId);
    if (!def || def.orbital || def.aura || def.arc) continue;
    const vis = resolveWeaponVisual(weapon.definitionId);
    const sp = getWeaponSlotPosition(state, weapon, player);
    const _outward = Math.atan2(sp.y - player.y, sp.x - player.x);
    const rest = vis.restAngleMode === 'fixed' ? vis.restAngle
      : vis.restAngleMode === 'tangent' ? _outward + Math.PI / 2 + vis.orbitAngleOffset
      : _outward + vis.orbitAngleOffset;
    weapon.aimAngle = lerpAngle(weapon.aimAngle, rest, Math.min(1, dt * vis.aimTurnSpeed * 0.5));
  }

  // ── Update all projectiles ──
  updateProjectiles(state, dt, enemies, onEnemyHit, player, onApplyStatus);

  // ── Update damage numbers ──
  updateDamageNumbers(state, dt);

  // ── Update muzzle flashes ──
  for (let i = state.muzzleFlashes.length - 1; i >= 0; i--) {
    state.muzzleFlashes[i].timer -= dt;
    if (state.muzzleFlashes[i].timer <= 0) {
      state.muzzleFlashes.splice(i, 1);
    }
  }

  // ── Update arc slash visuals + damage ────────────────────────────────
  // BETA 19.44 — Dylan spec: the slash sprite IS the damaging hitbox now.
  // tickArcSlashHits scans every alive enemy against every active slash and
  // applies damage on sprite-overlap. Per-slash hitEnemyIds prevents double-
  // hitting the same enemy as the sprite sweeps past it. In orbit mode the
  // hitEnemyIds Set clears every orbitHitResetSec so a continuously-spinning
  // sprite can re-hit an enemy as it swings back around.
  tickArcSlashHits(state, dt, player, enemies, onEnemyHit);

  for (let i = state.arcSlashes.length - 1; i >= 0; i--) {
    state.arcSlashes[i].timer -= dt;
    if (state.arcSlashes[i].timer <= 0) {
      state.arcSlashes.splice(i, 1);
    }
  }
}

// ═══════════════════════════════════════════════════════════════
//  WEAPON FIRING LOGIC
// ═══════════════════════════════════════════════════════════════

function updateWeaponFiring(
  state: WeaponSystemState,
  weapon: WeaponInstance,
  dt: number,
  player: PlayerForWeapon,
  enemies: EnemyForWeapon[],
  onEnemyHit: OnEnemyHitCallback,
  viewHalfMin?: number,  // min(halfW, halfH) — the shorter viewport dimension
  onApplyArmorStrip?: OnApplyArmorStripCallback,  // BETA 13.97 — Ultra Beam shield/armor strip
): void {
  const def = getWeaponDef(weapon.definitionId);
  if (!def) return;

  // BETA 13.96 — tick down fired-sprite flash (Poop Sling loaded↔fired animation)
  if (weapon.firedFlashTimer > 0) {
    weapon.firedFlashTimer -= dt;
    if (weapon.firedFlashTimer < 0) weapon.firedFlashTimer = 0;
  }
  // BETA 22.69 — recoil kick decay (render-only feel state)
  if ((weapon.recoilTimer ?? 0) > 0) {
    weapon.recoilTimer = Math.max(0, (weapon.recoilTimer ?? 0) - dt);
  }

  // ── BETA 13.100 — Karen Drones contact damage. When def.orbitalContactDamage,
  // Karen damages enemies in radius around her orbital position with a per-enemy
  // cooldown. No projectiles fired. Skip the rest of normal firing logic.
  if (def.orbital && def.orbitalContactDamage) {
    // BETA 14.30 (BUG-03) — orbital weapons increment tickEvents per active
    // frame so accuracy math works (totalShotsFired stays 0 for these
    // since they don't fire projectiles).
    weapon.totalTickEvents++;
    // Tick down per-enemy cooldowns
    const _toDelete: number[] = [];
    weapon.contactCooldowns.forEach((cd, eid) => {
      const newCd = cd - dt;
      if (newCd <= 0) _toDelete.push(eid);
      else weapon.contactCooldowns.set(eid, newCd);
    });
    for (const eid of _toDelete) weapon.contactCooldowns.delete(eid);

    // BETA 16.24 — PSBOT cycle-25 P0.1: iterate over ALL drone positions,
    // not just the single slot. Drone count = 1 + bonusProjectileCount.
    //
    // BETA 16.27 — PSBOT cycle-30b Bug 1 ROOT CAUSE FOUND. The cycle-25
    // shipped with `if (weapon.contactCooldowns.has(enemy.id)) continue;`
    // — a SHARED cooldown across all drones. Result: 4 drones still hit
    // each enemy max once per 0.5s = SAME DPS as 1 drone. PSBOT cycle-28
    // data: 3.7% hit rate, 1834 avg damage (5-8× lower than peer weapons).
    //
    // Fix: per-drone cooldown. Composite key `${droneIdx}:${enemyId}` so
    // drone 0 and drone 1 each track their own per-enemy cooldown
    // independently. With 4 drones this multiplies effective hit rate
    // ~4× since each drone has its own 0.5s cd against the same enemy.
    // Cycle-30b Bug 1 expected outcome: avg damage 1834 → 8000-15000.
    const dronePositions = getOrbitalDronePositions(state, weapon, def, player);
    const contactRadius = def.orbitalContactRadius ?? 32;
    const contactCd = def.orbitalContactCooldown ?? 0.5;
    // BETA 17.07 — Karen diagnostic instrumentation. Update peak count.
    if (dronePositions.length > _karenDroneTrace.peakDroneCount) {
      _karenDroneTrace.peakDroneCount = dronePositions.length;
    }
    for (let droneIdx = 0; droneIdx < dronePositions.length; droneIdx++) {
      const slotPos = dronePositions[droneIdx];
      for (const enemy of enemies) {
        if (!enemy.alive) continue;
        // Per-drone cooldown: composite numeric key isolates drone 0 vs
        // drone 1 vs drone 2 etc. against the same enemy. droneIdx ×
        // 100000 + enemyId guarantees no collision (enemy IDs are
        // unique sequential ints < 100000 per run).
        const cdKey = droneIdx * 100000 + enemy.id;
        if (weapon.contactCooldowns.has(cdKey)) { _karenDroneTrace.cooldownSkips++; continue; }
        const dx = enemy.x - slotPos.x;
        const dy = enemy.y - slotPos.y;
        const reach = contactRadius + enemy.hitboxRadius;
        if (dx * dx + dy * dy > reach * reach) { _karenDroneTrace.radiusSkips++; continue; }
        // BETA 16.08 — LoS check from this drone's position (each drone
        // can be on a different side of a wall).
        if (state.hasLineOfSight && !state.hasLineOfSight(slotPos.x, slotPos.y, enemy.x, enemy.y)) { _karenDroneTrace.losSkips++; continue; }
        _karenDroneTrace.contacts++;
        const baseDmg = weapon.damage * (1 + player.bonusDamagePercent / 100);
        const totalCritChance = Math.min(1.0, weapon.critChance + player.bonusCritChance / 100);
        const isCrit = Math.random() < totalCritChance;
        const totalCritDamage = weapon.critDamage + player.bonusCritDamage / 100;
        const finalDmg = isCrit ? Math.round(baseDmg * (1 + totalCritDamage)) : Math.round(baseDmg);
        const knockAngle = Math.atan2(dy, dx);
        onEnemyHit(enemy.id, finalDmg, weapon.knockback, knockAngle, isCrit, weapon.definitionId);
        _weaponHitTracker?.(weapon.definitionId, enemy.type, finalDmg, isCrit);
        state.totalDamageDealt += finalDmg;
        weapon.totalDamageDealt += finalDmg;
        weapon.totalShotsHit++;
        if (isCrit) { state.totalCrits++; weapon.totalCrits++; weapon.totalCritRolls++; }
        weapon.contactCooldowns.set(cdKey, contactCd);
        spawnDamageNumber(state, {
          x: enemy.x + (Math.random() - 0.5) * 14,
          y: enemy.y - 14,
          value: finalDmg,
          isCrit,
          timer: isCrit ? 0.7 : 0.45,
          velocityY: -50,
        });
      }
    }
    return; // skip the regular firing loop entirely
  }

  // ── BETA 20.06 — SECONDARY orbital contact damage (Karen Drones) ──
  // Runs alongside projectile firing (does NOT return). The spinning drone
  // bodies chip enemies they touch for a fraction of weapon damage. Per Dylan:
  // "i love how they spin in a circle fast as a secondary attack. i dont think
  // any enemy took damage just by the drones touching them." Per-drone-per-enemy
  // cooldown keyed in a separate namespace (droneIdx*100000 + enemyId + a big
  // offset) so it never collides with the primary-contact path's keys.
  if (def.orbital && def.orbitalSecondaryContactDamage && def.orbitalSecondaryContactDamage > 0) {
    // Tick down the secondary-contact cooldowns (stored in the same map, high keys).
    const _secToDelete: number[] = [];
    weapon.contactCooldowns.forEach((cd, key) => {
      if (key < 1_000_000) return; // only tick secondary-namespace keys here
      const newCd = cd - dt;
      if (newCd <= 0) _secToDelete.push(key);
      else weapon.contactCooldowns.set(key, newCd);
    });
    for (const key of _secToDelete) weapon.contactCooldowns.delete(key);

    const _secPositions = getOrbitalDronePositions(state, weapon, def, player);
    const _secRadius = def.orbitalContactRadius ?? 40;
    const _secCd = def.orbitalContactCooldown ?? 0.4;
    const _secMult = def.orbitalSecondaryContactDamage;
    for (let _di = 0; _di < _secPositions.length; _di++) {
      const _dp = _secPositions[_di];
      for (const enemy of enemies) {
        if (!enemy.alive) continue;
        const _secKey = 1_000_000 + _di * 100_000 + enemy.id;
        if (weapon.contactCooldowns.has(_secKey)) continue;
        const _ddx = enemy.x - _dp.x;
        const _ddy = enemy.y - _dp.y;
        const _reach = _secRadius + enemy.hitboxRadius;
        if (_ddx * _ddx + _ddy * _ddy > _reach * _reach) continue;
        if (state.hasLineOfSight && !state.hasLineOfSight(_dp.x, _dp.y, enemy.x, enemy.y)) continue;
        const _secBase = weapon.damage * (1 + player.bonusDamagePercent / 100) * _secMult;
        const _secCrit = Math.random() < Math.min(1.0, weapon.critChance + player.bonusCritChance / 100);
        const _secDmg = Math.max(1, Math.round(_secCrit ? _secBase * (1 + weapon.critDamage + player.bonusCritDamage / 100) : _secBase));
        onEnemyHit(enemy.id, _secDmg, weapon.knockback * 0.5, Math.atan2(_ddy, _ddx), _secCrit, weapon.definitionId);
        _weaponHitTracker?.(weapon.definitionId, enemy.type, _secDmg, _secCrit);
        state.totalDamageDealt += _secDmg;
        weapon.totalDamageDealt += _secDmg;
        weapon.contactCooldowns.set(_secKey, _secCd);
        spawnDamageNumber(state, {
          x: enemy.x + (Math.random() - 0.5) * 14,
          y: enemy.y - 14,
          value: _secDmg, isCrit: _secCrit,
          timer: _secCrit ? 0.7 : 0.45, velocityY: -50,
        });
      }
    }
    // NO return — fall through to the projectile-firing logic below so the
    // purse projectile still throws as the primary attack.
  }

  // ── Aura weapons are handled entirely separately ──
  if (def.aura) {
    updateAuraWeapon(state, weapon, def, dt, player, enemies, onEnemyHit, onApplyArmorStrip);
    return;
  }

  // ── Arc weapons (directional melee swing) ──
  if (def.arc) {
    updateArcWeapon(state, weapon, def, dt, player, enemies, onEnemyHit);
    return;
  }

  // ── BETA 15.74 — Charge-up tick (Ultra Beam etc.) ─────────────────────────
  // When def.chargeUpMs > 0, the weapon "winds up" between trigger and shot.
  // Audio plays at trigger (in the reload-completion block below), then 737ms
  // later the projectile spawns. This block ticks the wind-up timer down each
  // frame; while it's > 0 we early-return (no firing, no aim recalc — keeps
  // aim direction effectively locked from the player's natural drift).
  // When the timer hits 0, we open up the firing state so the next-frame fire
  // branch spawns the projectile.
  const chargeUpMs = def.chargeUpMs ?? 0;
  if (weapon.chargeUpRemaining > 0) {
    weapon.chargeUpRemaining -= dt;
    if (weapon.chargeUpRemaining <= 0) {
      weapon.chargeUpRemaining = 0;
      // Charge complete: ready to fire — set up burst state so the firing
      // branch executes this frame.
      weapon.isFiring = true;
      weapon.currentAmmo = weapon.projectileCount;
      weapon.burstTimer = 0;
      // BETA 20.85 — Dylan: "the sound should activate ON the shot." Fire the
      // beam audio HERE, at charge-complete = the exact frame the projectile
      // spawns. playUltraBeamFire skips the WAV's quiet lead-in so the blast
      // is instant. (No second sound — this is the only beam fire SFX.)
      if (def.id === 'wpn_ultra_beam') playUltraBeamFire();
      // BETA: do NOT restore the trigger-time locked aim angle here. The
      // per-frame aim-tracking block below re-points the weapon at the
      // current nearest enemy, so the beam auto-aims at FIRE time like every
      // other auto_aim weapon (was: weapon.aimAngle = chargeUpLockedAimAngle,
      // which fired a stale direction if enemies juked during the charge).
      // Fall through into the normal firing logic below.
    } else {
      // Still charging — skip aim recalc + reload tick + fire branch.
      return;
    }
  }

  // Megabonk formula: cooldown ÷ (1 + totalBonus/100). Diminishing returns — each extra
  // stack gives less gain. Hard floor prevents sub-0.1s cooldowns regardless of stacking.
  const attackSpeedMult = 1 / (1 + player.bonusAttackSpeedPercent / 100);

  // ── Per-weapon targeting range ──
  // Each weapon definition sets its own lockOnRange matching its personality.
  // player.bonusRange (from Lock-On Range shrines) adds to the base.
  // viewHalfMin (min of halfW/halfH) is a hard ceiling so enemies are always
  // on-screen even with max range shrine stacking.
  const baseRange    = def.lockOnRange ?? FALLBACK_LOCK_ON_RANGE;
  const buffedRange  = baseRange + player.bonusRange;
  const clampedRange = viewHalfMin
    ? Math.min(buffedRange, viewHalfMin)   // hard ceiling = screen's shorter edge
    : Math.min(buffedRange, baseRange * 1.5); // fallback: cap at 150% of base

  // ── Visual aim tracking — every frame, point this weapon at its current target ──
  // Uses the player's orbit-slot position so the angle is from the muzzle, not the player center.
  // BETA 22.69 — per-weapon aim feel: light weapons track fast (SMG ~24),
  // heavies swing slow (~9). No target → ease to the weapon's REST angle
  // instead of hard-swapping to the slot tangent (killed the old snap when
  // a target died). Default config reproduces the historical dt*18 + outward
  // resting pose exactly.
  const vis = resolveWeaponVisual(weapon.definitionId);
  const slotPos = getWeaponSlotPosition(state, weapon, player);
  const aimTarget = findNearestEnemy({ x: slotPos.x, y: slotPos.y } as PlayerForWeapon, enemies, clampedRange, { x: player.x, y: player.y, r: 48 });
  if (aimTarget) {
    const desired = Math.atan2(aimTarget.y - slotPos.y, aimTarget.x - slotPos.x);
    weapon.aimAngle = lerpAngle(weapon.aimAngle, desired, Math.min(1, dt * vis.aimTurnSpeed));
    weapon.hasTarget = true;
    // BETA 22.70 — Firestarter hose: steady ember + soot drip off the
    // continuous flame cone (rate-limited; density mult applies inside the
    // particle bridge). Keeps the fire alive between damage shots.
    if (weapon.definitionId === 'fireball_wand' && _weaponParticleSpawner
        && state.elapsedTime - _flameEmberLast > 0.05) {
      _flameEmberLast = state.elapsedTime;
      const tip = getBarrelTipOffset(getWeaponDef(weapon.definitionId)!, weapon.aimAngle, weapon);
      const ex = slotPos.x + tip.dx, ey = slotPos.y + tip.dy;
      // hot streaks shooting down the cone
      _weaponParticleSpawner(ex, ey, '#ffb703', 2, { shape: 'spark', dir: weapon.aimAngle, spread: 0.5, speedMin: 260, speedMax: 480, lifeMin: 0.15, lifeMax: 0.35, sizeMin: 2, sizeMax: 4, stretch: 5 });
      // fat orange flame blobs tumbling mid-cone (the "real fire" body)
      _weaponParticleSpawner(ex, ey, '#e85d04', 2, { shape: 'mote', dir: weapon.aimAngle, spread: 0.55, speedMin: 140, speedMax: 300, lifeMin: 0.25, lifeMax: 0.5, sizeMin: 3, sizeMax: 6.5, gravity: -30, drag: 0.93 });
      _weaponParticleSpawner(ex, ey, '#c23a00', 1, { shape: 'mote', dir: weapon.aimAngle, spread: 0.6, speedMin: 100, speedMax: 220, lifeMin: 0.3, lifeMax: 0.55, sizeMin: 3, sizeMax: 6, gravity: -40, drag: 0.92 });
      // sooty smoke curling off the END of the cone
      const smx = ex + Math.cos(weapon.aimAngle) * FLAME_CONE_LEN * 0.7;
      const smy = ey + Math.sin(weapon.aimAngle) * FLAME_CONE_LEN * 0.7;
      _weaponParticleSpawner(smx, smy, '#4a423a', 1, { shape: 'mote', gravity: -70, drag: 0.9, speedMin: 15, speedMax: 70, lifeMin: 0.5, lifeMax: 1.0, sizeMin: 3, sizeMax: 6 });
    }
  } else {
    weapon.hasTarget = false;
    // BETA 23.03 — outward computed from REAL positions (atan2 gun−player),
    // not slotPos.angle bookkeeping: guaranteed to point AWAY from Greg
    // (Dylan: "frozen weapon is pointing at me").
    const _outward = Math.atan2(slotPos.y - player.y, slotPos.x - player.x);
    const rest = vis.restAngleMode === 'fixed' ? vis.restAngle
      : vis.restAngleMode === 'tangent' ? _outward + Math.PI / 2 + vis.orbitAngleOffset
      : _outward + vis.orbitAngleOffset;
    weapon.aimAngle = lerpAngle(weapon.aimAngle, rest, Math.min(1, dt * vis.aimTurnSpeed * 0.5));
  }

  // ── BETA 22.72 — FLAME CONE weapon (Firestarter). Dylan: "the continuous
  // flame does zero damage... and they don't stay on fire." The cone IS the
  // weapon now — no projectiles. Every enemy inside the cone takes
  // weapon.damage on its OWN per-enemy cooldown (the proven aura pattern),
  // so damage-per-touch and cadence match the old fireball exactly; the
  // flames just deliver it, which also re-ignites the burn visual constantly.
  if (def.flameCone) {
    // HUD reads a steady weapon — no reload bar for a hose.
    weapon.isFiring = true;
    weapon.isReloading = false;
    weapon.currentAmmo = weapon.projectileCount;
    const _fcCooldown = Math.max(MIN_WEAPON_COOLDOWN, weapon.reloadTime * attackSpeedMult);
    if (!weapon.auraEnemyCd) weapon.auraEnemyCd = new Map<number, number>();
    const _fcCd = weapon.auraEnemyCd;
    _fcCd.forEach((_rem, _eid) => {
      const _r = _rem - dt;
      if (_r <= -2) _fcCd.delete(_eid); else _fcCd.set(_eid, _r);
    });
    if (!weapon.hasTarget) return;
    const _fcTip = getBarrelTipOffset(def, weapon.aimAngle, weapon);
    const _fcMx = slotPos.x + _fcTip.dx, _fcMy = slotPos.y + _fcTip.dy;
    // telemetry beat (aura convention: tickEvents is the accuracy denominator)
    weapon.auraTick -= dt;
    if (weapon.auraTick <= 0) {
      weapon.auraTick = _fcCooldown;
      weapon.totalTickEvents++;
    }
    for (const enemy of enemies) {
      if (!enemy.alive) continue;
      if ((_fcCd.get(enemy.id) ?? 0) > 0) continue;
      const _dx = enemy.x - _fcMx, _dy = enemy.y - _fcMy;
      const _dist = Math.hypot(_dx, _dy);
      if (_dist > FLAME_CONE_LEN + enemy.hitboxRadius) continue;
      let _dA = Math.atan2(_dy, _dx) - weapon.aimAngle;
      while (_dA > Math.PI) _dA -= Math.PI * 2;
      while (_dA < -Math.PI) _dA += Math.PI * 2;
      // hitbox slop widens the cone for fat enemies near the edge
      const _slop = Math.atan2(enemy.hitboxRadius, Math.max(20, _dist));
      if (Math.abs(_dA) > FLAME_CONE_HALF_ANGLE + _slop) continue;
      if (state.hasLineOfSight && !state.hasLineOfSight(_fcMx, _fcMy, enemy.x, enemy.y)) continue;
      const _isCrit = Math.random() < weapon.critChance;
      if (_isCrit) weapon.totalCritRolls++;
      const _dmg = _isCrit
        ? Math.round(weapon.damage * (1 + weapon.critDamage))
        : Math.round(weapon.damage);
      onEnemyHit(enemy.id, _dmg, weapon.knockback * 0.4, Math.atan2(_dy, _dx), _isCrit, weapon.definitionId);
      weapon.totalDamageDealt += _dmg;
      weapon.totalShotsHit++;
      if (_isCrit) weapon.totalCrits++;
      state.totalDamageDealt += _dmg;
      _fcCd.set(enemy.id, _fcCooldown);
    }
    return;
  }

  // ── RELOADING STATE ──
  if (weapon.isReloading) {
    weapon.reloadTimer -= dt;
    if (weapon.reloadTimer <= 0) {
      // Reload complete
      weapon.isReloading = false;
      // BETA 15.74 — Charge-up weapons (Ultra Beam) start a wind-up phase
      // here instead of firing immediately. Audio plays at trigger (now);
      // projectile spawns chargeUpMs later (handled in the charge-up tick
      // block at the top of this function on subsequent frames). Aim is
      // locked at this moment so enemies juking during charge don't
      // dodge the eventual beam.
      if (chargeUpMs > 0) {
        const scaledCooldownMs = Math.max(50, weapon.reloadTime * attackSpeedMult * 1000);
        weapon.chargeUpRemaining = scaledCooldownMs / 1000;
        weapon.chargeUpTotal = weapon.chargeUpRemaining;   // BETA 22.86 — for the charge-glow ramp
        weapon.chargeUpLockedAimAngle = weapon.aimAngle;
        weapon.isFiring = false;
        // BETA 20.85 — Ultra Beam audio MOVED to charge-COMPLETE (the actual
        // shot — see the charge-tick block near the top of this fn). It used
        // to fire HERE at charge-START, so the WAV's quiet ~0.5s wind-up
        // played during the windup and the BLAM landed misaligned with the
        // shot (Dylan: "delayed / starts silent for a second or two"). The
        // old playbackRate-stretch that aligned it was removed in 20.51.
      } else {
        // Normal weapon: refill ammo, start firing
        weapon.isFiring = true;
        weapon.currentAmmo = weapon.projectileCount;
        weapon.burstTimer = 0;
      }
    }
    return;
  }

  // ── FIRING STATE (burst mode) ──
  if (weapon.isFiring && weapon.currentAmmo > 0) {
    weapon.burstTimer -= dt;

    if (weapon.burstTimer <= 0) {
      // Fire one shot from the burst — aim from the weapon's orbit slot, not player center
      let target = aimTarget ?? findNearestEnemy(player, enemies, clampedRange);

      // BETA — auto-aim charge-up weapons (Ultra Beam) at FIRE time. If the
      // target the burst captured is gone (died / left range during the
      // charge), re-evaluate the nearest enemy from the weapon slot — the
      // SAME lookup the per-frame aim-tracking block uses — and snap aim to
      // it so the beam tracks the live nearest enemy instead of firing a
      // stale locked direction. If there's genuinely no enemy in range,
      // leave target null and skip firing this frame (the `if (target)`
      // guard below handles that) rather than blasting a phantom direction.
      if (!target && (def.chargeUpMs ?? 0) > 0) {
        const reTarget = findNearestEnemy(
          { x: slotPos.x, y: slotPos.y } as PlayerForWeapon,
          enemies,
          clampedRange,
        );
        if (reTarget) {
          weapon.aimAngle = Math.atan2(reTarget.y - slotPos.y, reTarget.x - slotPos.x);
          target = reTarget;
        }
      }

      if (target) {
        // BETA 20.50 — Snap the visual aim STRAIGHT at the firing target this
        // frame so the gun sprite, the barrel-tip origin, AND the projectile
        // velocity all agree. weapon.aimAngle is otherwise only partway through
        // its per-frame visual lerp (~line 1189), so the gun could point one way
        // (e.g. fully east) while the beam fired another (e.g. south), and the
        // barrel tip sat along the wrong angle — throwing close-range shots wide
        // (the "Ultra Beam shoots sideways + misses" report). The lerp still
        // smooths aim BETWEEN shots; this only corrects the fire frame itself.
        weapon.aimAngle = Math.atan2(target.y - slotPos.y, target.x - slotPos.x);
        const tipOffset = getBarrelTipOffset(def, weapon.aimAngle, weapon);
        const barrelOrigin = {
          x: slotPos.x + tipOffset.dx,
          y: slotPos.y + tipOffset.dy,
        };

        // BETA 15.26 — character passive fire hook. Killa: timestamp last
        // fire so Suppression Fire timer increments. Cowboy: first projectile
        // of every burst gets the Quick Draw bonus (+25% dmg + force crit).
        // isFirstBurstShot detected by ammo === full magazine (pre-decrement).
        const _isFirstBurstShot = (weapon.currentAmmo === weapon.projectileCount);
        const _passive = onPlayerFireProjectile(state.elapsedTime, _isFirstBurstShot);
        const _drawShot = _passive.cowboyDrawShot;

        // BETA 13.44 — instant-hit weapons (snipers): apply damage to the locked target
        // AT FIRE TIME so a sniper shot is guaranteed to land even if the target dies
        // mid-flight or the projectile expires. Visual projectile still spawns and
        // travels for feedback (and can piercing-hit other enemies on the way).
        if (def.instantHit && target.alive) {
          // BETA 16.08 — Snipers / instant-hit weapons skip the projectile
          // wall-kill because they damage at fire-time. Add the LoS check
          // here so they don't hit through walls. Per Dylan: "check every
          // weapon. they are not allowed to shoot through or over walls."
          if (state.hasLineOfSight && !state.hasLineOfSight(barrelOrigin.x, barrelOrigin.y, target.x, target.y)) {
            // Skip the instant-hit damage for this shot. The visual
            // projectile still spawns below for feedback (and dies at the
            // wall via the projectile wall-kill in weaponAdapter).
          } else {
          let baseDmg = weapon.damage * (1 + player.bonusDamagePercent / 100);
          // BETA 15.26 — Quick Draw: +25% damage to first projectile of burst
          // BETA 20.88 — credit the +25% to Cowboy's signature bonus (telemetry).
          if (_drawShot) { creditCowboyQuickDraw(baseDmg); baseDmg *= 1.25; }
          const totalCritChance = Math.min(1.0, weapon.critChance + player.bonusCritChance / 100);
          // BETA 15.26 — Quick Draw: forced crit on the draw shot
          const isCrit = _drawShot ? true : (Math.random() < totalCritChance);
          const totalCritDamage = weapon.critDamage + player.bonusCritDamage / 100;
          const finalDamage = isCrit
            ? Math.round(baseDmg * (1 + totalCritDamage))
            : Math.round(baseDmg);
          const dx = target.x - barrelOrigin.x;
          const dy = target.y - barrelOrigin.y;
          const knockAngle = Math.atan2(dy, dx);
          const totalKnockback = weapon.knockback * (1 + player.bonusKnockback / 100);
          onEnemyHit(target.id, finalDamage, totalKnockback, knockAngle, isCrit, weapon.definitionId);
          _weaponHitTracker?.(weapon.definitionId, target.type, finalDamage, isCrit); // BETA 14.47
          // BETA 20.72 — hitscan impact spray: instant-hit weapons damage at
          // fire time, so the projectile collision path (and its new impact
          // VFX) never fires for the REAL hit. Show the connect at the target
          // in the weapon's own color — rifle ice-blue, revolver gold.
          _beamImpactVfx?.(target.x, target.y, knockAngle, def.id === 'peanut_rifle' ? '#CFEFFF' : '#FFD700');
          state.totalDamageDealt += finalDamage;
          weapon.totalDamageDealt += finalDamage;
          weapon.totalShotsHit++;
          if (isCrit) { state.totalCrits++; weapon.totalCrits++; weapon.totalCritRolls++; /* BETA 14.30 BUG-01 / BETA 17.07 audit (PSBOT cycle-30v #2): this is the INSTANT-HIT branch — one fire-cycle locks ONE target, so this IS per-shot. Per-target inflation doesn't apply here. Original TODO was misleading; current call is correct. */ }
          // Floating damage number
          spawnDamageNumber(state, {
            x: target.x + (Math.random() - 0.5) * 20,
            y: target.y - 20,
            value: finalDamage,
            isCrit,
            timer: isCrit ? 0.8 : 0.5,
            velocityY: -60,
          });
          } // end LoS-passed branch (BETA 16.08)
        }

        // BETA 15.26 — pass Quick Draw flag down to fireProjectile. For
        // non-instant-hit weapons, this controls the damage + crit roll
        // baked into the spawned projectile.
        // BETA 17.48 — Dylan: "if i have 5 karen drones only one shoots."
        // Root cause: barrelOrigin used the single anchor drone position
        // (line 1056) for the fire even when def.orbital. All visual
        // drones were just decoration — only the anchor fired. Fix:
        // for orbital weapons, fire ONE projectile from EACH drone
        // position so 5 visual drones = 5 projectiles per reload tick.
        const _orbitalBarrels = def.orbital
          ? getOrbitalDronePositions(state, weapon, def, player).map(p => ({
              x: p.x + Math.cos(weapon.aimAngle) * (def.barrelLength ?? 0),
              y: p.y + Math.sin(weapon.aimAngle) * (def.barrelLength ?? 0),
            }))
          : [barrelOrigin];
        for (const _bo of _orbitalBarrels) {
          fireProjectile(state, weapon, def, player, target, _bo, 0, _drawShot && !def.instantHit);
        }

        // For instant-hit weapons, prevent the just-spawned projectile from double-hitting
        // its locked target (we already applied damage above).
        if (def.instantHit) {
          const lastProj = state.projectiles[state.projectiles.length - 1];
          if (lastProj) lastProj.hitEnemyIds.add(target.id);
        }

        // Fire bonus projectiles simultaneously at per-weapon spread angles.
        // BETA 22.21 — a socketed MULTISHOT mod adds extra simultaneous bullets to
        // THIS gun only (the +3/+5/+7 the bench advertises), on top of any global
        // bonusProjectileCount from shrines/percs.
        const bonusCount = Math.floor(player.bonusProjectileCount) + weaponModMultishot(weapon.definitionId);
        if (bonusCount > 0) {
          const offsets = getBonusProjectileOffsets(weapon.definitionId, bonusCount);
          for (const angleDeg of offsets) {
            // BETA 17.48 — for orbital weapons, bonus projectiles spawn
            // from the anchor barrel (not each drone) to avoid 5×N bullet
            // explosion. Tuning if Dylan wants more chaos: change `[
            // barrelOrigin]` → `_orbitalBarrels` here too.
            fireProjectile(state, weapon, def, player, target, barrelOrigin, angleDeg);
          }
        }

        // BETA 17.48 — for orbital multi-drone shots, totalShotsFired
        // must count every drone projectile so hit% telemetry stays
        // accurate (was: 1 fire = 1 shot counted, but 5 drones spawning
        // 5 projectiles each tracked as separate hits — denominator off).
        const _orbitalExtraShots = def.orbital ? (_orbitalBarrels.length - 1) : 0;
        weapon.currentAmmo--;
        // BETA 16.28 — PSBOT cycle-30f §1.6 Bug 7 FIX. Was incrementing
        // by 1 per FIRE event, but each fire spawns `1 + bonusCount`
        // projectiles (line 988 base + 1002 bonus loop). totalShotsHit
        // increments per-projectile-collision so hit% can exceed 100%
        // when the player has +1+ projectile shrines. PSBOT cycle-30f
        // saw RPK at 129%, revolver 294%, kittens 531% hit rates —
        // impossible without broken denominator. Now totalShotsFired
        // increments by total projectiles fired this shot.
        const _shotsThisFire = 1 + bonusCount + _orbitalExtraShots;
        state.totalShotsFired += _shotsThisFire;
        weapon.totalShotsFired += _shotsThisFire;

        // BETA 13.96 — fired sprite flash (Poop Sling loaded → fired animation)
        if (def.firedSpriteUrl) {
          weapon.firedFlashTimer = def.firedSpriteDuration ?? 0.18;
        }

        // BETA 22.69 — recoil kick (render-only; slotPos/barrelOrigin untouched)
        const _visFire = resolveWeaponVisual(weapon.definitionId);
        if (_visFire.recoilDistance > 0) {
          weapon.recoilTimer = _visFire.recoilDuration;
        }

        // Set delay until next shot in burst
        weapon.burstTimer = def.burstDelay;
      } else {
        // No enemies in range — wait a frame and try again
        weapon.burstTimer = 0.05;
      }
    }

    // Check if burst is complete (all ammo spent)
    if (weapon.currentAmmo <= 0) {
      weapon.isFiring = false;
      weapon.isReloading = true;
      weapon.reloadTimer = Math.max(MIN_WEAPON_COOLDOWN, weapon.reloadTime * attackSpeedMult);
    }
  }
}

// ═══════════════════════════════════════════════════════════════
//  AURA WEAPON UPDATE
// ═══════════════════════════════════════════════════════════════

const AURA_PULSE_DURATION = 0.08; // brief visual ripple on each tick (very short — feels constant)

/**
 * Update an aura weapon. The full disc inside the ring radius deals continuous damage
 * to every enemy inside it — no waiting, no pulse cooldown visible to the player.
 * Ticks every ~0.25s (feels constant). Hit zone = entire circle, not just the edge.
 */
function updateAuraWeapon(
  state: WeaponSystemState,
  weapon: WeaponInstance,
  def: WeaponDefinition,
  dt: number,
  player: PlayerForWeapon,
  enemies: EnemyForWeapon[],
  onEnemyHit: OnEnemyHitCallback,
  onApplyArmorStrip?: OnApplyArmorStripCallback,
): void {
  // Megabonk formula — same as regular weapon path, with hard floor cap
  const cooldown = Math.max(MIN_WEAPON_COOLDOWN, weapon.reloadTime / (1 + player.bonusAttackSpeedPercent / 100));

  // Tick internal timer — BETA: this now drives ONLY the visual pulse + the
  // telemetry beat. Damage is PER-ENEMY (below) and no longer waits for this
  // shared tick, so a fast enemy can't slip through the gap between ticks.
  weapon.auraTick -= dt;

  // Brief visual pulse ripple
  if (weapon.auraPulseTimer > 0) {
    weapon.auraPulseTimer = Math.max(0, weapon.auraPulseTimer - dt);
  }

  // ── Global visual / telemetry beat (does NOT gate damage anymore) ──
  if (weapon.auraTick <= 0) {
    weapon.auraTick = cooldown;
    weapon.auraPulseTimer = AURA_PULSE_DURATION;
    // BETA 14.30 — aura pulses recorded in tickEvents; keep totalShotsFired too.
    weapon.totalShotsFired++;
    weapon.totalTickEvents++;
  }

  // ── PER-ENEMY damage cooldowns (BETA — Dylan: "as soon as it touches should
  // cause damage immediately, for every enemy, individually"). enemyId → seconds
  // until it can be hit again. A new enemy (no entry) is hit IMMEDIATELY on
  // contact; then it's on its own `cooldown` interval. Long-expired entries are
  // pruned so the map stays bounded.
  if (!weapon.auraEnemyCd) weapon.auraEnemyCd = new Map<number, number>();
  const _auraCd = weapon.auraEnemyCd;
  _auraCd.forEach((_rem, _eid) => {
    const _r = _rem - dt;
    if (_r <= -2) _auraCd.delete(_eid); else _auraCd.set(_eid, _r);
  });

  // Disc radius — the entire filled circle deals damage, not just the ring edge.
  // BETA 19.63 — PSBOT cycle-30aw P0: chad_aura hit rate dropped 28% → 22%
  // despite BETA 19.58 lawnmower-mode positioning fix. Root cause: aura
  // discRadius was using raw `weapon.projectileSize` — ignored
  // `attackAreaBonus` from items (Hutch's Sausage Fingers, Not Viagra,
  // etc.) AND from player level scaling. Now multiplied by attackAreaBonus
  // so items that grow projectile size also grow the aura ring. Plus
  // ultraState.chadAuraBonus (line 377 characterPassives.ts adds 15wu/lvl
  // for Chad specifically) gets read here too. Net: at L10 with one size
  // item equipped, aura ring goes from 130wu → 150wu base + 150wu char
  // = 300wu × 1.20 attackArea = 360wu effective. Enemies actually inside
  // the ring much more often → hit rate up.
  const sizeMult = 1 + (player.attackAreaBonus ?? 0) / 100;
  const chadAuraBonus = (player as unknown as { ultraState?: { chadAuraBonus?: number } }).ultraState?.chadAuraBonus ?? 0;
  // BETA 21.45 — Dylan: "any attack-range buff should grow the aura." For an
  // aura, its RANGE *is* its radius, so fold player.bonusRange (the flat
  // lock-on-range bonus from C01 Sausage Fingers / range shrines / level-up
  // range picks) into the disc. attackAreaBonus (%) still scales everything.
  // So BOTH attack-area AND attack-range now grow the aura.
  const discRadius = (weapon.projectileSize + chadAuraBonus + (player.bonusRange ?? 0)) * sizeMult;

  // BETA 13.49: track aura hits (was 0% in CSV — looked broken). Mark this pulse
  // as a hit if at least one enemy is in range when it fires.
  let auraPulseHitAtLeastOne = false;

  for (const enemy of enemies) {
    if (!enemy.alive) continue;

    const dx = enemy.x - player.x;
    const dy = enemy.y - player.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    // Enemy overlaps the filled disc (dist from center ≤ disc radius + enemy hitbox)
    if (dist - enemy.hitboxRadius > discRadius) continue;
    // BETA 16.08 — Per Dylan: "they are not allowed to shoot through or
    // over walls of rooms." Aura damage is radial-from-player; without
    // this check, walls don't block it. Skip enemies with no line of
    // sight from player.
    if (state.hasLineOfSight && !state.hasLineOfSight(player.x, player.y, enemy.x, enemy.y)) continue;

    // Per-enemy cooldown — skip if this enemy was hit within its own interval.
    // No entry (just entered the aura) or elapsed → fall through + damage NOW.
    const _ecd = _auraCd.get(enemy.id);
    if (_ecd !== undefined && _ecd > 0) continue;
    _auraCd.set(enemy.id, cooldown);
    auraPulseHitAtLeastOne = true;

    // Compute damage
    const baseDmg = weapon.damage * (1 + player.bonusDamagePercent / 100);
    const totalCritChance = Math.min(1.0, weapon.critChance + player.bonusCritChance / 100);
    const isCrit = Math.random() < totalCritChance;
    const totalCritDamage = weapon.critDamage + player.bonusCritDamage / 100;
    const finalDamage = isCrit
      ? Math.round(baseDmg * (1 + totalCritDamage))
      : Math.round(baseDmg);

    // Knockback pushes outward from player center
    // bonusKnockback is a % multiplier (e.g. 15 = +15%), NOT a flat add
    const knockbackAngle = dist > 0 ? Math.atan2(dy, dx) : 0;
    const totalKnockback = weapon.knockback * (1 + player.bonusKnockback / 100);

    onEnemyHit(enemy.id, finalDamage, totalKnockback, knockbackAngle, isCrit, weapon.definitionId);
    _weaponHitTracker?.(weapon.definitionId, enemy.type, finalDamage, isCrit); // BETA 14.47

    // BETA 13.97 — Ultra Beam armor/shield strip on each aura tick
    if (onApplyArmorStrip && (def.shieldStripPerHit || def.armorStripPerHit)) {
      onApplyArmorStrip(
        enemy.id,
        def.shieldStripPerHit ?? 0,
        def.armorStripPerHit ?? 0,
      );
    }

    // BETA 17.07 — PSBOT cycle-30v audit (aura per-target crit rolls).
    // Per Dylan B-pick: leave aura crit FEEL alone — keep independent
    // per-target rolls. Chad Aura / Ultra Beam: each enemy in pulse
    // rolls separately so crits appear "scattered" rather than the
    // whole-pulse-or-nothing burst pattern. Math is per-target but
    // PSBOT's >100% crit/hit complaint was about ARC PROJECTILES
    // (Spatula / Drac's Fang) which were already fixed in BETA 16.84
    // at line 1372-1384. No code change here.
    if (isCrit) { state.totalCrits++; weapon.totalCrits++; weapon.totalCritRolls++; }
    state.totalDamageDealt += finalDamage;
    weapon.totalDamageDealt += finalDamage;

    // Floating damage number at enemy position
    spawnDamageNumber(state, {
      x: enemy.x + (Math.random() - 0.5) * 20,
      y: enemy.y - 15,
      value: finalDamage,
      isCrit,
      timer: isCrit ? CRIT_FLASH_DURATION * 4 : 0.8,
      velocityY: -40,
    });
  }
  // BETA 13.49: aura pulse hit-tracking. Counts pulses that contacted ≥1 enemy.
  if (auraPulseHitAtLeastOne) weapon.totalShotsHit++;
}

// ═══════════════════════════════════════════════════════════════
//  ARC SLASH HIT TICK (BETA 19.44 — sprite-collision damage)
// ═══════════════════════════════════════════════════════════════

/**
 * How far the swinging sprite's CENTER sits from the player, expressed as a
 * fraction of slash.radius (the weapon's full reach). Was 0.6 (sprite center
 * at 60% of reach, sprite tail/handle landing ON the player at small sizes
 * — Dylan: "the handle and such is covering the main character"). Bumped to
 * 1.0 so the sprite center sits AT the reach distance — the handle now lives
 * outside the player's body and the blade tip extends past the reach line.
 * Both renderArcSlashes (visual) and tickArcSlashHits (hitbox) read this so
 * they stay in lockstep.
 */
const ARC_SPRITE_DISTANCE_FRACTION = 1.0;

/**
 * BETA 19.44 — Cached "last movement angle" for arc weapons that aim along
 * the player's movement direction (Spatula). When the player stops moving
 * (vx, vy ≈ 0), the swing would otherwise snap to facingAngle (mouse
 * direction) and feel jittery. PSBOT cycle-30at-ADDENDUM-2 spec: fall back
 * to the LAST KNOWN movement angle on stationary frames so the swing keeps
 * pointing the way the player last ran. Module-level state because the
 * player ref is short-lived (rebuilt every frame in weaponAdapter).
 *
 * Initialized to null — first call uses facingAngle until the player
 * actually moves once.
 */
let _lastMovementAngle: number | null = null;

/**
 * Minimum speed in world-units per second to register as "moving" for arc
 * weapons. Below this, fall back to _lastMovementAngle. The bot can twitch
 * vx/vy on idle frames and we don't want those micro-movements to drag the
 * swing around.
 */
const MOVEMENT_AIM_MIN_SPEED = 20;

/**
 * Compute the swing-aim direction for arc weapons that aim along the player's
 * movement vector (Spatula, per Dylan + PSBOT cycle-30at-ADDENDUM-2).
 *
 *  - BOT MODE: if the engine's arc aim-assist (weaponAdapter.ts:219)
 *    picked a nearest enemy in arc reach, use THAT angle. Dylan: "make
 *    the bot fucking smart as fuck" — the bot kites AWAY from enemies,
 *    so raw velocity-based aim would sweep into empty space. Smart aim
 *    keeps the swing pointed at the closest threat regardless of where
 *    the bot is fleeing to.
 *  - HUMAN MODE: if the player is currently moving fast enough, use the
 *    velocity angle (PSBOT spec — kite-clear weapon).
 *  - Else if we have a cached last-movement angle, use that (so a stationary
 *    swing keeps pointing where the player just ran from — kite finish).
 *  - Else fall back to facingAngle (game just started; player hasn't moved).
 */
function getMovementAimAngle(player: PlayerForWeapon): number {
  // Bot smart-aim override — only fires when bot is active AND an enemy is
  // within arc reach. Humans never trigger this branch (override stays null).
  if (player.arcAimOverride !== null && player.arcAimOverride !== undefined) {
    return player.arcAimOverride;
  }
  const speed = Math.hypot(player.vx, player.vy);
  if (speed >= MOVEMENT_AIM_MIN_SPEED) {
    _lastMovementAngle = Math.atan2(player.vy, player.vx);
    return _lastMovementAngle;
  }
  return _lastMovementAngle ?? player.facingAngle;
}

/**
 * Compute the live "sweep angle" for an ArcSlash this frame. Mirrors the
 * progress math used in renderArcSlashes so the visible sprite and the
 * hit-detection sprite are at the SAME world position.
 *
 *  fan mode  — sprite sweeps from start→end over the slash's lifetime
 *              (linear unless rampUp = ease-in).
 *  orbit mode — sprite spins continuously around the player at a rate that
 *               completes one full revolution per slash lifetime.
 */
function computeSlashSweepAngle(slash: ArcSlash): number {
  const t = slash.timer / slash.maxTimer;              // 1 → 0
  if (slash.mode === 'orbit') {
    // 0 → 2π over the slash lifetime (sprite makes one full revolution).
    const progress = 1 - t;
    return slash.centerAngle - Math.PI + progress * Math.PI * 2;
  }
  const startAngle = slash.centerAngle - slash.halfSpread;
  const endAngle   = slash.centerAngle + slash.halfSpread;
  const linearProgress = 1 - t;
  const progress = slash.rampUp ? linearProgress * linearProgress : linearProgress;
  return startAngle + progress * (endAngle - startAngle);
}

/**
 * Approximate sprite hitbox radius in world units. The sprite is drawn at
 * `slash.radius * 0.6 * sizeMult` from the player along sweepAngle, with a
 * height of `slash.spriteSize * sizeMult`. We treat the sprite as a circle
 * around its center with radius = half the longer dimension (assume sprites
 * are roughly square — close enough for melee feel). Cheaper + more
 * forgiving than oriented-rect-vs-circle math, and matches Dylan's
 * "if an enemy touches the sprite" spec: a fat melee swipe should not
 * miss enemies pressed against the blade.
 */
function spriteHitRadius(slash: ArcSlash): number {
  return slash.spriteSize * slash.sizeMult * 0.5;
}

/**
 * BETA 19.44 — Per-frame damage tick for every active ArcSlash.
 *
 * For each slash:
 *  1. (Spatula) If liveDirection, refresh centerAngle to the live player
 *     movement direction so the swing follows wherever you turn mid-swing.
 *  2. (Orbit) Tick the hitEnemyIds reset timer so the same enemy can be
 *     re-hit as the spinning sprite swings back around.
 *  3. Compute current sprite center (sweepAngle × spriteRadius from player).
 *  4. For each alive enemy NOT in hitEnemyIds, check circle-circle overlap
 *     (enemy.hitboxRadius vs spriteHitRadius). On hit:
 *       - roll crit independently per enemy
 *       - apply damage + knockback via onEnemyHit
 *       - apply lifesteal if the weapon has lifestealPct (Drac's Fang)
 *       - spawn floating damage number
 *       - bump the weapon-instance stats
 *       - record enemy.id in hitEnemyIds (prevents double-hits this swing
 *         until the orbit reset fires)
 */
function tickArcSlashHits(
  state: WeaponSystemState,
  dt: number,
  player: PlayerForWeapon,
  enemies: EnemyForWeapon[],
  onEnemyHit: OnEnemyHitCallback,
): void {
  if (state.arcSlashes.length === 0) return;
  for (const slash of state.arcSlashes) {
    if (slash.timer <= 0) continue;

    // Spatula spec: sprite follows the live MOVEMENT direction. Without this
    // a fast 180° turn would leave the swing pointing the old way. Uses
    // velocity (player.vx/vy via getMovementAimAngle) instead of facingAngle
    // so KB+M players running away from the mouse cursor still sweep behind
    // them (the kite-clear behaviour Dylan + PSBOT cycle-30at-ADD-2 specced).
    if (slash.liveDirection) {
      slash.centerAngle = getMovementAimAngle(player);
    }

    // Orbit hit-reset: the same enemy can be re-damaged as the sprite swings
    // back around if it's still inside the hitbox. Without this, a 360°
    // continuous spin would only ever damage each enemy once.
    if (slash.mode === 'orbit') {
      slash.orbitHitResetTimer -= dt;
      if (slash.orbitHitResetTimer <= 0) {
        slash.hitEnemyIds.clear();
        slash.orbitHitResetTimer = slash.orbitHitResetSec;
      }
    }

    // Sprite center in world coordinates. Mirrors renderArcSlashes:
    //   spriteRadius = slash.radius * ARC_SPRITE_DISTANCE_FRACTION × sizeMult
    //   position     = player + sweepAngle × spriteRadius
    const sweepAngle = computeSlashSweepAngle(slash);
    const spriteRadius = slash.radius * ARC_SPRITE_DISTANCE_FRACTION * slash.sizeMult;
    const spriteX = player.x + Math.cos(sweepAngle) * spriteRadius;
    const spriteY = player.y + Math.sin(sweepAngle) * spriteRadius;
    const hitR = spriteHitRadius(slash);

    // Sprite-vs-enemy circle overlap. Squared distance to avoid sqrt.
    for (const enemy of enemies) {
      if (!enemy.alive) continue;
      if (slash.hitEnemyIds.has(enemy.id)) continue;
      const dx = enemy.x - spriteX;
      const dy = enemy.y - spriteY;
      const reachR = hitR + enemy.hitboxRadius;
      if (dx * dx + dy * dy > reachR * reachR) continue;

      // Per-enemy crit roll — independent per hit so a 360° spin can crit
      // on enemy A and not B (matches projectile crit semantics).
      const isCrit = Math.random() < slash.critChance;
      const finalDamage = isCrit
        ? Math.round(slash.damage * (1 + slash.critDamage))
        : Math.round(slash.damage);

      // Knockback angle = from sprite-center → enemy (push them outward).
      const kbAngle = Math.atan2(dy, dx);
      onEnemyHit(enemy.id, finalDamage, slash.knockback, kbAngle, isCrit, slash.weaponId);

      // BETA 22.69 — melee impact sparks at the VISUAL contact point (Dylan:
      // "add impact effects at the point where the weapon visually contacts
      // the enemy"). Midpoint between the swinging sprite and the enemy edge,
      // sprayed outward along the knockback direction. Hitbox untouched.
      // BETA 22.74 — color is per-weapon via the WEAPON_VFX registry
      // (identity law); gray is only the fallback for unregistered melee.
      _beamImpactVfx?.(
        (spriteX + enemy.x) / 2,
        (spriteY + enemy.y) / 2,
        kbAngle,
        getWeaponVfx(slash.weaponId)?.meleeSparkColor ?? '#c8c0b0',
      );

      // Lifesteal — heals the engine-side player via the engine's existing
      // lifesteal application. weaponSystem doesn't own the player HP, so
      // we plumb lifesteal through onEnemyHit via the engine's `lifesteal`
      // stat path normally. For arc weapons we encode it directly: the
      // engine's enemySystem callback handles the damage; lifesteal lives
      // on player.bonusLifesteal already (item-driven). The arc weapon's
      // weapon-specific lifestealPct (Drac's Fang) was historically
      // applied inside updateProjectiles. We need to apply it here too:
      if (slash.lifestealPct > 0) {
        // BETA 19.44 — feeds the same per-frame bucket projectile-hit
        // lifesteal uses (engine consumes pendingLifestealHeal each tick
        // and bumps player.hp). Drac's Fang base = 8%, 20% with Gas Mask.
        // BETA 20.77 — cap parity: the player-STAT lifesteal path caps at 8
        // HP/hit (weaponAdapter "so lifesteal can't trivialise the game");
        // this weapon-def path was uncapped — at +430% damage one swing
        // pumped hundreds of HP (god-mode audit).
        state.pendingLifestealHeal += Math.min(8, finalDamage * slash.lifestealPct);
      }

      // Damage tally + crit tally on the owning weapon instance.
      state.totalDamageDealt += finalDamage;
      if (slash.weaponInstance) {
        slash.weaponInstance.totalDamageDealt += finalDamage;
        slash.weaponInstance.totalShotsHit++;
        if (isCrit) {
          slash.weaponInstance.totalCrits++;
          slash.weaponInstance.totalCritRolls++;
        }
        // BETA 14.47 — per-weapon × per-enemy attribution matrix
        _weaponHitTracker?.(slash.weaponInstance.definitionId, enemy.type, finalDamage, isCrit);
      }

      // Floating damage number — same shape as projectile-hit popups.
      spawnDamageNumber(state, {
        x: enemy.x + (Math.random() - 0.5) * 20,
        y: enemy.y - 15,
        value: finalDamage,
        isCrit,
        timer: isCrit ? CRIT_FLASH_DURATION * 4 : 0.8,
        velocityY: -40,
      });

      slash.hitEnemyIds.add(enemy.id);
    }
  }
}

// ═══════════════════════════════════════════════════════════════
//  ARC WEAPON UPDATE (directional melee swing)
// ═══════════════════════════════════════════════════════════════

/**
 * Update an arc weapon. On each cooldown, fires a fan of piercing projectiles
 * in the player's current facing direction. No target required.
 * Visual: spawns an ArcSlash effect that fades out quickly.
 */
function updateArcWeapon(
  state: WeaponSystemState,
  weapon: WeaponInstance,
  def: WeaponDefinition,
  dt: number,
  player: PlayerForWeapon,
  enemies: EnemyForWeapon[],
  onEnemyHit: OnEnemyHitCallback,
): void {
  // BETA 19.44 — arc weapons used to spawn invisible ray-projectiles for damage
  // and a separate cosmetic ArcSlash sprite (the old ray loop is deleted).
  // Dylan: damage from the weapon must be when an enemy TOUCHES the sprite.
  // We now skip the rays entirely and spawn a damaging ArcSlash whose sprite
  // IS the hitbox (handled in tickArcSlashHits, called from updateWeapons).
  void enemies; void onEnemyHit;

  const atkSpeedMult = 1 / (1 + player.bonusAttackSpeedPercent / 100);

  // Use the reload timer as swing cooldown
  if (weapon.isReloading) {
    weapon.reloadTimer -= dt;
    if (weapon.reloadTimer <= 0) {
      weapon.isReloading = false;
      weapon.isFiring = true;
    }
    return;
  }

  if (!weapon.isFiring) return;

  // ── Swing! ──
  weapon.isFiring = false;
  weapon.isReloading = true;
  weapon.reloadTimer = Math.max(MIN_WEAPON_COOLDOWN, weapon.reloadTime * atkSpeedMult);

  // BETA 19.44 — arc width grows with weapon level via spreadPerLevel. Cap at
  // arcMaxSpreadDeg (default 360). When the cap is reached, the swing flips to
  // continuous-orbit mode (sprite spins around the player without fading).
  const maxSpreadDeg = def.arcMaxSpreadDeg ?? 360;
  const effectiveSpreadDeg = Math.min(maxSpreadDeg, weapon.spreadAngle);
  const spreadRad = (effectiveSpreadDeg * Math.PI) / 180;
  const isOrbit = effectiveSpreadDeg >= maxSpreadDeg - 0.5;

  // ── Aim direction ─────────────────────────────────────────────────────
  // Drac's Fang spec: auto-aim at the nearest alive enemy at swing-start.
  // Spatula spec (Dylan + PSBOT cycle-30at-ADD-2): aim along live MOVEMENT
  // direction (player.vx/vy), NOT player.facingAngle (which is the aim/
  // mouse cursor). For KB+M kiting (mouse forward, running backward) the
  // swing follows the running direction so zombies chasing from behind
  // get hit. tickArcSlashHits re-applies this every frame when
  // liveDirection=true.
  let centerAngle = player.facingAngle;
  if (def.arcAutoAim) {
    let bestDist2 = Infinity;
    let bestX = 0, bestY = 0;
    let found = false;
    for (const e of enemies) {
      if (!e.alive) continue;
      const dx = e.x - player.x;
      const dy = e.y - player.y;
      const d2 = dx * dx + dy * dy;
      if (d2 < bestDist2) { bestDist2 = d2; bestX = dx; bestY = dy; found = true; }
    }
    if (found) centerAngle = Math.atan2(bestY, bestX);
  } else if (def.arcLiveDirection) {
    centerAngle = getMovementAimAngle(player);
  }

  // ── Pre-roll damage / crit / knockback (applied per-enemy on contact) ──
  // Crit is rolled PER HIT inside tickArcSlashHits (not stamped here) so that
  // a 360° spin can crit on enemy A and not on enemy B independently.
  const baseDmg = weapon.damage * (1 + player.bonusDamagePercent / 100);
  const totalCritChance = Math.min(1.0, weapon.critChance + player.bonusCritChance / 100);
  const totalCritDamage = weapon.critDamage + player.bonusCritDamage / 100;
  const knockback = weapon.knockback * (1 + player.bonusKnockback / 100);

  // BETA 13.98 — Drac's Gas Mask buff: bumps Drac's Fang lifesteal from 8% → 20%.
  const baseLifesteal = def.lifestealPct ?? 0;
  const lifestealPct = (def.id === 'wpn_dracs_fang' && player.hasDracsGasMask)
    ? Math.max(baseLifesteal, tuneOf('item_dracs_gas_mask', 'boostedLifestealPct', 0.20))
    : baseLifesteal;

  // ── Sprite reach + spawn the damaging slash ───────────────────────────
  const reach = def.projectile.speed * def.projectile.lifetime;
  const swingDuration = isOrbit
    ? Math.max(0.35, weapon.reloadTime * atkSpeedMult)  // orbit slash lasts ≥ one fire interval
    : 0.35;

  // BETA 19.44 — Prevent orbit-slash stacking. When the weapon hits its 360°
  // cap, fires keep happening at the normal reload cadence — but each fire
  // would spawn a NEW orbit slash, layering multiple sprites that all spin
  // independently. Instead, if this weapon already has an orbit slash alive,
  // just refresh its timer + clear hitEnemyIds so it starts a new hit cycle
  // (the visual spin is uninterrupted).
  if (isOrbit) {
    const existing = state.arcSlashes.find(s => s.mode === 'orbit' && s.weaponId === def.id);
    if (existing) {
      existing.timer = swingDuration;
      existing.maxTimer = swingDuration;
      existing.hitEnemyIds.clear();
      existing.orbitHitResetTimer = existing.orbitHitResetSec;
      existing.damage = baseDmg;
      existing.critChance = totalCritChance;
      existing.critDamage = totalCritDamage;
      existing.knockback = knockback;
      existing.sizeMult = 1 + (player.attackAreaBonus ?? 0) / 100;
      state.totalShotsFired += 1;
      weapon.totalShotsFired += 1;
      return;
    }
  }

  // BETA 19.50 — Dylan live: "why am i seeing two spatchulas when we are near
  // a wall?" Fan-mode swing flash lasts 0.35s, but spatula reload at high
  // attack-speed (level 15+ or with atk-speed buffs) goes below 0.35s — so
  // the previous swing's sprite is still visible when the next swing fires.
  // Result: 2 sprites on screen. Fix: drop any existing fan-slash for this
  // weapon before pushing the new one. Orbit-mode already had a refresh-
  // don't-stack guard above; fan-mode needed the same enforcement.
  for (let i = state.arcSlashes.length - 1; i >= 0; i--) {
    const s = state.arcSlashes[i];
    if (s.weaponId === def.id && s.mode === 'fan') {
      state.arcSlashes.splice(i, 1);
    }
  }

  state.arcSlashes.push({
    x: player.x,
    y: player.y,
    centerAngle,
    halfSpread: spreadRad / 2,
    radius: reach,
    timer: swingDuration,
    maxTimer: swingDuration,
    spriteUrl: def.spriteUrl,
    spriteSize: def.spriteSize,
    // BETA 15.54 — capture attackAreaBonus at swing-time so the sprite scales
    // proportional to the hitbox grow (matching weaponSystem.ts:1071 fix).
    // BETA 16.27 — PSBOT cycle-29 P0: was reading `bonusProjectileSize` (a
    // FLAT-add field for round projectile size), but the COMMENT and the
    // intent were always `attackAreaBonus`. T13 Not Viagra and Wizard
    // Surge etc. feed attackAreaBonus, not bonusProjectileSize, so the
    // spatula sprite + sector never scaled with size buffs. Now reads
    // the correct field.
    sizeMult: 1 + (player.attackAreaBonus ?? 0) / 100,
    // BETA 13.101 — Drac's Fang arc render variants
    noFlip: def.arcSpriteNoFlip,
    rampUp: def.arcRampUpSwing,
    noFade: def.arcNoFade,
    // BETA 19.44 — damaging-sprite fields
    weaponId: def.id,
    weaponInstance: weapon,
    damage: baseDmg,
    critChance: totalCritChance,
    critDamage: totalCritDamage,
    knockback,
    lifestealPct,
    hitEnemyIds: new Set<number>(),
    liveDirection: def.arcLiveDirection,
    aoeKnockback: def.arcAoEKnockback,
    mode: isOrbit ? 'orbit' : 'fan',
    orbitHitResetTimer: 0,
    orbitHitResetSec: Math.max(0.25, weapon.reloadTime * atkSpeedMult * 0.6),
  });

  // BETA 19.44 — one swing = one shot (the sprite). Was `totalCount` (rays).
  state.totalShotsFired += 1;
  weapon.totalShotsFired += 1;

  // BETA 22.69 — swing recoil (render-only)
  const _visSwing = resolveWeaponVisual(weapon.definitionId);
  if (_visSwing.recoilDistance > 0) {
    weapon.recoilTimer = _visSwing.recoilDuration;
  }
}

// BETA 22.73 — fireArcProjectile (the deprecated 19.44 ray-pierce arc path)
// DELETED. Arc weapons damage via the ArcSlash sprite hitbox (tickArcSlashHits).

/**
 * Find the nearest alive enemy to the player.
 */
// Fallback targeting range used only before the first frame sets up viewport/weapon data
const FALLBACK_LOCK_ON_RANGE = 300;

function findNearestEnemy(
  player: PlayerForWeapon,
  enemies: EnemyForWeapon[],
  maxRange: number = FALLBACK_LOCK_ON_RANGE,
  // BETA 23.05 — Dylan: "weapons still pointing at the character." A gun
  // whose own-side targets are dead would lock a FAR-side enemy and aim
  // straight across Greg. Targets whose firing line passes near this point
  // (the player) now carry a heavy score penalty — guns prefer their own
  // side whenever ANY alternative exists. Penalty, not exclusion: a gun
  // with only cross-side targets still fights.
  avoidThrough?: { x: number; y: number; r: number },
): EnemyForWeapon | null {
  let nearest: EnemyForWeapon | null = null;
  let bestScore = Infinity;   // lower = better (effective/priority-weighted distance)

  for (const enemy of enemies) {
    if (!enemy.alive) continue;
    const dx = enemy.x - player.x;
    const dy = enemy.y - player.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist > maxRange) continue;   // out of lock-on range — never target
    // BETA 22.08 — Dylan: "weapons should prioritize more difficult enemies if it has to
    // choose." Crowned (elite) + bosses get a distance DISCOUNT so auto-aim favors them over a
    // closer regular zombie. The discount is BOUNDED, so a regular right on top of you still
    // wins over a far-off boss — you're never left defenseless against the thing in your face.
    const _prio = enemy.variant === 'boss' ? 480 : enemy.variant === 'elite' ? 280 : 0;
    let score = dist - _prio;
    // BETA 23.05 — through-player penalty (see param note above)
    if (avoidThrough && dist > 1) {
      // distance from avoidThrough to the segment gun→enemy
      const t = Math.max(0, Math.min(1,
        ((avoidThrough.x - player.x) * dx + (avoidThrough.y - player.y) * dy) / (dist * dist)));
      const cx = player.x + dx * t, cy = player.y + dy * t;
      const dpx = avoidThrough.x - cx, dpy = avoidThrough.y - cy;
      if (dpx * dpx + dpy * dpy < avoidThrough.r * avoidThrough.r) score += 600;
    }
    if (score < bestScore) {
      bestScore = score;
      nearest = enemy;
    }
  }

  return nearest;
}

// ─── Per-weapon angle offset for bonus projectiles ─────────────────────────
// How many degrees each extra projectile is offset from the main shot.
// Extra shots fan out symmetrically: +1deg, -1deg, +2deg, -2deg, …
// Short-range / spread weapons use wider angles so they feel distinct.
const WEAPON_BONUS_SPREAD_DEG: Record<string, number> = {
  'peanut_rifle':         5,  // tight twin beams — barely separated
  // (fireball_wand removed BETA 22.74 — flameCone weapons never fire projectiles)
  'viking_axe':           8,  // twin axes thrown in tandem
  'tactical_basketball':  8,  // balls diverge slightly
  'rpk_machine_gun':     10,  // twin-barrel feel at full-auto
  'revolver':            14,  // fanned cylinder — each extra shot is clearly separate
  'spatula_slash':       28,  // wide hip angle — really feels like a second spatula
  'voogoo_rager_toss':   18,  // more chaotic blobs flying out
  'chad_aura':           22,  // arc gets wider with each extra beam
  'blunderbuss':         16,  // extra pellets fill the cone
  // BETA 15.74 — Ultra Beam tight alternating fan per PSBOT RUN spec section 5.
  // 1.5° feels visible without splitting too widely; tunable later.
  'wpn_ultra_beam':       1.5,
};
const DEFAULT_BONUS_SPREAD_DEG = 10;

/**
 * Returns the angle offsets (degrees) for bonus projectiles fired alongside the main shot.
 * Pattern alternates left/right from center: +1×, -1×, +2×, -2×, …
 */
function getBonusProjectileOffsets(weaponId: string, bonusCount: number): number[] {
  const spread = WEAPON_BONUS_SPREAD_DEG[weaponId] ?? DEFAULT_BONUS_SPREAD_DEG;
  const offsets: number[] = [];
  for (let i = 0; i < bonusCount; i++) {
    const side = i % 2 === 0 ? 1 : -1;
    const tier = Math.floor(i / 2) + 1;
    offsets.push(side * tier * spread);
  }
  return offsets;
}

/**
 * Fire a single projectile from a weapon toward a target enemy.
 * `origin` is the spawn position of the projectile (defaults to player center).
 * `extraAngleDeg` is an additional angle offset in degrees for bonus projectiles.
 */
function fireProjectile(
  state: WeaponSystemState,
  weapon: WeaponInstance,
  def: WeaponDefinition,
  player: PlayerForWeapon,
  target: EnemyForWeapon,
  origin?: { x: number; y: number },
  extraAngleDeg: number = 0,
  // BETA 15.26 — Cowboy Quick Draw: when true, this projectile gets +25%
  // damage AND a forced crit (skips the random roll). Set by caller for the
  // first projectile of every burst when player character is Cowboy.
  quickDrawShot: boolean = false,
): void {
  if (state.projectiles.length >= PROJECTILE_MAX) return;

  const spawnX = origin?.x ?? player.x;
  const spawnY = origin?.y ?? player.y;

  // Calculate angle to target from the spawn point
  const dx = target.x - spawnX;
  const dy = target.y - spawnY;
  const baseAngle = Math.atan2(dy, dx);

  // Apply spread (random offset within spread angle) + any bonus-projectile offset
  const spreadRad = (weapon.spreadAngle * Math.PI) / 180;
  const spreadOffset = (Math.random() - 0.5) * spreadRad;
  const angle = baseAngle + spreadOffset + (extraAngleDeg * Math.PI) / 180;

  // Calculate damage (with player bonuses) — Quick Draw bumps by +25%
  let baseDmg = weapon.damage * (1 + player.bonusDamagePercent / 100);
  if (quickDrawShot) { creditCowboyQuickDraw(baseDmg); baseDmg *= 1.25; } // BETA 20.88 — credit Cowboy signature

  // Roll crit — Quick Draw forces crit
  const totalCritChance = Math.min(1.0, weapon.critChance + player.bonusCritChance / 100);
  const isCrit = quickDrawShot ? true : (Math.random() < totalCritChance);
  const totalCritDamage = weapon.critDamage + player.bonusCritDamage / 100;
  const finalDamage = isCrit
    ? Math.round(baseDmg * (1 + totalCritDamage))
    : Math.round(baseDmg);

  // Projectile speed with bonus
  // BETA 21.41 — bonusProjectileSpeed is a PERCENT (T09 Caffeine "+3%"), but this added
  // it FLAT (+3 on a 1400 speed = 0.2% — invisible). Now multiplicative like its size/
  // knockback siblings just below, so "+3% projectile speed" actually means +3%.
  const speed = weapon.projectileSpeed * (1 + (player.bonusProjectileSpeed ?? 0) / 100);

  // BETA 15.51 — projectile size is PERCENT bonus, not flat-wu add (same fix as line 1071).
  // BETA 21.50 — fold in attackAreaBonus so "attack size" grows bullets too (see above).
  const size = weapon.projectileSize * (1 + ((player.bonusProjectileSize ?? 0) + (player.attackAreaBonus ?? 0)) / 100);

  // Knockback with % bonus from percs
  const knockback = weapon.knockback * (1 + player.bonusKnockback / 100);

  const projectile: Projectile = {
    id: state.nextProjectileId++,
    weaponId: weapon.definitionId,

    x: spawnX,
    y: spawnY,
    prevX: spawnX, // BETA 15.40 — anti-tunneling raycast baseline
    prevY: spawnY,
    velocityX: Math.cos(angle) * speed,
    velocityY: Math.sin(angle) * speed,
    speed,
    size: Math.max(2, size),

    damage: finalDamage,
    isCrit,
    knockback,
    knockbackAngle: angle,

    piercingLeft: weapon.projectilePiercing + (player.bonusProjectilePiercing | 0), // BETA 19.98 — N08 Not Ritalin pierce bonus now actually applies
    bouncesLeft: weapon.projectileBounces + (player.bonusProjectileBounces | 0), // BETA 13.82 — `+1 Projectile Bounce` shrine
    bounceRange: def.projectile.bounceRange,
    bounceCount: 0, // BETA 20.72 — render-only ricochet tally

    lifetime: def.projectile.lifetime,
    age: 0,

    trailEffect: def.projectile.trailEffect,
    trailColor: def.projectile.trailColor,
    trailPositions: [],
    shape: def.projectile.projectileShape,
    bodyColor: def.projectile.projectileColor,
    glowColor: def.projectile.projectileGlowColor,
    length: def.projectile.projectileLength,
    spriteUrl: def.projectile.spriteUrl,
    spinRate: def.projectile.spinRate ?? 0,
    spriteDisplaySize: def.projectile.spriteDisplaySize ?? 0,

    hitEnemyIds: new Set(),
    alive: true,

    // BETA 13.96 — Terminator boomerang init
    boomerangReturn: !!def.projectile.boomerangReturn,
    boomerangPhase: 'outbound',
    // BETA 14.05 — trap-projectile hit flag (Ugly Kittens). Defaults false;
    // gets flipped true when an enemy contacts the trap.
    wasHit: false,
  };

  state.projectiles.push(projectile);

  // Track crit
  if (isCrit) state.totalCrits++;

  // BETA 14.54/14.56 — Kitten placement tracker (generic spawn helper).
  // BETA 14.56: pass spawn coords for corner/center/scatter bucketing.
  if (weapon.definitionId === _UGLY_KITTENS_ID) _kittenEventTracker?.onPlace(spawnX, spawnY);

  // Muzzle flash at barrel tip (spawn is already at barrel tip)
  // BETA 22.69 — gated: weapons whose VfxSpec brings its OWN muzzle moment
  // (Beta B+) set muzzleFlashType 'none' to suppress the generic 3-ring.
  if (resolveWeaponVisual(weapon.definitionId).muzzleFlashType !== 'none') {
    state.muzzleFlashes.push({
      x: spawnX + Math.cos(angle) * 3,
      y: spawnY + Math.sin(angle) * 3,
      timer: MUZZLE_FLASH_DURATION,
      angle,
    });
  }

  // BETA 20.72 — per-weapon FIRE signature (VFX identity law: every weapon
  // owns its muzzle moment, the shared 3-layer flash alone made them twins).
  // Routed through the engine-registered particle bridge; no-op until wired.
  // Burst-gated weapons key on currentAmmo === projectileCount (ammo is
  // pre-decrement here — decrement happens after fireProjectile returns) and
  // extraAngleDeg === 0 so bonus-projectile fan shots don't re-trigger.
  if (_weaponParticleSpawner) {
    // BETA 22.73 — per-weapon fire signatures moved to weaponVfx.ts (one
    // module owns every weapon-id VFX decision). Same particles, same gates.
    const _isFirstShot = weapon.currentAmmo === weapon.projectileCount && extraAngleDeg === 0;
    spawnFireSignature(weapon.definitionId, _weaponParticleSpawner, spawnX, spawnY, angle, _isFirstShot);
  }
}

// ═══════════════════════════════════════════════════════════════
//  PROJECTILE UPDATE & COLLISION
// ═══════════════════════════════════════════════════════════════

function updateProjectiles(
  state: WeaponSystemState,
  dt: number,
  enemies: EnemyForWeapon[],
  onEnemyHit: OnEnemyHitCallback,
  player: PlayerForWeapon,
  onApplyStatus?: OnApplyStatusCallback,
): void {
  for (let i = state.projectiles.length - 1; i >= 0; i--) {
    const proj = state.projectiles[i];
    if (!proj.alive) {
      // BETA 14.56 — Kitten projectile death lifetime hook. proj.age is the
      // time-alive in seconds; convert to ms for the lifetimeAvgMs telemetry.
      if (proj.weaponId === _UGLY_KITTENS_ID) {
        _kittenEventTracker?.onProjectileDeath(proj.age * 1000);
      }
      state.projectiles.splice(i, 1);
      continue;
    }

    // ── Save trail position ──
    if (proj.trailEffect) {
      proj.trailPositions.unshift({ x: proj.x, y: proj.y });
      if (proj.trailPositions.length > TRAIL_SEGMENTS) {
        proj.trailPositions.pop();
      }
    }

    // ── BETA 13.96 — Boomerang return (Terminator) ──
    // At halfLifetime, switch to 'returning' mode: home toward player, allow re-hits.
    // Original lifetime is preserved on the def; we use proj.age vs def.projectile.lifetime
    // (proj.lifetime decrements; halfway point = age >= original/2).
    if (proj.boomerangReturn && proj.boomerangPhase === 'outbound') {
      const _bDef = getWeaponDef(proj.weaponId);
      const fullLifetime = _bDef?.projectile.lifetime ?? 1.0;
      if (proj.age >= fullLifetime * 0.5) {
        proj.boomerangPhase = 'returning';
        proj.hitEnemyIds.clear(); // each enemy can be hit AGAIN on the return path
        // BETA 20.72 — FLIP POP: the turnaround used to happen silently.
        // Color-keyed per boomerang so the two never read alike mid-air:
        // Terminator = white ring-pop + cyan tech sparks; Drac's Fang =
        // blood ring + tumbling red shards.
        if (proj.weaponId === 'wpn_the_terminator') {
          _weaponParticleSpawner?.(proj.x, proj.y, '#FFFFFF', 2, { shape: 'ring', speedMin: 0, speedMax: 0, sizeMin: 8, sizeMax: 14, lifeMin: 0.3, lifeMax: 0.4 });
          _weaponParticleSpawner?.(proj.x, proj.y, '#00E5FF', 4, { shape: 'spark', speedMin: 90, speedMax: 200, lifeMin: 0.15, lifeMax: 0.3, sizeMin: 1.5, sizeMax: 3, stretch: 4 });
        } else if (proj.weaponId === 'wpn_dracs_fang') {
          _weaponParticleSpawner?.(proj.x, proj.y, '#CC1122', 1, { shape: 'ring', speedMin: 0, speedMax: 0, sizeMin: 7, sizeMax: 11, lifeMin: 0.25, lifeMax: 0.35 });
          _weaponParticleSpawner?.(proj.x, proj.y, '#FF3344', 4, { shape: 'shard', spin: 9, spinRandomSign: true, speedMin: 60, speedMax: 150, lifeMin: 0.2, lifeMax: 0.4, sizeMin: 2, sizeMax: 3.5 });
        }
      }
    }
    // While returning, homing-steer velocity toward player.
    if (proj.boomerangReturn && proj.boomerangPhase === 'returning') {
      const dxh = player.x - proj.x;
      const dyh = player.y - proj.y;
      const dh = Math.sqrt(dxh * dxh + dyh * dyh);
      if (dh > 1) {
        const targetVx = (dxh / dh) * proj.speed;
        const targetVy = (dyh / dh) * proj.speed;
        // Smooth turn (lerp velocity toward target)
        proj.velocityX = proj.velocityX * 0.85 + targetVx * 0.15;
        proj.velocityY = proj.velocityY * 0.85 + targetVy * 0.15;
      }
      // Despawn when it reaches player (within 30wu)
      if (dh < 30) {
        proj.alive = false;
        continue;
      }
    }

    // ── BETA 20.63 — Ugly Kittens: chasing pets (Dylan spec) ──
    // Cats RUN at the player's live move speed toward the nearest living enemy.
    // hypot(vx,vy) = the player's current speed (incl. move-speed buffs); base
    // 190 when the player stands still so the cat keeps hunting. Idle sprite
    // while running — the claw frames only flash ON HIT (attackFlashUntilAge).
    // Periodic hitEnemyIds clear lets one pet keep clawing a tanky target.
    if (proj.weaponId === _UGLY_KITTENS_ID) {
      // BETA 20.72 — Dylan-approved (audit: kittens at 7.9× median weapon DPS,
      // parity law caps at ~2×). Re-claw interval 0.5s → 1.0s: each cat claws
      // a given enemy once per second instead of twice. Cat COUNT untouched
      // (NO COUNT CAPS law) — only the chew rate halves.
      // BETA 21.70 — claw BUDGET. Lazily seed from this cat's spawn piercingLeft
      // (= base pierce + any global pierce buffs the player has) + 1, so a base
      // cat bites exactly 1 enemy and pierce upgrades raise it. Refilled on each
      // 1s re-claw clear below so the budget renews every cycle.
      if (proj.kittenClawBudget == null) {
        proj.kittenClawBudget = Math.max(1, (proj.piercingLeft | 0) + 1);
        proj.kittenClawLeft = proj.kittenClawBudget;
      }
      if (proj.nextRehitAge == null) proj.nextRehitAge = proj.age + 1.0;
      if (proj.age >= proj.nextRehitAge) { proj.hitEnemyIds.clear(); proj.kittenClawLeft = proj.kittenClawBudget; proj.nextRehitAge = proj.age + 1.0; }
      // Retarget at 8Hz, not per-frame (see catRetargetAge field comment).
      if (proj.age >= (proj.catRetargetAge ?? 0)) {
        proj.catRetargetAge = proj.age + 0.12;
        const catSpeed = Math.max(190, Math.hypot(player.vx, player.vy));
        let nx = 0, ny = 0, nd = Infinity;
        for (let ci = 0; ci < enemies.length; ci++) {
          const e = enemies[ci];
          if (!e.alive) continue;
          const ddx = e.x - proj.x, ddy = e.y - proj.y;
          const dd = ddx * ddx + ddy * ddy;
          if (dd < nd) { nd = dd; nx = ddx; ny = ddy; }
        }
        if (nd < Infinity && nd > 1) {
          const d = Math.sqrt(nd);
          proj.velocityX = (nx / d) * catSpeed;
          proj.velocityY = (ny / d) * catSpeed;
        }
      }
    }

    // ── Move projectile ──
    // BETA 15.40 — record prev position for anti-tunneling wall raycast.
    proj.prevX = proj.x;
    proj.prevY = proj.y;
    proj.x += proj.velocityX * dt;
    proj.y += proj.velocityY * dt;
    proj.age += dt;
    proj.lifetime -= dt;

    // ── Despawn check ──
    // BETA 14.08 — Boomerang projectiles (Terminator) are EXEMPT from
    // lifetime expiry while in the 'returning' phase. User: "should
    // boomerang and not be done attacking until it gets back to main
    // character/bot". The proximity-based despawn (within 30wu of player)
    // above already handles cleanup once it reaches the player. If the
    // player kites away forever, the homing velocity will still close the
    // distance because targetVx/Vy are recomputed each frame.
    if (proj.lifetime <= 0 && !(proj.boomerangReturn && proj.boomerangPhase === 'returning')) {
      // BETA 22.76 — a lobbed/explosive shot that MISSES and times out in the
      // open must STILL detonate + leave its ground effect where it lands
      // (22.75 audit: Beer Grenade / The Thumper produced nothing on a whiff —
      // the AoE/blast only fired on an enemy kill). Both helpers self-gate on
      // the weapon having aoe/explode radii, so this is a no-op for every other
      // projectile. No excludeEnemyId — nothing was struck on a timeout.
      spawnImpactAoe(state, proj.weaponId, proj.x, proj.y);
      spawnImpactExplosion(proj.weaponId, proj.x, proj.y, proj.damage, proj.isCrit, enemies, onEnemyHit);
      proj.alive = false;
      continue;
    }

    // ── Collision with enemies ──
    // BETA 19.90 — FPS optimization. Use squared-distance instead of
    // Math.sqrt for the per-pair hit-check. Sqrt is ~10× slower than
    // multiply/add on modern JS engines. At peak load (80 projectiles ×
    // 200 enemies = 16K pairs per frame), eliminating sqrt for the
    // ~99% of pairs that DON'T hit cuts collision-loop CPU by 8-9×.
    // Only computes sqrt when actually needed (e.g. for knockback angle
    // — but Math.atan2 doesn't need sqrt). Distance variable only used
    // inside the hit branch, so we can defer the sqrt entirely.
    // BETA 20.72 — CONFIRMED BUG FIX (123-run audit): fast projectiles
    // tunneled THROUGH enemies. Ultra Beam (speed 8000) moves 133wu+ per
    // 60fps frame — with dt clamped at 0.1s under load, up to 800wu — while
    // a zombie's hit window is only ~64wu wide. The point-in-circle check
    // below sampled once per frame and missed ~79% of passes (observed hit
    // rate 20.9% vs the 70% Charter law). The 15.40 prevX raycast only
    // covered WALLS. Fix: when this frame's step exceeds the hit window,
    // test the closest point on the prev→current segment instead. Slow
    // projectiles (the 99% case) skip the extra math entirely.
    const _stepX = proj.x - proj.prevX;
    const _stepY = proj.y - proj.prevY;
    const _stepLenSq = _stepX * _stepX + _stepY * _stepY;
    for (const enemy of enemies) {
      if (!enemy.alive) continue;
      if (proj.hitEnemyIds.has(enemy.id)) continue; // Already hit this enemy

      const dx = enemy.x - proj.x;
      const dy = enemy.y - proj.y;
      let distSq = dx * dx + dy * dy;
      const hitDist = enemy.hitboxRadius + proj.size;
      const hitDistSq = hitDist * hitDist;

      if (_stepLenSq > hitDistSq) {
        // Swept segment-circle test (only for fast movers, per-pair gate).
        const ex = enemy.x - proj.prevX;
        const ey = enemy.y - proj.prevY;
        let t = (ex * _stepX + ey * _stepY) / _stepLenSq;
        if (t < 0) t = 0; else if (t > 1) t = 1;
        const cx = proj.prevX + _stepX * t - enemy.x;
        const cy = proj.prevY + _stepY * t - enemy.y;
        const sweptSq = cx * cx + cy * cy;
        if (sweptSq < distSq) distSq = sweptSq;
      }

      if (distSq < hitDistSq) {
        // BETA 21.70 — Ugly Kittens: this cat has already spent its claw budget
        // for the current re-claw cycle (base = 1 enemy; pierce buffs add more).
        // It still chases + collides, but doesn't bite again until the 1s refill.
        // Keeps one cat single-target at base — the fix for "kitten attacks many
        // enemies early game".
        if (proj.weaponId === _UGLY_KITTENS_ID && (proj.kittenClawLeft ?? 1) <= 0) continue;
        const dist = Math.sqrt(distSq); // only sqrt when actually hit
        void dist; // (Math.atan2 below doesn't need it; left for readability)
        // HIT!
        const knockbackAngle = Math.atan2(dy, dx);

        onEnemyHit(
          enemy.id,
          proj.damage,
          proj.knockback,
          knockbackAngle,
          proj.isCrit,
        );
        // BETA 14.54 — weaponEnemyMatrix attribution. This is THE projectile
        // collision path used by Drac's Fang / Squirter / Ex-Girlfriend /
        // Ultra Beam / Frozen Darts / Fireball Wand / Shitty Cannon /
        // Terminator / Peanut Rifle / Revolver / VooGoo Rager / Poop Sling /
        // Six Shooter / Basketball — basically every projectile-based weapon.
        // Previously only orbital (line 661), piercing-loop (line 765), aura
        // (line 900), and ground-tick (line 1492) sites had the tracker, so
        // most weapons never appeared in the matrix.
        _weaponHitTracker?.(proj.weaponId, enemy.type, proj.damage, proj.isCrit);
        // BETA 14.54/14.56 — Kitten attack + hit tracker. When a trap kitten
        // contacts an enemy, that's an "attack fired" event for the trap.
        // BETA 14.56: enemy.alive reflects post-onEnemyHit state (the
        // EnemyForWeapon mirror is updated by enemyAdapter.damageEnemy).
        // Pass wasKill = !alive so weaponLab can increment kittenKillsTotal.
        if (proj.weaponId === _UGLY_KITTENS_ID) {
          _kittenEventTracker?.onAttack();
          _kittenEventTracker?.onHit(proj.damage, !enemy.alive);
          // BETA 15.74 — Dylan-provided cat fighting sound when kitten attacks.
          // Throttled inside playKittensAttack (0.15s) so multiple kittens
          // hitting simultaneously don't stack-overlap.
          playKittensAttack(enemy.x, enemy.y);
        }
        // BETA 19.67 — Per Dylan: "remove the second hit audio for the
        // slingshot. no need for the hit sound." playPoopSlingFire (the same
        // swoosh sample) already fires at shot-spawn time; the duplicate
        // on-hit swoosh felt redundant. Removed the hit-side call. Fire
        // audio stays; gameSfx.playPoopSlingHit function kept on disk in
        // case Dylan reverses.

        state.totalDamageDealt += proj.damage;
        // Per-weapon telemetry: find the weapon that fired this projectile
        const _srcWeapon = state.weapons.find(w => w.definitionId === proj.weaponId);
        if (_srcWeapon) {
          _srcWeapon.totalDamageDealt += proj.damage;
          // BETA 16.27 — PSBOT cycle-30b Bug 4 FIX. Crit was firing per
          // enemy-contact instead of per-shot. For piercing/arc weapons
          // (Drac's Fang, Spatula, etc.) one shot can hit 3+ enemies →
          // totalCrits counted 3+, while totalShotsHit gated to 1 (per
          // BETA 13.43 below). Result: PSBOT cycle-30b saw dracs_fang
          // 38 hits / 81 crits = 213% crit rate (impossible). Now
          // matches the hit-counter gate so crit-rate-as-percent-of-hits
          // is meaningful again. Same gate as totalShotsHit below.
          if (proj.isCrit && !proj.everHit) _srcWeapon.totalCrits++;
          // BETA 13.43/20.89: count this as a "hit" only on the projectile's FIRST
          // EVER contact. Piercing shots that hit 3 enemies still count as 1 hit;
          // kitten re-claws (which clear hitEnemyIds every 1s) no longer re-count.
          if (!proj.everHit) _srcWeapon.totalShotsHit++;
          proj.everHit = true;
        }

        // BETA 13.95: apply status effects + lifesteal based on weapon definition
        const _srcDef = getWeaponDef(proj.weaponId);
        if (_srcDef) {
          if (_srcDef.applyFreezeOnHit && onApplyStatus) {
            onApplyStatus(enemy.id, 'freeze', _srcDef.applyFreezeOnHit);
          }
          if (_srcDef.applySlowOnHit && onApplyStatus) {
            onApplyStatus(enemy.id, 'slow', _srcDef.applySlowOnHit, _srcDef.slowOnHitMultiplier ?? 0.5);
          }
          if (_srcDef.applyWetOnHit && onApplyStatus) {
            onApplyStatus(enemy.id, 'wet', _srcDef.applyWetOnHit);
          }
          if (_srcDef.lifestealPct && _srcDef.lifestealPct > 0) {
            // BETA 13.98 — Drac's Gas Mask item synergy: lifesteal 8% → 20%
            // (2.5× multiplier) when both Drac's Fang weapon + the item are equipped.
            const synergyMult = (player.hasDracsGasMask && _srcDef.id === 'wpn_dracs_fang') ? 2.5 : 1.0;
            const baseLifesteal = proj.damage * _srcDef.lifestealPct;
            // BETA 20.77 — cap parity with the player-stat path (8 HP/hit;
            // the Drac's-Gas-Mask synergy keeps its 2.5× edge inside a
            // proportionally higher cap).
            state.pendingLifestealHeal += Math.min(8 * synergyMult, baseLifesteal * synergyMult);
            // BETA 14.58 — Synergy triggerCount + valueDelta tracker. valueDelta
            // is the EXTRA HP gained from the synergy beyond the base lifesteal.
            if (synergyMult > 1.0) {
              _synergyTriggerCallback?.('dracs_fang_gas_mask', baseLifesteal * (synergyMult - 1.0));
            }
          }
        }

        // BETA 20.72 — Frozen Darts: the 2s freeze finally LANDS visibly —
        // expanding cyan ice ring + 3 spinning frost shards at the struck
        // enemy. (14.08 only removed the red crit halo; the freeze itself
        // never had a tell in this path.)
        if (proj.weaponId === 'wpn_frozen_darts') {
          _weaponParticleSpawner?.(enemy.x, enemy.y, '#7FDBFF', 1, { shape: 'ring', speedMin: 0, speedMax: 0, sizeMin: 6, sizeMax: 9, lifeMin: 0.25, lifeMax: 0.3 });
          _weaponParticleSpawner?.(enemy.x, enemy.y, '#CFEFFF', 3, { shape: 'shard', spin: 8, spinRandomSign: true, speedMin: 40, speedMax: 120, lifeMin: 0.25, lifeMax: 0.45, sizeMin: 2, sizeMax: 3.5 });
        }
        // BETA 20.72 — beam impact spray (engine-registered hook): Ultra Beam
        // finally shows WHERE it connects — a forward cone of purple sparks
        // continuing the beam's travel direction at the struck enemy. Beam-
        // class only; regular projectiles already read via damage numbers +
        // knockback.
        if (proj.weaponId === 'wpn_ultra_beam') {
          _beamImpactVfx?.(enemy.x, enemy.y, Math.atan2(proj.velocityY, proj.velocityX), '#bb55ff');
        }
        // BETA 22.84 — RPK impact burst: a chunky forward spray of hot orange
        // sparks + a wisp of smoke where the heavy tracer connects (was on the
        // generic hit only). Machine-gun weight read on every hit.
        if (proj.weaponId === 'rpk_machine_gun' && _weaponParticleSpawner) {
          const _ia = Math.atan2(proj.velocityY, proj.velocityX);
          _weaponParticleSpawner(enemy.x, enemy.y, '#FFB24A', 4, { shape: 'spark', dir: _ia, spread: 0.9, speedMin: 80, speedMax: 220, lifeMin: 0.1, lifeMax: 0.22, sizeMin: 1.5, sizeMax: 3, stretch: 3 });
          _weaponParticleSpawner(enemy.x, enemy.y, '#8a8a8a', 2, { shape: 'mote', speedMin: 15, speedMax: 60, lifeMin: 0.2, lifeMax: 0.4, sizeMin: 2, sizeMax: 3.5 });
        }

        // Spawn damage number
        spawnDamageNumber(state, {
          x: enemy.x + (Math.random() - 0.5) * 20,
          y: enemy.y - 20,
          value: proj.damage,
          isCrit: proj.isCrit,
          timer: proj.isCrit ? 0.8 : 0.5,
          velocityY: -60,
        });

        // Track this enemy as hit
        proj.hitEnemyIds.add(enemy.id);

        // BETA 16.69 — Trap-mode kittens persist after each hit
        // (piercing 999) so they keep luring + clawing more enemies.
        // Flip wasHit so the projectileAnimFrames claw cycle plays
        // every time an enemy contacts. Without this the cats look
        // permanently idle since piercing > 0 skips the trap-death
        // branch below where wasHit was previously flipped.
        if (proj.speed === 0) {
          proj.wasHit = true;
        }

        // BETA 20.63 — chasing-pet kittens: flash the claw frames on EACH
        // contact, then revert to the idle cat while running. 0.36s = one
        // full 3-frame cycle at 0.12s/frame, counted from this hit.
        if (proj.weaponId === _UGLY_KITTENS_ID) {
          proj.attackFlashUntilAge = proj.age + 0.36;
          // BETA 21.70 — spend one claw from this cycle's budget, then STOP for
          // this collision pass. The cat never falls through to the bounce / pierce /
          // death machinery below.
          if (proj.kittenClawLeft != null) proj.kittenClawLeft--;
          // BETA 22.04 — Dylan: a cat is a HUNTER, not a lawnmower. It exists to score a
          // KILL, then it's gone. !enemy.alive here means THIS claw just killed it (the
          // collision required it alive at the top of the loop, and nothing else touches
          // it in between). Count the kill; once the cat has killed its budget (base 1;
          // pierce buffs raise it), vanish it — ending the "running wild" where one cat
          // clawed the whole crowd for its full lifetime. (Lifetime cut to 6s too.)
          if (!enemy.alive) {
            proj.kittenKills = (proj.kittenKills ?? 0) + 1;
            if (proj.kittenKills >= (proj.kittenClawBudget ?? 1)) {
              proj.alive = false;
              break; // budget spent — stop clawing more enemies this frame; the cat poofs
            }
          }
          continue;
        }

        // ── Handle bouncing FIRST ──
        // BETA 20.42 — bounce is now checked BEFORE pierce so a weapon's
        // SIGNATURE bounce (Viking Axe, basketball) isn't silently cancelled by
        // a pierce bonus. Dylan: "axe hits one and goes straight past the nearby
        // enemies — on some throws." Root cause: any pierce bonus (N08 Not Ritalin,
        // 1000 LB Momentum's pierce-all, pierce shrines) made piercingLeft > 0, and
        // the pierce branch ran first + `continue`d, skipping the bounce. Now a
        // bounce weapon bounces to the next nearby enemy and only falls through to
        // pierce / expire when there's nothing in range to bounce to.
        if (proj.bouncesLeft > 0) {
          const bounceTarget = findBounceTarget(
            proj, enemy.id, enemies, proj.bounceRange,
          );

          if (bounceTarget) {
            proj.bouncesLeft--;
            proj.bounceCount++;         // BETA 20.72 — render-only ricochet tally
            proj.squashUntilAge = proj.age + 0.14; // BETA 22.86 — squash-pulse on ricochet (dodgeball THWAP)
            state.bouncesPerformed++;   // BETA 20.43 — telemetry: a bounce occurred
            // BETA 15.26 — King's Court (LeBron + basketball): compounding
            // 1.25× damage on every bounce. No-op for other characters or
            // weapons. Hit#1 = base, hit#2 = 1.25×, hit#6 = ~3.05×.
            proj.damage = onProjectileBounce(proj.weaponId, proj.damage);
            // BETA 20.69 — bounce POP: impact flash at every ricochet so
            // basketball passes + axe bounces finally FEEL like hits.
            // BETA 20.72 — Tactical Basketball: generic pop replaced with a
            // GOLD 8-point star-burst; every 3rd bounce fires bigger/faster
            // sparks + a white ring so the 1.25×-per-bounce ramp is VISIBLE.
            // Axe/Fang/others keep the 20.69 flash (axe = already-good list).
            // Falls back to the generic flash if the bridge isn't wired yet.
            if (proj.weaponId === 'tactical_basketball' && _weaponParticleSpawner) {
              const _big = proj.bounceCount % 3 === 0;
              const _bSpd = _big ? 340 : 170;
              for (let _bi = 0; _bi < 8; _bi++) {
                _weaponParticleSpawner(proj.x, proj.y, '#FFD700', 1, { shape: 'spark', dir: _bi * Math.PI / 4, spread: 0, speedMin: _bSpd, speedMax: _bSpd + 40, lifeMin: _big ? 0.28 : 0.2, lifeMax: _big ? 0.38 : 0.3, sizeMin: _big ? 2.5 : 1.5, sizeMax: _big ? 4 : 2.5, stretch: 4 });
              }
              if (_big) {
                _weaponParticleSpawner(proj.x, proj.y, '#FFFFFF', 1, { shape: 'ring', speedMin: 0, speedMax: 0, sizeMin: 10, sizeMax: 14, lifeMin: 0.3, lifeMax: 0.4 });
              }
            } else {
              state.muzzleFlashes.push({ x: proj.x, y: proj.y, timer: MUZZLE_FLASH_DURATION, angle: 0 });
            }
            // Redirect projectile toward bounce target
            const bx = bounceTarget.x - proj.x;
            const by = bounceTarget.y - proj.y;
            const bDist = Math.sqrt(bx * bx + by * by);
            if (bDist > 0) {
              proj.velocityX = (bx / bDist) * proj.speed;
              proj.velocityY = (by / bDist) * proj.speed;
            }
            continue;
          } else {
            // BETA 20.43 — had bounces left but NO enemy in bounceRange to chain
            // to. This is the "should have bounced but didn't" signal Dylan wants
            // tracked. (Usually: tight cluster where the projectile already hit /
            // is past every nearby enemy, or the rest are >bounceRange away.)
            state.bounceMissedNoTarget++;
          }
        }

        // ── Handle piercing ──
        if (proj.piercingLeft > 0) {
          proj.piercingLeft--;
          // Projectile continues through enemy
          continue;
        }

        // No piercing or bouncing left — projectile dies
        // BETA 13.97 — spawn AoE ground effect at impact point if configured
        spawnImpactAoe(state, proj.weaponId, proj.x, proj.y);
        // BETA 22.56 — instant blast (Beer Grenade) on enemy impact; the
        // struck enemy is excluded (it already took the projectile hit).
        spawnImpactExplosion(proj.weaponId, proj.x, proj.y, proj.damage, proj.isCrit, enemies, onEnemyHit, enemy.id);
        // BETA 14.05 — trap-mode projectiles (speed === 0, e.g. Ugly Kittens
        // placed on the ground) flip wasHit instead of immediately dying so
        // the projectileAnimFrames cycle plays for ~0.4s. The trap STILL dies
        // shortly after — lifetime is clamped to 0.4s so the animation has
        // time to play, then natural lifetime expiry handles cleanup.
        if (proj.speed === 0 && !proj.wasHit) {
          proj.wasHit = true;
          // Clamp remaining lifetime so the trap fades after the anim plays
          proj.lifetime = Math.min(proj.lifetime, 0.4);
        } else {
          proj.alive = false;
        }
        break;
      }
    }
  }

  // BETA 13.97 — tick down ground effects, apply slow + DoT to enemies in radius
  updateGroundEffects(state, dt, enemies, onEnemyHit, onApplyStatus);
}

/** BETA 13.97 — tick down ground effects (smoke clouds, slow puddles).
 *  Applies slow to enemies in radius (refreshed each frame inside).
 *  Applies DoT (per-enemy 0.5s cooldown so we don't hammer damageEnemy). */
function updateGroundEffects(
  state: WeaponSystemState,
  dt: number,
  enemies: EnemyForWeapon[],
  onEnemyHit: OnEnemyHitCallback,
  onApplyStatus?: OnApplyStatusCallback,
): void {
  for (let i = state.groundEffects.length - 1; i >= 0; i--) {
    const ge = state.groundEffects[i];
    ge.lifetime -= dt;
    if (ge.lifetime <= 0) {
      state.groundEffects.splice(i, 1);
      continue;
    }
    // Decrement per-enemy DoT cooldowns (forEach pattern — avoid downlevelIteration)
    const _toDelete: number[] = [];
    ge.enemyTickCooldowns.forEach((cd, eid) => {
      const newCd = cd - dt;
      if (newCd <= 0) _toDelete.push(eid);
      else ge.enemyTickCooldowns.set(eid, newCd);
    });
    for (const eid of _toDelete) ge.enemyTickCooldowns.delete(eid);

    const radSq = ge.radius * ge.radius;
    for (const enemy of enemies) {
      if (!enemy.alive) continue;
      const dx = enemy.x - ge.x;
      const dy = enemy.y - ge.y;
      const distSq = dx * dx + dy * dy;
      const reach = ge.radius + enemy.hitboxRadius;
      if (distSq > reach * reach) continue;

      // BETA 13.102 — first enemy contact: flip wasSteppedOn (sprite swaps to
      // stage-2 in renderGroundEffects) and fire the one-shot onFirstStep
      // callback (Whoopie Cushion stun, Asmon's Banana knockback).
      if (!ge.wasSteppedOn) {
        ge.wasSteppedOn = true;
        if (ge.onFirstStep) ge.onFirstStep(ge, enemy.id);
        // BETA 14.55 — floorEffectTracker first-contact hook
        _floorEffectTracker?.onFirstContact(ge.weaponId);
      }

      // Apply slow (refresh status each frame the enemy is inside)
      if (ge.slowMult < 1.0 && onApplyStatus) {
        onApplyStatus(enemy.id, 'slow', ge.slowDurationOnTick, ge.slowMult);
      }

      // Apply DoT — per-enemy 0.5s tick cooldown
      if (ge.dotPerSec > 0) {
        const cd = ge.enemyTickCooldowns.get(enemy.id) ?? 0;
        if (cd <= 0) {
          const tickDmg = Math.round(ge.dotPerSec * 0.5);
          onEnemyHit(enemy.id, tickDmg, 0, 0, false);
          state.totalDamageDealt += tickDmg;
          ge.enemyTickCooldowns.set(enemy.id, 0.5);
          // BETA 14.55 — floorEffectTracker tick hook (per-DoT-tick dmg)
          _floorEffectTracker?.onEnemyTick(ge.weaponId, tickDmg);
        }
      }
      void radSq; // already used
    }
  }
}

/**
 * Find the nearest enemy to bounce to (excluding already-hit enemies).
 */
function findBounceTarget(
  proj: Projectile,
  lastHitId: number,
  enemies: EnemyForWeapon[],
  maxRange: number,
): EnemyForWeapon | null {
  let nearest: EnemyForWeapon | null = null;
  let nearestDist = maxRange;

  for (const enemy of enemies) {
    if (!enemy.alive) continue;
    if (proj.hitEnemyIds.has(enemy.id)) continue;
    if (enemy.id === lastHitId) continue;

    const dx = enemy.x - proj.x;
    const dy = enemy.y - proj.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist < nearestDist) {
      nearestDist = dist;
      nearest = enemy;
    }
  }

  return nearest;
}

// ═══════════════════════════════════════════════════════════════
//  DAMAGE NUMBERS
// ═══════════════════════════════════════════════════════════════

function updateDamageNumbers(state: WeaponSystemState, dt: number): void {
  // BETA 16.94 — pool-aware update. Iterate forward; skip inactive
  // slots; mark expired slots inactive (no splice = no array churn).
  const arr = state.damageNumbers;
  for (let i = 0; i < arr.length; i++) {
    const dn = arr[i];
    if (!dn.active) continue;
    dn.timer -= dt;
    dn.y += dn.velocityY * dt;
    dn.velocityY *= 0.95;
    if (dn.timer <= 0) {
      dn.active = false;
    }
  }
}

// ═══════════════════════════════════════════════════════════════
//  RENDERING
// ═══════════════════════════════════════════════════════════════

/**
 * Render all projectiles, damage numbers, and muzzle flashes.
 * Called from engine.ts render() after drawing enemies.
 *
 * If `player` is provided, the equipped weapons are also drawn orbiting the
 * player and rotated to point at their current target.
 */
export function renderWeapons(
  state: WeaponSystemState,
  ctx: CanvasRenderingContext2D,
  camera: CameraForWeapon,
  player?: {
    x: number;
    y: number;
    // BETA 16.24 — optional projectile-count + attack-speed for Karen
    // Drones multi-drone rendering. Defaults to 0 if absent (legacy
    // single-drone behavior).
    bonusProjectileCount?: number;
    bonusAttackSpeedPercent?: number;
  },
  // BETA 23.01 — 'skip' = orbit sprites are drawn separately inside the
  // player render group (weapon depth system); this call then only draws
  // projectiles/trails/flashes ABOVE the world (Dylan rule 2).
  orbitSprites: 'all' | 'skip' = 'all',
): void {
  // Arc slashes rendered at end (after renderArcSlashesOverlay call from engine)

  // BETA 13.99 — Ground effects (smoke clouds, slow puddles) are rendered
  // by engine.ts BEFORE enemies, so they appear under enemy + player sprites.
  // (Was previously rendered here, which put them ON TOP of enemies.)

  // ── Render orbit weapons under projectiles (so muzzle flash sits on top) ──
  if (player && orbitSprites !== 'skip') {
    renderOrbitWeapons(state, ctx, camera, player);
    // Aura rendered separately in world-space before the player sprite (renderAuraInWorldSpace)
  }

  // ── Render projectile trails ──
  for (const proj of state.projectiles) {
    if (!proj.alive || !proj.trailEffect) continue;

    const screenX = (proj.x - camera.x) * camera.zoom + camera.viewportWidth / 2;
    const screenY = (proj.y - camera.y) * camera.zoom + camera.viewportHeight / 2;

    // Frustum culling
    if (screenX < -50 || screenX > camera.viewportWidth + 50 ||
        screenY < -50 || screenY > camera.viewportHeight + 50) continue;

    // BETA 20.72 — STREAK TRAILS (per-weapon trail identity). These weapons
    // skip the 20.69 ghost hijack below and render tapered velocity STREAKS
    // in their own trailColor instead: boomerangs on the RETURN leg only
    // (Terminator cyan / Drac's Fang blood-red — outbound keeps spinning
    // sprite ghosts, so out-vs-back reads at a glance), Frozen Darts (thin
    // ice line, not the "way too circular" 13.101 dots), and the Revolver
    // (clean gold tracer with a white-hot core line).
    // BETA 22.73 — trail identity now lives in WEAPON_VFX.trailKind (was
    // hardcoded weapon-id lists here). Boomerang return-streak stays
    // phase-driven; everything else is registry data.
    const _trailKind = getWeaponVfx(proj.weaponId)?.trailKind;
    const _isReturnStreak = proj.boomerangReturn && proj.boomerangPhase === 'returning' && proj.trailColor !== '';
    const _isStreakTrail = _isReturnStreak || _trailKind === 'streak' || _trailKind === 'streakCore';
    if (_isStreakTrail && proj.trailPositions.length > 0) {
      const _isRevolverStreak = _trailKind === 'streakCore';
      ctx.save();
      ctx.lineCap = 'round';
      let _spx = screenX, _spy = screenY;
      for (let t = 0; t < proj.trailPositions.length; t++) {
        const tp = proj.trailPositions[t];
        const tx = (tp.x - camera.x) * camera.zoom + camera.viewportWidth / 2;
        const ty = (tp.y - camera.y) * camera.zoom + camera.viewportHeight / 2;
        const _sa = Math.max(0, 1 - (t + 1) * TRAIL_FADE_RATE);
        ctx.globalAlpha = _sa * 0.8;
        ctx.strokeStyle = proj.trailColor;
        ctx.lineWidth = Math.max(1, proj.size * camera.zoom * 0.7 * (1 - t * 0.15));
        ctx.beginPath();
        ctx.moveTo(_spx, _spy);
        ctx.lineTo(tx, ty);
        ctx.stroke();
        // Revolver only: white-hot core inside the gold streak = "brighter
        // clean tracer" without fattening the line.
        if (_isRevolverStreak) {
          ctx.globalAlpha = _sa * 0.9;
          ctx.strokeStyle = '#FFFFFF';
          ctx.lineWidth = Math.max(0.5, proj.size * camera.zoom * 0.25 * (1 - t * 0.15));
          ctx.beginPath();
          ctx.moveTo(_spx, _spy);
          ctx.lineTo(tx, ty);
          ctx.stroke();
        }
        _spx = tx; _spy = ty;
      }
      ctx.restore();
      continue; // streaks replace both ghost + dot trails for these weapons
    }

    // BETA 20.69 — SPRITE GHOST TRAILS (Dylan: "vfx on the basketball when
    // it is thrown. looks like crap"). Sprite projectiles — the throwables —
    // now trail spinning AFTERIMAGES of their own sprite instead of flat
    // colored dots. Non-sprite projectiles keep the classic dot trail below.
    const _ghostSprite = proj.spriteUrl ? getWeaponSprite(proj.spriteUrl) : null;
    if (_ghostSprite && _ghostSprite.naturalWidth > 0) {
      const _gAspect = _ghostSprite.naturalWidth / _ghostSprite.naturalHeight;
      const _gBaseH = (proj.spriteDisplaySize > 0 ? proj.spriteDisplaySize : proj.size * 1.5) * camera.zoom;
      // BETA 20.72 — Shitty Cannon ghosts run HEAVY: denser alpha + slower
      // shrink than the default 0.35/0.16 so the cannonball smears like a
      // dense mass vs Poop Sling's light 0.35 lob.
      const _isHeavyGhost = _trailKind === 'ghostHeavy';   // BETA 22.73 — registry-driven
      const _gAlpha = _isHeavyGhost ? 0.55 : 0.35;
      const _gShrink = _isHeavyGhost ? 0.10 : 0.16;
      for (let t = 0; t < proj.trailPositions.length; t++) {
        const tp = proj.trailPositions[t];
        const tx = (tp.x - camera.x) * camera.zoom + camera.viewportWidth / 2;
        const ty = (tp.y - camera.y) * camera.zoom + camera.viewportHeight / 2;
        const fade = Math.max(0, 1 - (t + 1) * TRAIL_FADE_RATE);
        const sc = 1 - (t + 1) * _gShrink;
        if (fade <= 0 || sc <= 0.2) continue;
        const gh = _gBaseH * sc, gw = gh * _gAspect;
        ctx.save();
        ctx.globalAlpha = fade * _gAlpha;
        ctx.translate(tx, ty);
        if (proj.spinRate > 0) ctx.rotate(proj.age * proj.spinRate - (t + 1) * 0.45);
        ctx.drawImage(_ghostSprite, -gw / 2, -gh / 2, gw, gh);
        ctx.restore();
      }
      continue; // ghosts replace the dot trail for sprite projectiles
    }

    // Draw trail
    for (let t = 0; t < proj.trailPositions.length; t++) {
      const tp = proj.trailPositions[t];
      const tScreenX = (tp.x - camera.x) * camera.zoom + camera.viewportWidth / 2;
      const tScreenY = (tp.y - camera.y) * camera.zoom + camera.viewportHeight / 2;
      const alpha = 1 - (t + 1) * TRAIL_FADE_RATE;
      const trailSize = proj.size * camera.zoom * (1 - t * 0.1);

      ctx.save();
      ctx.globalAlpha = Math.max(0, alpha * 0.6);
      ctx.fillStyle = proj.trailColor;
      ctx.beginPath();
      ctx.arc(tScreenX, tScreenY, Math.max(1, trailSize), 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }

  // BETA 16.77 — Per Dylan: "the kittens is showing a large purple
  // circle now. we dont need to see the purple circle. just explain on
  // the card for te wepon what it deos." Lure ring removed — the suck
  // mechanic still works invisibly via kittenLure.tickKittenLure;
  // players read the weapon card description for what kittens do.
  // (Set to false to skip the entire ring render block while keeping
  // the code in place for any future debug toggle.)
  const _SHOW_KITTEN_LURE_RING = false;
  if (_SHOW_KITTEN_LURE_RING) {
    const lureRadius = _kittenLureRadiusGetter ? _kittenLureRadiusGetter() : 0;
    if (lureRadius > 0) {
      const t = (typeof performance !== 'undefined' ? performance.now() : Date.now()) / 1000;
      const pulse = 0.55 + Math.sin(t * 2.2) * 0.18;
      for (const proj of state.projectiles) {
        if (!proj.alive) continue;
        if (proj.weaponId !== 'wpn_ugly_kittens') continue;
        if (proj.speed !== 0) continue;
        const sx = (proj.x - camera.x) * camera.zoom + camera.viewportWidth / 2;
        const sy = (proj.y - camera.y) * camera.zoom + camera.viewportHeight / 2;
        const r = lureRadius * camera.zoom;
        if (sx + r < 0 || sx - r > camera.viewportWidth ||
            sy + r < 0 || sy - r > camera.viewportHeight) continue;
        // Lifetime fade — ring brighter as the kitten nears expiry.
        // Projectile interface doesn't track initialLifetime; use the
        // weapon-def lifetime as the reference baseline (12s for kittens).
        const KITTEN_BASE_LIFETIME = 12;
        const lifeFrac = Math.max(0, Math.min(1, proj.lifetime / KITTEN_BASE_LIFETIME));
        const urgency = lifeFrac < 0.3 ? 1.0 : 0.6 + (1 - lifeFrac) * 0.8;
        ctx.save();
        // Soft fill — just a hint of purple in the ground area
        ctx.globalAlpha = 0.10 * pulse * urgency;
        ctx.fillStyle = '#cc55ff';
        ctx.beginPath();
        ctx.arc(sx, sy, r, 0, Math.PI * 2);
        ctx.fill();
        // Bright ring outline
        ctx.globalAlpha = 0.55 * pulse * urgency;
        ctx.strokeStyle = '#ff66ff';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(sx, sy, r, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }
    }
  }

  // ── Render projectiles ──
  // BETA 15.40 — two-pass kitten render. Per Dylan: "when the cat item attacks
  // that attack image should be above any floor sprite. including another cat."
  // First pass renders all projectiles INCLUDING idle traps (the static "floor
  // sprite" base cats). Attacking traps (wasHit=true on speed=0 projectiles)
  // are deferred to a second pass that runs after the main loop, so the attack
  // animation always paints on top of every other projectile/floor cat.
  const _deferredAttackingTraps: Projectile[] = [];
  for (const proj of state.projectiles) {
    if (!proj.alive) continue;

    // Defer attacking trap projectiles (e.g. Ugly Kittens mid-fight) to pass 2.
    if (proj.speed === 0 && proj.wasHit) {
      _deferredAttackingTraps.push(proj);
      continue;
    }

    const screenX = (proj.x - camera.x) * camera.zoom + camera.viewportWidth / 2;
    const screenY = (proj.y - camera.y) * camera.zoom + camera.viewportHeight / 2;

    // Frustum culling
    if (screenX < -50 || screenX > camera.viewportWidth + 50 ||
        screenY < -50 || screenY > camera.viewportHeight + 50) continue;

    const renderSize = proj.size * camera.zoom;
    const bodyColor = proj.isCrit ? '#FF6B6B' : proj.bodyColor;
    const glowColor = proj.isCrit ? '#FF4444' : proj.glowColor;

    // BETA 22.70 — weaponVfx custom projectile body (hero weapons). Replaces
    // the entire default shape/glow/sprite render for weapons that opted in
    // (Firestarter's flame gout, etc.). Everything else falls through.
    const _customVfx = getWeaponVfx(proj.weaponId);
    if (_customVfx?.drawBody) {
      ctx.save();
      _customVfx.drawBody(ctx, proj, screenX, screenY, camera.zoom);
      ctx.restore();
      continue;
    }

    // Skip invisible melee/arc projectiles (no sprite and no body color)
    // BETA 13.95: also accept anim-frame projectiles (Ugly Kittens) which have
    // an empty proj.spriteUrl but a non-empty animFrames array on the weapon def.
    const _animDefCheck = getWeaponDef(proj.weaponId);
    const _hasAnim = !!_animDefCheck?.projectileAnimFrames?.length;
    const projSprite0 = (proj.spriteUrl || _hasAnim)
      ? getWeaponSprite(_animDefCheck?.projectileAnimFrames?.[0] ?? proj.spriteUrl)
      : null;
    if (!projSprite0 && !bodyColor) { continue; }

    ctx.save();

    // ── BETA 13.95 — animated projectile sprite (Ugly Kittens etc.) ──
    // If the weapon def has projectileAnimFrames, cycle through them based on age.
    // Falls back to proj.spriteUrl if no anim frames or weapon def missing.
    // BETA 14.05 — for trap-mode projectiles (speed === 0), the cycle is
    // GATED on proj.wasHit. Idle = static base sprite (proj.spriteUrl); on
    // first enemy contact wasHit flips and the cycle starts.
    let activeSpriteUrl = proj.spriteUrl;
    const _animDef = getWeaponDef(proj.weaponId);
    // BETA 20.63 — chasing-pet kittens: claw frames ONLY during the on-hit
    // flash window; idle cat sprite while running. Other anim weapons keep
    // the legacy trap gate (speed 0 + wasHit).
    const _isKittenPet = proj.weaponId === _UGLY_KITTENS_ID;
    if (_animDef?.projectileAnimFrames && _animDef.projectileAnimFrames.length > 0) {
      const isTrap = proj.speed === 0;
      const animActive = _isKittenPet
        ? proj.age < (proj.attackFlashUntilAge ?? -1)
        : (!isTrap || proj.wasHit);
      if (animActive) {
        const frameDur = _animDef.projectileAnimFrameDuration ?? 0.15;
        // Kittens: count frames from the flash START so each pounce plays
        // 1→2→3 from the top. Legacy weapons: age-modulo cycling as before.
        const _animBase = _isKittenPet ? Math.max(0, (proj.attackFlashUntilAge ?? 0) - 0.36) : 0;
        const frameIdx = Math.floor(Math.max(0, proj.age - _animBase) / frameDur) % _animDef.projectileAnimFrames.length;
        activeSpriteUrl = _animDef.projectileAnimFrames[frameIdx];
        // BETA 20.69 — claw impact feedback. BETA 20.78 — the 20.69 white
        // expanding ring was yet another generic circle (Dylan: "still
        // seeing circles"). Now: CLAW RAKE — three parallel slash strokes
        // lashed across the victim, quick fade, no ring. Slash angle is
        // seeded from attackFlashUntilAge (constant for the whole flash →
        // strokes hold steady; each new pounce rakes at a fresh angle).
        // Deterministic, zero RNG, zero allocations (PERF law).
        if (_isKittenPet) {
          const _flashEl = proj.age - _animBase;
          if (_flashEl >= 0 && _flashEl < 0.15) {
            const _rt = _flashEl / 0.15;
            const _slashA = ((proj.attackFlashUntilAge ?? 0) * 53.17) % 6.2832;
            const _cs = Math.cos(_slashA);
            const _sn = Math.sin(_slashA);
            const _grow = Math.min(1, _rt * 2.5); // strokes lash out fast
            const _len = (14 + 10 * _grow) * camera.zoom;
            const _gap = 4.5 * camera.zoom;       // parallel claw spacing
            ctx.save();
            ctx.strokeStyle = '#FFF6E8';
            ctx.lineCap = 'round';
            for (let _s = -1; _s <= 1; _s++) {
              // perpendicular offset between claws + a slight stagger along
              // the rake direction (real claw marks don't start in a line)
              const _ox = -_sn * _gap * _s + _cs * _s * 2 * camera.zoom;
              const _oy = _cs * _gap * _s + _sn * _s * 2 * camera.zoom;
              ctx.globalAlpha = (1 - _rt) * (_s === 0 ? 0.95 : 0.7);
              ctx.lineWidth = (_s === 0 ? 2.6 : 1.8) * camera.zoom * (1 - _rt * 0.5);
              ctx.beginPath();
              ctx.moveTo(screenX + _ox - _cs * _len, screenY + _oy - _sn * _len);
              ctx.lineTo(screenX + _ox + _cs * _len, screenY + _oy + _sn * _len);
              ctx.stroke();
            }
            ctx.restore();
          }
        }
      }
    }

    // ── Sprite projectile (has an actual image) ──────────────────
    const projSprite = activeSpriteUrl ? getWeaponSprite(activeSpriteUrl) : null;
    if (projSprite) {
      const velAngle = Math.atan2(proj.velocityY, proj.velocityX);
      const aspect = projSprite.naturalWidth / projSprite.naturalHeight;
      // Use spriteDisplaySize override if set (throwable weapons need to be bigger)
      const baseH = proj.spriteDisplaySize > 0
        ? proj.spriteDisplaySize * camera.zoom
        : Math.max(2, renderSize * 0.75);
      const sprH = baseH;
      const sprW = sprH * aspect;
      // Spinning: use age × spinRate; non-spinning: orient along velocity.
      // BETA 20.63 — kitten pets stay UPRIGHT (a cat rotated along velocity
      // runs sideways/upside-down); mirror left/right by heading instead.
      const drawAngle = _isKittenPet ? 0 : (proj.spinRate > 0 ? proj.age * proj.spinRate : velAngle);

      // BETA 22.83 — ground shadow for THROWN props (Bible: "ground shadows on
      // all physical flying objects"). A flattened dark ellipse just under the
      // spinning object = depth. Drawn BEFORE the translate/rotate so it stays
      // flat on the floor. Pure VFX — no gameplay/collision effect.
      if (_animDef?.throwable) {
        ctx.save();
        ctx.globalAlpha = 0.26;
        ctx.fillStyle = '#000';
        ctx.beginPath();
        ctx.ellipse(screenX, screenY + sprH * 0.42, sprW * 0.36, sprH * 0.14, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      ctx.translate(screenX, screenY);
      ctx.rotate(drawAngle);
      if (_isKittenPet && proj.velocityX < 0) ctx.scale(-1, 1);
      // BETA 22.86 — squash-and-stretch on bounce (Bible: a rubber dodgeball
      // must DEFORM when it slams a wall, not just carom like a marble). Set at
      // each bounce (proj.squashUntilAge = age + 0.14); here it eases out as a
      // quick stretch-along-travel + flatten-perpendicular that springs back.
      // Pure VFX — the sim center/collision already used the true position. The
      // 0.32/0.24 pair keeps volume roughly constant so it reads as rubber, not
      // a size pop.
      const _sqLeft = (proj.squashUntilAge ?? 0) - proj.age;
      if (_sqLeft > 0) {
        const _t = _sqLeft / 0.14;             // 1 → 0 over the window
        const _e = _t * _t;                    // ease-out (snappier settle)
        ctx.scale(1 + 0.32 * _e, 1 - 0.24 * _e);
      }
      // BETA 13.101 — `cleanSprite` opt-out for the procedural glow halo.
      // BETA 14.08 — also kills the crit-halo for cleanSprite weapons. User:
      // "every now and then there is a red VFX on frozen dart projectile.
      // please remove" — that red flash was the crit halo firing on lucky
      // shots. Damage numbers floating off the enemy still convey crit
      // feedback (red text). For non-cleanSprite (legacy throwable weapons:
      // Viking Axe, VooGoo, Basketball), keep the original behavior.
      const _isClean = !!_animDefCheck?.projectile?.cleanSprite;
      // BETA 20.69 — faux-glow disc REMOVED (Dylan: the basketball "looks
      // like crap" — it was exactly this flat 15%-alpha circle under the
      // ball). Throwables read via sprite ghost-trails + bounce pops now.
      // BETA 20.78 — the legacy 25%-alpha red crit DISC is gone too (last
      // flat circle on this path). Crit feedback for non-cleanSprite
      // throwables (Viking Axe / VooGoo / Basketball) is now the 4-point
      // star glint drawn AFTER the sprite below — sprite itself untouched.
      // For cleanSprite weapons, skip everything — just the sprite, no drop
      // shadow, no glow. User: "no extra visualls needed".
      ctx.globalAlpha = 1.0;
      ctx.drawImage(projSprite, -sprW / 2, -sprH / 2, sprW, sprH);
      // BETA 20.78 — crit STAR GLINT: sharp red 4-point star riding the
      // sprite's top-right corner with a white-hot core, size twinkling off
      // proj.age (deterministic — no RNG in render). We're still inside the
      // sprite's translate+rotate, so the glint rides the corner as the
      // throwable spins — light catching an edge on a crit.
      if (!_isClean && proj.isCrit) {
        const _gx = sprW * 0.42;
        const _gy = -sprH * 0.42;
        const _tw = 0.8 + 0.2 * Math.sin(proj.age * 28); // twinkle
        const _gl = Math.max(4, (sprW + sprH) * 0.16) * _tw; // spike length
        const _gw = _gl * 0.22;                              // star waist
        ctx.fillStyle = '#FF2A2A';
        ctx.globalAlpha = 0.9;
        ctx.beginPath();
        ctx.moveTo(_gx, _gy - _gl);
        ctx.lineTo(_gx + _gw, _gy - _gw);
        ctx.lineTo(_gx + _gl, _gy);
        ctx.lineTo(_gx + _gw, _gy + _gw);
        ctx.lineTo(_gx, _gy + _gl);
        ctx.lineTo(_gx - _gw, _gy + _gw);
        ctx.lineTo(_gx - _gl, _gy);
        ctx.lineTo(_gx - _gw, _gy - _gw);
        ctx.closePath();
        ctx.fill();
        // white-hot core — same star shape at 38% scale (no circles)
        const _cl = _gl * 0.38;
        const _cw = _gw * 0.38;
        ctx.fillStyle = '#FFFFFF';
        ctx.globalAlpha = 0.95;
        ctx.beginPath();
        ctx.moveTo(_gx, _gy - _cl);
        ctx.lineTo(_gx + _cw, _gy - _cw);
        ctx.lineTo(_gx + _cl, _gy);
        ctx.lineTo(_gx + _cw, _gy + _cw);
        ctx.lineTo(_gx, _gy + _cl);
        ctx.lineTo(_gx - _cw, _gy + _cw);
        ctx.lineTo(_gx - _cl, _gy);
        ctx.lineTo(_gx - _cw, _gy - _cw);
        ctx.closePath();
        ctx.fill();
        ctx.globalAlpha = 1.0;
      }
      // BETA 20.72 — Poop Sling: 3 tiny STINK-FLY motes circling the flying
      // poop with a per-fly alpha flicker (0.1-0.9). Pure arc math at the
      // already-translated origin — no allocs, no shadowBlur. The flies are
      // the Sling's airborne identity; the Cannon's is the heavy ghost smear.
      if (proj.weaponId === 'wpn_poop_sling') {
        ctx.fillStyle = '#3a2a14';
        const _flyR = sprH * 0.7;
        for (let _fi = 0; _fi < 3; _fi++) {
          const _fa = proj.age * 6 + _fi * 2.1;
          ctx.globalAlpha = 0.5 + 0.4 * Math.sin(proj.age * 23 + _fi * 4);
          ctx.beginPath();
          ctx.arc(Math.cos(_fa) * _flyR, Math.sin(_fa) * _flyR, Math.max(1, 1.3 * camera.zoom), 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.globalAlpha = 1.0;
      }
      ctx.restore();
      continue;
    }

    // BETA 13.101 — Squirter: thin water-stream streak (NOT a blue ball).
    // User: "this is supposed to be a steady stream and look like water.
    // liquid. thin line warping". Tapered ellipse oriented along velocity
    // with white core + soft blue halo + trailing droplet.
    if (proj.weaponId === 'wpn_squirter') {
      const angle = Math.atan2(proj.velocityY, proj.velocityX);
      const streamLen = Math.max(14, renderSize * 4.5);
      const streamThick = Math.max(1.2, renderSize * 0.55);

      ctx.translate(screenX, screenY);
      ctx.rotate(angle);

      // Outer wet halo (soft blue)
      ctx.globalAlpha = 0.30;
      ctx.fillStyle = '#aaddff';
      ctx.beginPath();
      ctx.ellipse(0, 0, streamLen * 0.55, streamThick * 1.3, 0, 0, Math.PI * 2);
      ctx.fill();
      // Bright water core (white tapered ellipse)
      ctx.globalAlpha = 0.95;
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.ellipse(0, 0, streamLen * 0.5, streamThick * 0.5, 0, 0, Math.PI * 2);
      ctx.fill();
      // Trailing droplet
      ctx.globalAlpha = 0.7;
      ctx.fillStyle = '#cce6ff';
      ctx.beginPath();
      ctx.arc(-streamLen * 0.42, 0, streamThick * 0.45, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1.0;

      ctx.restore();
      continue;
    }

    // BETA 22.76 — Bad Breakfast: the TOASTER GUN fires scalding burnt toast,
    // not rainbow confetti. (22.75 audit caught the flamethrower-class identity
    // drift: def/sprite/description/Bible all say toaster, render said party
    // confetti.) A tumbling square toast slice — brown body, darker toasted
    // crust — with an intermittent blue-white electric zap arc (Bible: "toast
    // and electric junk"). Deterministic; no RNG in the render.
    if (proj.weaponId === 'wpn_hurray') {
      const tR = Math.max(3, renderSize * 1.7);        // toast half-size
      const spinAngle = proj.id * 0.7 + proj.age * 14; // tumbling slice
      ctx.translate(screenX, screenY);
      ctx.rotate(spinAngle);
      // toasted crust edge (darker, slightly larger square)
      ctx.fillStyle = proj.isCrit ? '#3a220e' : '#5a3a1b';
      ctx.fillRect(-tR, -tR, tR * 2, tR * 2);
      // golden-brown toast body inset from the crust
      ctx.fillStyle = proj.isCrit ? '#c07a2e' : '#8a5a2b';
      ctx.fillRect(-tR * 0.74, -tR * 0.74, tR * 1.48, tR * 1.48);
      // two dark toast-hole specks so it reads as a slice, not a block
      ctx.fillStyle = '#4a2f14';
      ctx.beginPath();
      ctx.arc(-tR * 0.22, -tR * 0.15, tR * 0.13, 0, Math.PI * 2);
      ctx.arc(tR * 0.25, tR * 0.2, tR * 0.1, 0, Math.PI * 2);
      ctx.fill();
      // intermittent electric zap arc jumping off the toast (blue-white)
      if (Math.floor(proj.age * 12) % 3 === 0) {
        ctx.strokeStyle = '#cfefff';
        ctx.lineWidth = Math.max(1, tR * 0.16);
        ctx.globalAlpha = 0.9;
        ctx.beginPath();
        ctx.moveTo(-tR * 0.9, -tR * 0.2);
        ctx.lineTo(-tR * 0.2, tR * 0.15);
        ctx.lineTo(tR * 0.35, -tR * 0.35);
        ctx.lineTo(tR * 1.05, tR * 0.1);
        ctx.stroke();
        ctx.globalAlpha = 1.0;
      }
      ctx.restore();
      continue;
    }

    if (proj.shape === 'beam') {
      // Long thin streak oriented along velocity (sniper-style)
      const angle = Math.atan2(proj.velocityY, proj.velocityX);
      const beamLen = Math.max(8, proj.length * camera.zoom);
      const beamThick = Math.max(1.5, renderSize * 0.6);

      ctx.translate(screenX, screenY);
      ctx.rotate(angle);

      if (proj.weaponId === 'wpn_ultra_beam') {
        // BETA 22.86 — ULTRA BEAM is now a JAGGED ELECTRIC LANCE, not a flat
        // rect (Bible: "the beam is raw energy — it should CRACKLE"). A
        // midpoint-displaced polyline (house lightning pattern) whose nodes
        // jitter every frame via a deterministic hash of (node, age, id) so it
        // reads as live electricity. Endpoints pinned to y=0 so it still
        // emanates dead-straight from the muzzle to the hit point. No
        // shadowBlur (13.70) — the glow is a wide low-alpha jagged pass.
        const NODES = 12;
        const amp = beamThick * 2.2;
        const tSlot = Math.floor(proj.age * 34);   // ~34 crackle updates/sec
        const pts: Array<[number, number]> = [];
        for (let n = 0; n <= NODES; n++) {
          const fx = (n / NODES) * beamLen;
          // taper to 0 at both ends (anchored muzzle + tip)
          const taper = Math.sin((n / NODES) * Math.PI);
          const h = Math.sin(n * 12.9898 + tSlot * 78.233 + proj.id * 3.7) * 43758.5453;
          const r = h - Math.floor(h);            // 0..1 deterministic
          pts.push([fx, (r * 2 - 1) * amp * taper]);
        }
        const strokePath = (w: number, col: string, a: number) => {
          ctx.globalAlpha = a;
          ctx.strokeStyle = col;
          ctx.lineWidth = w;
          ctx.lineJoin = 'round';
          ctx.lineCap = 'round';
          ctx.beginPath();
          ctx.moveTo(pts[0][0], pts[0][1]);
          for (let n = 1; n < pts.length; n++) ctx.lineTo(pts[n][0], pts[n][1]);
          ctx.stroke();
        };
        // 3 stacked passes: wide violet glow → mid body → white-hot core
        strokePath(beamThick * 4.0, glowColor, 0.22);
        strokePath(beamThick * 1.8, bodyColor, 0.9);
        strokePath(Math.max(1, beamThick * 0.7), '#FFFFFF', 1.0);
        // crackle forks off a couple of mid nodes
        ctx.globalAlpha = 0.7;
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = Math.max(1, beamThick * 0.5);
        for (let f = 0; f < 2; f++) {
          const ni = 3 + f * 5 + (tSlot % 2);
          if (ni >= pts.length) continue;
          const [bx, by] = pts[ni];
          const fh = Math.sin(ni * 7.13 + tSlot * 3.1 + proj.id) * 1000;
          const fr = fh - Math.floor(fh);
          ctx.beginPath();
          ctx.moveTo(bx, by);
          ctx.lineTo(bx + (fr - 0.5) * beamThick * 3, by + (fr < 0.5 ? -1 : 1) * beamThick * 4);
          ctx.stroke();
        }
        ctx.globalAlpha = 1.0;
      } else {
        // BETA 13.70 FPS: faux-glow halo around beam instead of shadowBlur.
        // Beam now trails FORWARD from the origin (x-start 0, not -beamLen/2)
        // so it visually emanates from the gun tip instead of being centered
        // on the projectile point.
        ctx.globalAlpha = 0.22;
        ctx.fillStyle = glowColor;
        ctx.fillRect(0, -beamThick * 1.6, beamLen, beamThick * 3.2);
        ctx.globalAlpha = 1.0;
        ctx.fillStyle = bodyColor;
        ctx.fillRect(0, -beamThick / 2, beamLen, beamThick);

        // Bright core line
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, -beamThick * 0.2, beamLen, beamThick * 0.4);
      }
    } else if (proj.shape === 'orb') {
      // BETA 20.69 — COMET ORB (Dylan: "weapons using circles faded in
      // color... lame"). Was three flat stacked discs. Now: the orb
      // stretches along its velocity into a comet — tapered colored tail,
      // bright shell, white-hot core, subtle flicker. Still pure arcs
      // (13.70's no-shadowBlur perf rule kept).
      const orbR = Math.max(4, renderSize * 1.4);
      const _oAng = Math.atan2(proj.velocityY, proj.velocityX);
      const _oSpd = Math.hypot(proj.velocityX, proj.velocityY);
      const _stretch = 1 + Math.min(0.9, _oSpd / 700);
      const _flick = 0.92 + 0.08 * Math.sin(proj.age * 31 + proj.id);
      ctx.save();
      ctx.translate(screenX, screenY);
      ctx.rotate(_oAng);
      // tapered tail (two fading ellipses trailing behind)
      ctx.globalAlpha = 0.22;
      ctx.fillStyle = glowColor;
      ctx.beginPath();
      ctx.ellipse(-orbR * 1.6, 0, orbR * 1.5, orbR * 0.55, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 0.35;
      ctx.beginPath();
      ctx.ellipse(-orbR * 0.7, 0, orbR * 1.2, orbR * 0.75, 0, 0, Math.PI * 2);
      ctx.fill();
      // body shell, velocity-stretched
      ctx.globalAlpha = _flick;
      ctx.fillStyle = bodyColor;
      ctx.beginPath();
      ctx.ellipse(0, 0, orbR * _stretch, orbR, 0, 0, Math.PI * 2);
      ctx.fill();
      // white-hot core
      ctx.fillStyle = '#FFFFFF';
      ctx.beginPath();
      ctx.ellipse(orbR * 0.25, 0, Math.max(2, renderSize * 0.62), Math.max(1.5, renderSize * 0.5), 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    } else if (proj.shape === 'bullet') {
      // Pistol-style bullet: rounded casing end, tapered tip, oriented along velocity
      const velAngle = Math.atan2(proj.velocityY, proj.velocityX);
      const bLen = Math.max(8, renderSize * 3.0);
      const bRad = Math.max(1.5, renderSize * 0.65);

      ctx.translate(screenX, screenY);
      ctx.rotate(velAngle);

      // BETA 13.70 FPS: tiny halo behind bullet instead of shadowBlur=6.
      ctx.globalAlpha = 0.18;
      ctx.fillStyle = glowColor;
      ctx.beginPath();
      ctx.arc(0, 0, bRad * 2.0, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1.0;

      // Brass body: left semicircle (casing), straight sides, pointed tip
      ctx.fillStyle = proj.isCrit ? '#FF9900' : bodyColor;
      ctx.beginPath();
      ctx.arc(-bLen / 2 + bRad, 0, bRad, Math.PI / 2, -Math.PI / 2, true);
      ctx.lineTo(bLen * 0.2, -bRad);
      ctx.lineTo(bLen / 2, 0);
      ctx.lineTo(bLen * 0.2, bRad);
      ctx.closePath();
      ctx.fill();

      // Subtle shine
      ctx.fillStyle = 'rgba(255,255,200,0.45)';
      ctx.beginPath();
      ctx.ellipse(-bLen * 0.08, -bRad * 0.35, bLen * 0.22, bRad * 0.28, 0, 0, Math.PI * 2);
      ctx.fill();
    } else {
      // Default: circle (RPK, generic)
      // BETA 13.70 FPS: crit halo via alpha disc instead of shadowBlur.
      if (proj.isCrit) {
        ctx.globalAlpha = 0.28;
        ctx.fillStyle = glowColor;
        ctx.beginPath();
        ctx.arc(screenX, screenY, Math.max(3, renderSize * 1.8), 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1.0;
      }
      ctx.fillStyle = bodyColor;
      ctx.beginPath();
      ctx.arc(screenX, screenY, Math.max(2, renderSize), 0, Math.PI * 2);
      ctx.fill();

      // Bright center
      ctx.fillStyle = '#FFFFFF';
      ctx.beginPath();
      ctx.arc(screenX, screenY, Math.max(1, renderSize * 0.4), 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  // ── BETA 15.40 — PASS 2: Render attacking trap projectiles (Ugly Kittens
  // mid-fight) ON TOP of all other projectiles. This guarantees the cat-attack
  // animation frames are never covered by another cat's idle floor sprite or
  // by any other projectile in the same render layer.
  // Per Dylan: "the cat attacks are unable ot be seen. they are being covered.
  // so always on top."
  for (const proj of _deferredAttackingTraps) {
    const screenX = (proj.x - camera.x) * camera.zoom + camera.viewportWidth / 2;
    const screenY = (proj.y - camera.y) * camera.zoom + camera.viewportHeight / 2;
    if (screenX < -50 || screenX > camera.viewportWidth + 50 ||
        screenY < -50 || screenY > camera.viewportHeight + 50) continue;
    const renderSize = proj.size * camera.zoom;
    const _animDef = getWeaponDef(proj.weaponId);
    if (!_animDef?.projectileAnimFrames || _animDef.projectileAnimFrames.length === 0) continue;
    const frameDur = _animDef.projectileAnimFrameDuration ?? 0.15;
    const frameIdx = Math.floor(proj.age / frameDur) % _animDef.projectileAnimFrames.length;
    const activeSpriteUrl = _animDef.projectileAnimFrames[frameIdx];
    const projSprite = getWeaponSprite(activeSpriteUrl);
    if (!projSprite) continue;
    const aspect = projSprite.naturalWidth / projSprite.naturalHeight;
    const baseH = proj.spriteDisplaySize > 0
      ? proj.spriteDisplaySize * camera.zoom
      : Math.max(2, renderSize * 0.75);
    const sprH = baseH;
    const sprW = sprH * aspect;
    const drawAngle = proj.spinRate > 0 ? proj.age * proj.spinRate : 0;
    ctx.save();
    ctx.translate(screenX, screenY);
    ctx.rotate(drawAngle);
    ctx.globalAlpha = 1.0;
    ctx.drawImage(projSprite, -sprW / 2, -sprH / 2, sprW, sprH);
    ctx.restore();
  }

  // ── Render muzzle flashes ──
  for (const flash of state.muzzleFlashes) {
    const screenX = (flash.x - camera.x) * camera.zoom + camera.viewportWidth / 2;
    const screenY = (flash.y - camera.y) * camera.zoom + camera.viewportHeight / 2;

    ctx.save();
    const fa = flash.timer / MUZZLE_FLASH_DURATION;   // 1 → 0 over the flash
    // BETA 20.69 — three-layer discharge (VFX overhaul): white-hot core that
    // SHRINKS + gold flash + orange ring that EXPANDS as it fades. Ease-out
    // brightness. Reads as a shot, not a static blob. (13.70's no-shadowBlur
    // perf rule kept — pure arcs.)
    const ease = fa * fa;
    const grow = 1 + (1 - fa) * 0.9;
    ctx.globalAlpha = ease * 0.45;
    ctx.strokeStyle = '#FF9500';
    ctx.lineWidth = 3 * camera.zoom;
    ctx.beginPath();
    ctx.arc(screenX, screenY, 12 * grow * camera.zoom, 0, Math.PI * 2);
    ctx.stroke();
    ctx.globalAlpha = ease * 0.4;
    ctx.fillStyle = '#FFD700';
    ctx.beginPath();
    ctx.arc(screenX, screenY, 9 * grow * camera.zoom, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = Math.min(1, ease * 1.2);
    ctx.fillStyle = '#FFFFFF';
    ctx.beginPath();
    ctx.arc(screenX, screenY, (5 * fa + 1.5) * camera.zoom, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  // ── Render damage numbers ──
  // BETA 16.94 — skip inactive pool slots.
  for (const dn of state.damageNumbers) {
    if (!dn.active) continue;
    const screenX = (dn.x - camera.x) * camera.zoom + camera.viewportWidth / 2;
    const screenY = (dn.y - camera.y) * camera.zoom + camera.viewportHeight / 2;

    ctx.save();
    ctx.globalAlpha = Math.min(1, dn.timer * 2); // Fade out

    if (dn.isBuff && dn.label) {
      // BETA 15.79 — Buff popup (Mystery Stew Meat random stat roll, etc.).
      // Green text, smaller than crit, distinct from damage numbers so the
      // player can tell at a glance which event happened.
      ctx.font = `bold ${Math.round(12 * camera.zoom)}px 'Share Tech Mono', monospace`;
      ctx.fillStyle = dn.color ?? '#7ee090';   // BETA 20.27 — XP/gold gain popups carry their own color
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.textAlign = 'center';
      ctx.strokeText(dn.label, screenX, screenY);
      ctx.fillText(dn.label, screenX, screenY);
    } else if (dn.isCrit) {
      // BETA 15.67 — Crit popup made VERY visually distinct per Dylan: "no
      // crit chance pop up above enemies so I know if I am actually doing
      // crit damage and how much." Was bold 18px red. Now: bold 24px GOLD
      // with thicker outline + "CRIT!" prefix so you can't miss it.
      ctx.font = `bold ${Math.round(24 * camera.zoom)}px 'Share Tech Mono', monospace`;
      ctx.fillStyle = '#FFD700';
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 4;
      ctx.textAlign = 'center';
      const text = `CRIT! ${dn.value}`;
      ctx.strokeText(text, screenX, screenY);
      ctx.fillText(text, screenX, screenY);
    } else {
      // Normal: white text
      ctx.font = `bold ${Math.round(13 * camera.zoom)}px 'Share Tech Mono', monospace`;
      ctx.fillStyle = '#FFFFFF';
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.textAlign = 'center';
      ctx.strokeText(`${dn.value}`, screenX, screenY);
      ctx.fillText(`${dn.value}`, screenX, screenY);
    }

    ctx.restore();
  }
}

// ─── Arc Weapon Idle Preview ─────────────────────────────────
/**
 * BETA 15.49 — Per Dylan: "they are not locking on the circle around the
 * character. we can not see the attack box on either weapon. the weapon
 * should point wherever direction the player is running."
 *
 * Renders a persistent faint sector + weapon sprite preview for each
 * equipped arc/melee weapon (Spatula, Drac's Fang). Shows the player:
 *   - WHERE the swing will hit (sector range + spread)
 *   - WHICH WAY the weapon is currently pointing (player's facing direction)
 *   - WHAT WEAPON they're holding (sprite hovering at sector front)
 *
 * Skipped when an active arc-slash is firing (the bright slash visual
 * takes over). Faint enough to not dominate gameplay but readable.
 *
 * Called from engine.ts render pipeline before/with the player layer.
 */
export function renderArcWeaponIdle(
  state: WeaponSystemState,
  ctx: CanvasRenderingContext2D,
  camera: CameraForWeapon,
  playerX: number,
  playerY: number,
  facingAngle: number,
  /** BETA 15.54 — attackAreaBonus from player so sprite + sector both scale with size buffs. */
  attackAreaBonus: number = 0,
): void {
  // BETA 19.44 — Dylan spec (live audit): kill the idle attack-zone preview
  // entirely. It rendered a soft-gold sector + a faded weapon sprite hovering
  // ON the player so the user knew where the next swing would land. With the
  // new sprite-collision model the swing itself is the visual + hitbox — the
  // preview is redundant and Dylan called out:
  //   "the VFX is still there. that yellow animation need to go.
  //    also the handle and such is covering the main character.
  //    this looks like shit."
  // Soft-gold rgba(255,200,80,...) sector = the "yellow animation".
  // Faded weapon sprite at 60% reach in the facing direction = the "handle
  // covering the main character" (sprite half-extended over player).
  // No-op early return preserves the export so weaponAdapter wiring stays.
  void state; void ctx; void camera; void playerX; void playerY;
  void facingAngle; void attackAreaBonus;
  return;
}

// ─── Arc Slash Visual Rendering ──────────────────────────────
/**
 * Draw the glowing sector flash when an arc weapon swings.
 *
 * BETA 15.54 — fixed 4 PSBOT-flagged bugs:
 *   Bug 1: Sprite radius was hardcoded 50wu; hitbox extends to slash.radius
 *          (~84wu). Now uses `slash.radius * 0.6` so sprite ends near the
 *          hitbox edge (matched visually).
 *   Bug 2: Slash position (slash.x/y) was a SNAPSHOT at attack-spawn.
 *          When the player moved during the 0.35s swing, the visual
 *          stayed at spawn position — Dylan's "lag" complaint. Now
 *          uses live `playerX/playerY` passed in each frame.
 *   Bug 3: Sprite size was static; didn't scale with attackAreaBonus.
 *          Now multiplied by `slash.sizeMult` (captured at swing-time).
 *   Bug 4: Sprite at hardcoded 50wu meant it didn't dock at the orbit
 *          ring like other weapons. Resolved by Bug 1's fix (sprite
 *          extends to ~60% of full reach, much closer to weapon ring).
 */
/** Draw arc slash visuals on top of enemies. Called from weaponAdapter after renderEnemies. */
export function renderArcSlashes(
  state: WeaponSystemState,
  ctx: CanvasRenderingContext2D,
  camera: CameraForWeapon,
  playerX: number,
  playerY: number,
): void {
  for (const slash of state.arcSlashes) {
    // BETA 15.54 (Bug 2): use LIVE player position — sprite follows the
    // player during the 0.35s swing instead of staying at spawn position.
    const cx = (playerX - camera.x) * camera.zoom + camera.viewportWidth  / 2;
    const cy = (playerY - camera.y) * camera.zoom + camera.viewportHeight / 2;

    // ── Weapon sprite sweeps through the arc ──────────────────────────────
    const img = slash.spriteUrl ? getWeaponSprite(slash.spriteUrl) : null;
    if (img && img.naturalWidth > 0) {
      // BETA 19.44 — sweep angle math lives in computeSlashSweepAngle so the
      // visual sprite and the damage-hitbox (tickArcSlashHits) stay in sync.
      // Handles fan mode (linear or rampUp ease-in), orbit mode (continuous
      // rotation), and liveDirection (centerAngle refreshed by hit-tick).
      const sweepAngle = computeSlashSweepAngle(slash);
      // BETA 15.54 (Bug 3): sprite size scales with attackAreaBonus captured
      // at swing-time (slash.sizeMult). Matches the hitbox grow.
      const sprH = slash.spriteSize * camera.zoom * (slash.sizeMult ?? 1);
      const aspect = img.naturalWidth / img.naturalHeight;
      const sprW = sprH * aspect;
      // BETA 19.44 — sprite center sits at full reach distance × sizeMult
      // (was 0.6 — Dylan: handle was covering the player). Multiplier lives
      // in ARC_SPRITE_DISTANCE_FRACTION so hit-tick stays in sync.
      const spriteRadius = slash.radius * ARC_SPRITE_DISTANCE_FRACTION * (slash.sizeMult ?? 1) * camera.zoom;
      const px = cx + Math.cos(sweepAngle) * spriteRadius;
      const py = cy + Math.sin(sweepAngle) * spriteRadius;

      // BETA 22.86 — melee SWING SMEAR (Bible: a swing must leave a motion arc,
      // not just snap a static sprite through the sweep). A tapered, fading
      // ribbon traces the path already swept this frame, tinted per-weapon via
      // meleeSparkColor (Barbed Wire = blood-red, Chain Whip = hot yellow,
      // Chainsaw = orange grind). Drawn UNDER the blade so the sprite reads on
      // top. Pure VFX — no hitbox change (tickArcSlashHits is untouched).
      const _smearColor = getWeaponVfx(slash.weaponId)?.meleeSparkColor ?? '#e8e8e8';
      const _headAngle = sweepAngle;
      const _tailAngle = slash.mode === 'orbit'
        ? _headAngle - 1.15                                  // fixed trail behind the spin
        : slash.centerAngle - slash.halfSpread;              // from the swing's start edge
      const _span = _headAngle - _tailAngle;
      if (Math.abs(_span) > 0.05) {
        const _tNorm = slash.timer / slash.maxTimer;                    // 1 → 0
        const _fade = (slash.noFade || slash.mode === 'orbit') ? 1 : Math.min(1, _tNorm * 2.2);
        ctx.save();
        ctx.lineCap = 'round';
        ctx.strokeStyle = _smearColor;
        const _ribbonW = sprH * 0.42;
        const SEG = 7;
        for (let s = 0; s < SEG; s++) {
          const a0 = _tailAngle + (_span * s) / SEG;
          const a1 = _tailAngle + (_span * (s + 1)) / SEG;
          const lead = (s + 1) / SEG;                        // 0 (tail) → 1 (blade)
          ctx.globalAlpha = (0.05 + 0.30 * lead) * _fade;    // faint tail, bright head
          ctx.lineWidth = _ribbonW * (0.35 + 0.65 * lead);   // widens toward the blade
          ctx.beginPath();
          ctx.arc(cx, cy, spriteRadius, a0, a1, _span < 0);
          ctx.stroke();
        }
        ctx.restore();
      }

      ctx.save();
      // BETA 13.101 — `noFade` (Drac's Fang) keeps sprite at full alpha.
      // BETA 19.44 — orbit mode also stays at full alpha (continuous spin
      // should never look like it's "fading out").
      // Default: full visible at start, fades in last ~45% of swing.
      const t = slash.timer / slash.maxTimer;
      ctx.globalAlpha = (slash.noFade || slash.mode === 'orbit') ? 1.0 : Math.min(1, t * 2.2);
      ctx.translate(px, py);
      ctx.rotate(sweepAngle);
      const flipY = Math.cos(sweepAngle) < 0 ? -1 : 1;
      // BETA 13.101 — `noFlip` (Drac's Fang) skips the X-mirror. Spatula
      // sprite faces left in source so needs the mirror; Drac's Fang faces
      // right in source so the mirror inverts it (user: "facing backwards").
      const flipX = slash.noFlip ? 1 : -1;
      ctx.scale(flipX, flipY);
      // Anchor offset: mirrored sprites need -0.35 offset; non-mirrored
      // sprites read better with the handle at the player side (+0.35).
      const anchorX = slash.noFlip ? -sprW * 0.65 : -sprW * 0.35;
      ctx.drawImage(img, anchorX, -sprH / 2, sprW, sprH);
      ctx.restore();
    }
  }
}

// ─── Orbit Weapon Rendering ──────────────────────────────────
/**
 * Draw each equipped weapon at its slot position around the player,
 * rotated to point at its current aim target.
 */
// BETA 14.47 — Weapon-hit tracker registration. weaponSystem can't import
// from weaponLab (would create circular dep with engine.ts ← weaponLab).
// engine.ts wires this on init by registering weaponLab.trackWeaponHit.
// Each damage application site below calls _weaponHitTracker?.() with
// (weaponId, enemy.type, dmg, isCrit) so the per-weapon × per-enemy
// attribution matrix populates in WL CSV.
type WeaponHitTracker = (weaponId: string, enemyType: string, dmg: number, isCrit: boolean) => void;
let _weaponHitTracker: WeaponHitTracker | null = null;
export function registerWeaponHitTracker(t: WeaponHitTracker | null): void {
  _weaponHitTracker = t;
}

// BETA 20.72 — Weapon particle-spawner bridge. weaponSystem has no access to
// the engine's per-run particles array (circular dep with engine.ts), so
// engine.ts registers a thin spawnParticles wrapper here — same pattern as
// _weaponHitTracker above. NOTE for the engine wiring: state.particles is
// per-run, so the callback must capture a LIVE state getter, not a snapshot.
// Null-safe: until wired, every fire/hit/flip/bounce burst below is a silent
// no-op, and all the ctx-drawn effects (streak trails, stun rings, banana
// arcs, aura sunburst, stink flies, roiling puffs) still land regardless.
type WeaponParticleSpawner = (
  x: number, y: number, color: string, count: number,
  opts?: import('./types').ParticleOpts,
) => void;
let _weaponParticleSpawner: WeaponParticleSpawner | null = null;
export function registerWeaponParticleSpawner(fn: WeaponParticleSpawner | null): void {
  _weaponParticleSpawner = fn;
}

// BETA 20.72 — the other half of the impact-feedback bridge. engine.ts:384-418
// probes for these two exports with guarded property access (they were
// designed there first); without them the damage-number sparks + beam impact
// spray are permanent no-ops. Same registered-callback pattern as above.
let _damageNumberVfx: ((x: number, y: number, isCrit: boolean, isBuff: boolean, color?: string) => void) | null = null;
export function registerDamageNumberVfx(fn: typeof _damageNumberVfx): void {
  _damageNumberVfx = fn;
}
let _beamImpactVfx: ((x: number, y: number, travelAngle: number, color: string) => void) | null = null;
export function registerBeamImpactVfx(fn: typeof _beamImpactVfx): void {
  _beamImpactVfx = fn;
}

// BETA 16.69 — Kitten lure radius getter. Registered at engine init
// from kittenLure.getCurrentLureRadius(). Returns 0 when Ugly Kittens
// isn't equipped. Used in renderWeapons to draw the suck-radius ring.
let _kittenLureRadiusGetter: (() => number) | null = null;
export function registerKittenLureRadiusGetter(fn: (() => number) | null): void {
  _kittenLureRadiusGetter = fn;
}

// BETA 14.54 — Ugly Kittens event tracker registration. Same pattern as
// _weaponHitTracker. weaponSystem doesn't know about weaponLab; engine.ts
// wires this at init. Hooks: onPlace fires at projectile spawn for
// wpn_ugly_kittens; onAttack + onHit(dmg) fire at projectile collision when
// proj.weaponId === 'wpn_ugly_kittens'.
type KittenEventTracker = {
  onPlace: (x: number, y: number) => void;
  onAttack: () => void;
  onHit: (dmg: number, wasKill: boolean) => void;
  onProjectileDeath: (lifetimeMs: number) => void;
};
let _kittenEventTracker: KittenEventTracker | null = null;
export function registerKittenEventTracker(t: KittenEventTracker | null): void {
  _kittenEventTracker = t;
}
const _UGLY_KITTENS_ID = 'wpn_ugly_kittens';

// BETA 17.07 — Karen Drone diagnostic counter (PSBOT cycle-30w Protocol B
// step 1: INSTRUMENT FIRST, don't fix blind). PSBOT flagged Karen Drones
// at 2.1% hit rate in cycle-30r (BETA 17.00) vs 9.1% in cycle-30q (BETA
// 16.88). My git bisect showed NO Karen-related code changes between
// 16.88 and 17.00, so the "regression" might be character/build-dependent
// (cycle-30q was Killa, cycle-30r was Chad). This counter will surface
// the per-run drone behavior so PSBOT can analyze what's actually
// happening before we attempt a fix.
//
// peakDroneCount  = max active drone count seen this run
// contacts        = drone-vs-enemy DAMAGE applications (the actual "hit")
// cooldownSkips   = times we skipped because per-drone cd was active
// radiusSkips     = times an enemy was out of contact radius for a drone
// losSkips        = times line-of-sight check failed
//
// "Hit rate" math from PSBOT = collisions / opportunities. With this
// counter we can compute opportunity = contacts + cooldownSkips +
// radiusSkips + losSkips per run. Then PSBOT can see exactly where the
// drones are spending their time.
type KarenDroneTrace = {
  peakDroneCount: number;
  contacts: number;
  cooldownSkips: number;
  radiusSkips: number;
  losSkips: number;
};
let _karenDroneTrace: KarenDroneTrace = {
  peakDroneCount: 0,
  contacts: 0,
  cooldownSkips: 0,
  radiusSkips: 0,
  losSkips: 0,
};
export function resetKarenDroneTrace(): void {
  _karenDroneTrace = { peakDroneCount: 0, contacts: 0, cooldownSkips: 0, radiusSkips: 0, losSkips: 0 };
}
export function getKarenDroneTrace(): KarenDroneTrace {
  return _karenDroneTrace;
}

// BETA 14.55 — Floor-effect tracker registration. Wires up the
// floorEffectTracker CSV column. Fires at:
// - onSpawn(weaponId, lifetime) when spawnGroundEffectAt creates an effect
// - onEnemyTick(weaponId, dmg) when an enemy takes DoT damage from an effect
// - onFirstContact(weaponId) when first enemy steps onto a trap-style effect
// Captures spawn count, total dmg dealt, enemy trigger count per weapon ID.
type FloorEffectTracker = {
  onSpawn: (weaponId: string, lifetime: number) => void;
  onEnemyTick: (weaponId: string, dmg: number) => void;
  onFirstContact: (weaponId: string) => void;
};
let _floorEffectTracker: FloorEffectTracker | null = null;
export function registerFloorEffectTracker(t: FloorEffectTracker | null): void {
  _floorEffectTracker = t;
}

// BETA 14.58 — Synergy trigger callback. Fired when an actual synergy boost
// is applied (not just both items equipped). engine.ts wires this to
// weaponLab.trackSynergyTrigger.
type SynergyTriggerCallback = (key: string, valueDelta: number) => void;
let _synergyTriggerCallback: SynergyTriggerCallback | null = null;
export function registerSynergyTrigger(cb: SynergyTriggerCallback | null): void {
  _synergyTriggerCallback = cb;
}

// BETA 14.48 — Lag-fix helper. WL applyBuild hits this to flush all in-flight
// floor effects (SHIT trail puddles, Whoopie Cushion zones, etc.) so they
// don't accumulate across rapid RANDOMIZE clicks. CSV evidence: by 8th
// randomize, avg frame time had grown from 5ms → 144ms; some frames spiked
// to 2.6 SECONDS. Confirmed accumulator-leak pattern.
export function clearAllGroundEffects(state: WeaponSystemState): void {
  state.groundEffects.length = 0;
  state.nextGroundEffectId = 1;
}

/** BETA 23.01 — draw ONLY the attached orbit sprites, filtered by depth vs
 *  the player ('behind' = slot above Greg's center → draws before his
 *  sprite; 'front' = below → draws after). Called from the engine's player
 *  render group so weapons inherit Greg's y-sort vs props (Dylan rule 1/3).
 *  Flying orbitals (Karen Drones) are exempt and stay in the 'all' pass. */
export function renderOrbitWeaponSprites(
  state: WeaponSystemState,
  ctx: CanvasRenderingContext2D,
  camera: CameraForWeapon,
  player: { x: number; y: number; bonusProjectileCount?: number; bonusAttackSpeedPercent?: number },
  which: 'behind' | 'front',
): void {
  renderOrbitWeapons(state, ctx, camera, player, which);
}

function renderOrbitWeapons(
  state: WeaponSystemState,
  ctx: CanvasRenderingContext2D,
  camera: CameraForWeapon,
  player: {
    x: number;
    y: number;
    bonusProjectileCount?: number;
    bonusAttackSpeedPercent?: number;
  },
  which: 'behind' | 'front' | 'all' = 'all',
): void {
  for (const weapon of state.weapons) {
    const def = getWeaponDef(weapon.definitionId);
    if (!def) continue;
    if (def.aura) continue; // Aura weapons render their ring, not an orbiting sprite
    if (def.arc)  continue; // Arc weapons render a swing flash, not an orbiting sprite
    // BETA 23.01 — depth-filtered passes draw slot weapons only; flying
    // orbitals (drones) keep rendering in the 'all' (effects) pass — an
    // airborne drone above a machine is believable.
    if (which !== 'all' && def.orbital) continue;
    if (which === 'all' && def.orbital === undefined) { /* slot weapons draw here only in legacy mode */ }

    // BETA 14.79 — Throwable weapons leave the orbit ring while their projectile
    // is in flight.
    if (def.throwable) {
      const inFlight = state.projectiles.some(p =>
        p.alive && p.weaponId === weapon.definitionId,
      );
      if (inFlight) continue;
    }

    // BETA 16.24 — PSBOT cycle-25 P0.1 + P0.2: orbital weapons (Karen
    // Drones) render N drones distributed around the orbit, where
    // N = 1 + bonusProjectileCount. Spin speed is scaled by attack speed.
    // Non-orbital weapons stay single-position (1-element array).
    const positions = def.orbital
      ? getOrbitalDronePositions(state, weapon, def, player)
      : [getWeaponSlotPosition(state, weapon, player)];

    // BETA 22.69 — per-weapon feel config (recoil kick, idle bob, rest pose)
    const vis = resolveWeaponVisual(weapon.definitionId);

    for (const slot of positions) {

    // BETA 23.01 — depth filter: 'behind' = slot above Greg's center,
    // 'front' = at/below. ('all' draws everything — legacy path.)
    if (which === 'behind' && slot.y >= player.y) continue;
    if (which === 'front' && slot.y < player.y) continue;

    // World → screen
    let screenX = (slot.x - camera.x) * camera.zoom + camera.viewportWidth / 2;
    let screenY = (slot.y - camera.y) * camera.zoom + camera.viewportHeight / 2;

    // Frustum culling
    if (screenX < -50 || screenX > camera.viewportWidth + 50 ||
        screenY < -50 || screenY > camera.viewportHeight + 50) continue;

    // BETA 22.69 — aimAngle now eases to the rest pose in the update tick, so
    // the render always follows it (no more hard swap the frame a target
    // dies). Orbitals keep the legacy expression (they counter-rotate anyway).
    const drawAngle = def.orbital
      ? (weapon.hasTarget ? weapon.aimAngle : slot.angle)
      : weapon.aimAngle;

    // BETA 22.69 — recoil kick: sprite shoves backward along -aim, fast kick
    // for the first quarter of the duration then eased settle. RENDER-ONLY —
    // slotPos/barrelOrigin math never sees this offset.
    const _recoilT = weapon.recoilTimer ?? 0;
    if (_recoilT > 0 && vis.recoilDistance > 0) {
      const t = 1 - _recoilT / vis.recoilDuration;      // 0 → 1 over the kick
      const k = t < 0.25 ? t / 0.25 : 1 - Math.pow((t - 0.25) / 0.75, 1.6);
      const kick = -vis.recoilDistance * k * camera.zoom;
      screenX += Math.cos(drawAngle) * kick;
      screenY += Math.sin(drawAngle) * kick;
    }
    // Charge-up anticipation: steady draw-back while winding up (Railgun/Ultra Beam)
    if ((weapon.chargeUpRemaining ?? 0) > 0 && vis.recoilDistance > 0) {
      const pull = -vis.recoilDistance * 0.5 * camera.zoom;
      screenX += Math.cos(drawAngle) * pull;
      screenY += Math.sin(drawAngle) * pull;
    }
    // Idle bob: alive wobble perpendicular to aim, only with no target
    if (!weapon.hasTarget && vis.idleBobAmount > 0) {
      const bob = Math.sin(state.elapsedTime * vis.idleBobSpeed + (weapon.idleBobPhase ?? 0))
        * vis.idleBobAmount * camera.zoom;
      screenX += Math.cos(drawAngle + Math.PI / 2) * bob;
      screenY += Math.sin(drawAngle + Math.PI / 2) * bob;
    }

    // BETA 13.96 — Poop Sling: swap to "fired" sprite for the duration of firedFlashTimer.
    // Idle = loaded pouch (poop pulled left), brief flash = fired (empty, slung right).
    // BETA 22.21 — if this gun went into the run as its MODDED version, draw the
    // exact kitted combo sprite (normalized to the base framing) instead of base.
    let _moddedSpriteUrl: string | null = null;
    try {
      const _wid = weapon.definitionId;
      if (isArmed(_wid)) {
        const _codes = equippedCodes(_wid);
        if (_codes.length > 0) _moddedSpriteUrl = modComboImage(_wid, _codes);
      }
    } catch { /* mod resolver not ready */ }
    const _activeSpriteUrl = _moddedSpriteUrl
      ? _moddedSpriteUrl
      : (def.firedSpriteUrl && weapon.firedFlashTimer > 0) ? def.firedSpriteUrl : def.spriteUrl;
    const sprite = getWeaponSprite(_activeSpriteUrl);
    // BETA 14.19 — Optional spriteScale multiplier (set via BarrelCalibrator
    // scale slider). Multiplies the visible sprite size; barrelLength/offsetY
    // are calibrated relative to this scaled sprite so projectiles still
    // spawn from the visible tip.
    const _scale = def.spriteScale ?? 1.0;
    const sprH = def.spriteSize * camera.zoom * _scale * HELD_WEAPON_SCALE;

    // BETA 22.78 — drop shadow: gun-shaped black silhouette, offset down-right
    // in SCREEN space (so it reads the same no matter which way the gun aims),
    // drawn BEFORE the gun so it sits behind it. Skipped for orbital (Karen).
    if (sprite && !def.orbital) {
      const _sh = getWeaponShadow(_activeSpriteUrl, sprite);
      if (_sh) {
        const _asp = sprite.naturalWidth / sprite.naturalHeight;
        const _sw = sprH * _asp;
        const _sfy = Math.cos(drawAngle) < 0 ? -1 : 1;
        const _sfx = def.spriteFacesLeft ? -1 : 1;
        // BETA 22.79 — Dylan: shadow was too far from the gun; pull it in tight
        // (~half the offset) + a touch darker, just for weapon contrast.
        // BETA 23.15 — offset flipped to UP-right: gun shadows obey the same
        // NE law as every prop/machine/player shadow (sun low in the SW).
        const _off = Math.max(1.5, sprH * 0.045);
        ctx.save();
        ctx.translate(screenX + _off * 0.7, screenY - _off);
        ctx.rotate(drawAngle);
        if (def.spriteRotationOffset) ctx.rotate(-def.spriteRotationOffset);
        ctx.scale(_sfx, _sfy);
        ctx.globalAlpha = 0.44;
        ctx.drawImage(_sh, -_sw * 0.35, -sprH / 2, _sw, sprH);
        ctx.restore();
      }
    }

    ctx.save();
    ctx.translate(screenX, screenY);
    ctx.rotate(drawAngle);
    // BETA 14.11 — Source-PNG rotation correction. Some sprites are drawn
    // at an angle in the source asset (Ultra Beam: barrel points up-right at
    // ~-45°). Without correction, in-game the barrel ends up rotated by both
    // (drawAngle) AND the source-angle, so it never aligns with the aim
    // direction. Counter-rotating by spriteRotationOffset cancels the source
    // angle, leaving the barrel along +X = aim direction in canvas.
    if (def.spriteRotationOffset) {
      ctx.rotate(-def.spriteRotationOffset);
    }

    if (sprite) {
      // Sprites in the asset pack point left→right (barrel to the right);
      // rotation aligns the barrel with drawAngle.
      const aspect = sprite.naturalWidth / sprite.naturalHeight;
      const sprW = sprH * aspect;
      // If the weapon is on the left half of the orbit, flip vertically so
      // the gun appears upright instead of upside-down.
      const flipY = Math.cos(drawAngle) < 0 ? -1 : 1;
      // BETA 13.99 — sprites that face LEFT in source PNG (Frozen Darts, Ex Girlfriend)
      // need a horizontal flip so the barrel points along the aim direction.
      const flipX = def.spriteFacesLeft ? -1 : 1;
      ctx.scale(flipX, flipY);

      // BETA 13.96 — orbital weapons (Karen Drones) render upright facing the player,
      // not rotated along orbit tangent. The base ctx.rotate(drawAngle) earlier puts
      // her sideways otherwise. Counter-rotate to keep her upright.
      if (def.orbital) {
        ctx.rotate(-drawAngle); // cancels the outer rotate so she stays vertical
        ctx.drawImage(sprite, -sprW / 2, -sprH / 2, sprW, sprH);
      } else {
        ctx.drawImage(sprite, -sprW * 0.35, -sprH / 2, sprW, sprH);
      }

      // BETA 13.96 — 4 spinning propellers overlaid on Karen's wrists + ankles.
      // Each propeller is a "+" shape rotating fast, drawn in the weapon-local space.
      if (def.orbital && def.orbitalPropellers) {
        const propPositions: Array<[number, number]> = [
          // Wrists (upper, slightly outward)
          [-sprW * 0.32, -sprH * 0.15],
          [+sprW * 0.32, -sprH * 0.15],
          // Ankles (lower, slightly inward)
          [-sprW * 0.18, +sprH * 0.38],
          [+sprW * 0.18, +sprH * 0.38],
        ];
        const propSpinAngle = state.elapsedTime * 28; // fast spin (28 rad/s)
        const propSize = sprH * 0.12;
        ctx.fillStyle = 'rgba(255,255,255,0.92)';
        ctx.strokeStyle = 'rgba(60,60,90,0.95)';
        ctx.lineWidth = Math.max(1, sprH * 0.012);
        for (const [px, py] of propPositions) {
          ctx.save();
          ctx.translate(px, py);
          ctx.rotate(propSpinAngle);
          // Plus-shape propeller: 2 rectangles
          ctx.fillRect(-propSize, -propSize * 0.18, propSize * 2, propSize * 0.36);
          ctx.fillRect(-propSize * 0.18, -propSize, propSize * 0.36, propSize * 2);
          ctx.strokeRect(-propSize, -propSize * 0.18, propSize * 2, propSize * 0.36);
          ctx.strokeRect(-propSize * 0.18, -propSize, propSize * 0.36, propSize * 2);
          // Hub
          ctx.beginPath();
          ctx.arc(0, 0, propSize * 0.22, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(40,40,60,0.95)';
          ctx.fill();
          ctx.fillStyle = 'rgba(255,255,255,0.92)';
          ctx.restore();
        }
      }
    } else {
      // Fallback: small dark rectangle so something is visible while the sprite loads
      ctx.fillStyle = '#222';
      ctx.fillRect(-sprH * 0.6, -sprH * 0.15, sprH * 1.2, sprH * 0.3);
    }

    ctx.restore();

    // BETA 22.70 — Firestarter: CONTINUOUS flame cone at the nozzle every
    // frame it has a target (Dylan: "flame thrower is continuous fire that
    // looks like fire") — the hose never visibly stops between damage shots.
    if (weapon.definitionId === 'fireball_wand' && weapon.hasTarget) {
      const _fTip = getBarrelTipOffset(def, drawAngle, weapon);
      drawFlameCone(
        ctx,
        screenX + _fTip.dx * camera.zoom,
        screenY + _fTip.dy * camera.zoom,
        drawAngle,
        camera.zoom,
        state.elapsedTime,
        FLAME_CONE_LEN,
        FLAME_CONE_HALF_ANGLE,
      );
    }

    // BETA 22.86 — CHARGE-GLOW: energy visibly gathers at the muzzle while a
    // charge-up weapon winds up (Railgun / Ultra Beam). Bible: "the railgun
    // should telegraph its shot — you feel it charge." A growing electric orb +
    // converging sparks + a hot core, ramping 0→1 with the charge, so the blast
    // reads as EARNED, not instant. Pure VFX, drawn at the true muzzle socket
    // (same getBarrelTipOffset the projectile uses). No shadowBlur (13.70 rule)
    // — glow via layered radial-gradient discs.
    const _chgRem = weapon.chargeUpRemaining ?? 0;
    const _chgTot = weapon.chargeUpTotal ?? 0;
    if (_chgRem > 0 && _chgTot > 0 && !def.orbital) {
      const chg = Math.min(1, Math.max(0, 1 - _chgRem / _chgTot));   // 0 → 1
      const cTip = getBarrelTipOffset(def, drawAngle, weapon);
      const mx = screenX + cTip.dx * camera.zoom;
      const my = screenY + cTip.dy * camera.zoom;
      const isBeam = def.id === 'wpn_ultra_beam';
      const coreCol = isBeam ? 'rgba(230,210,255,'  : 'rgba(220,240,255,';  // ultra=violet-white, rail=blue-white
      const glowCol = isBeam ? 'rgba(150,90,255,'   : 'rgba(74,168,255,';
      const flick = 0.82 + 0.18 * Math.sin(state.elapsedTime * 42);
      const R = (3 + 17 * chg) * camera.zoom * flick;
      ctx.save();
      // outer glow disc (radial gradient — no blur)
      const g = ctx.createRadialGradient(mx, my, 0, mx, my, R);
      g.addColorStop(0,   coreCol + (0.85 * (0.4 + 0.6 * chg)) + ')');
      g.addColorStop(0.4, glowCol + (0.45 * (0.4 + 0.6 * chg)) + ')');
      g.addColorStop(1,   glowCol + '0)');
      ctx.fillStyle = g;
      ctx.beginPath(); ctx.arc(mx, my, R, 0, Math.PI * 2); ctx.fill();
      // converging sparks — short segments raining INWARD toward the muzzle,
      // shorter as the charge tops out (energy pulled in tight).
      const SPK = 6;
      ctx.strokeStyle = coreCol + (0.5 + 0.4 * chg) + ')';
      ctx.lineWidth = Math.max(1, 1.4 * camera.zoom);
      ctx.lineCap = 'round';
      for (let k = 0; k < SPK; k++) {
        const a = (k / SPK) * Math.PI * 2 + state.elapsedTime * 6 + chg * 3;
        const inR = R * (1.6 - 0.9 * chg);           // starts far, closes in
        const outR = inR + R * (0.7 * (1 - chg) + 0.15);
        ctx.beginPath();
        ctx.moveTo(mx + Math.cos(a) * outR, my + Math.sin(a) * outR);
        ctx.lineTo(mx + Math.cos(a) * inR,  my + Math.sin(a) * inR);
        ctx.stroke();
      }
      // hot core dot — grows with charge
      ctx.fillStyle = coreCol + '0.95)';
      ctx.beginPath(); ctx.arc(mx, my, (1.4 + 3.2 * chg) * camera.zoom, 0, Math.PI * 2); ctx.fill();
      ctx.restore();
    }

    // BETA 22.69 — DEBUG SOCKET OVERLAY (?dev=1 → backtick panel toggle).
    // Red dot = muzzle socket, blue dot = pivot (rotation origin), thin line
    // = aim direction from the muzzle. Uses the SAME getBarrelTipOffset the
    // projectile spawn uses, so what you see IS where bullets come from.
    if (debugWeaponSockets && !def.orbital) {
      const tip = getBarrelTipOffset(def, drawAngle, weapon);
      const mx = screenX + tip.dx * camera.zoom;
      const my = screenY + tip.dy * camera.zoom;
      ctx.save();
      ctx.strokeStyle = 'rgba(255,80,60,0.85)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(mx, my);
      ctx.lineTo(mx + Math.cos(drawAngle) * 90, my + Math.sin(drawAngle) * 90);
      ctx.stroke();
      ctx.fillStyle = '#3b82f6';
      ctx.beginPath(); ctx.arc(screenX, screenY, 3, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#ef4444';
      ctx.beginPath(); ctx.arc(mx, my, 3, 0, Math.PI * 2); ctx.fill();
      ctx.restore();
    }
    } // end inner for-position (BETA 16.24 multi-drone)
  }
}

// ─── Aura Weapon Rendering ───────────────────────────────────
// BETA 13.70 FPS: gradient cache. Recreated only when discR or ctx changes.
// Color stops are FULL strength; breathe alpha applied via globalAlpha at draw time.
// Gradients are origin-relative (0,0) and the ctx is translated to player position.
let _auraScreenCache: { discR: number; ctx: CanvasRenderingContext2D; base: CanvasGradient; shim: CanvasGradient } | null = null;
let _auraWorldCache:  { discR: number; ctx: CanvasRenderingContext2D; base: CanvasGradient; shim: CanvasGradient } | null = null;
function _buildAuraGradients(ctx: CanvasRenderingContext2D, discR: number) {
  // BETA 20.98 — BACK TO BLUE. Dylan: "i liked the blue." The 20.72 gold
  // recolor (+ sunburst rays + coin glints) made the aura wash out gold coins
  // and chests inside its radius (gold-on-gold = invisible) and added the
  // "wired triangles" he didn't want. Restored the exact pre-20.72 blue
  // gradient (these values are the originals from git). Gradient cache pattern
  // untouched.
  const base = ctx.createRadialGradient(0, 0, 0, 0, 0, discR);
  base.addColorStop(0,    'rgba(100,200,255,0.37)');
  base.addColorStop(0.75, 'rgba(70,160,255,0.32)');
  base.addColorStop(0.92, 'rgba(50,140,255,0.12)');
  base.addColorStop(1.0,  'rgba(30,110,255,0)');
  // Shim is also 0,0-anchored; we translate ctx to shim position before drawing.
  const shim = ctx.createRadialGradient(0, 0, 0, 0, 0, discR * 0.65);
  shim.addColorStop(0,   'rgba(180,230,255,0.22)');
  shim.addColorStop(0.5, 'rgba(120,200,255,0.10)');
  shim.addColorStop(1.0, 'rgba(80,160,255,0)');
  return { base, shim };
}

// BETA 21.01 — deleted the dead renderAuraWeapons() (deep-audit: zero callers
// since the live aura moved to renderAuraInWorldSpace below). It was the last
// place still holding the 20.72 gold ring — removing it finishes the blue
// revert and leaves no gold-aura code floating. _buildAuraGradients (above) +
// _auraScreenCache are still used by renderAuraInWorldSpace.

/**
 * Render aura in WORLD SPACE — call INSIDE the camera transform so world coords
 * are used directly. No manual coordinate conversion. Call BEFORE player sprite.
 */
export function renderAuraInWorldSpace(
  state: WeaponSystemState,
  ctx: CanvasRenderingContext2D,
  zoom: number,
  player: { x: number; y: number },
): void {
  for (const weapon of state.weapons) {
    const def = getWeaponDef(weapon.definitionId);
    if (!def?.aura) continue;

    const cx = player.x;
    const cy = player.y;
    const discR = weapon.projectileSize; // world units — camera transform handles zoom

    const t = Date.now() / 1000;
    const breathe  = 0.85 + 0.15 * (0.5 + 0.5 * Math.sin(t * 2.5));
    const shimAngle = t * (Math.PI * 2 / 4);
    const shimX = cx + Math.cos(shimAngle) * discR * 0.45;
    const shimY = cy + Math.sin(shimAngle) * discR * 0.45;

    ctx.save();

    // BETA 13.70 FPS: cached gradient + translated ctx + stroke ring (no shadowBlur).
    if (!_auraWorldCache || _auraWorldCache.discR !== discR || _auraWorldCache.ctx !== ctx) {
      _auraWorldCache = { discR, ctx, ..._buildAuraGradients(ctx, discR) };
    }

    ctx.translate(cx, cy);
    ctx.globalAlpha = breathe;
    ctx.fillStyle = _auraWorldCache.base;
    ctx.beginPath();
    ctx.arc(0, 0, discR, 0, Math.PI * 2);
    ctx.fill();

    ctx.translate(shimX - cx, shimY - cy);
    ctx.fillStyle = _auraWorldCache.shim;
    ctx.beginPath();
    ctx.arc(0, 0, discR, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1.0;

    ctx.translate(cx - shimX, cy - shimY);
    ctx.strokeStyle = `rgba(80,180,255,${0.25 * breathe})`; // BETA 20.98 — blue ring restored (was 20.72 gold)
    ctx.lineWidth = 2 / zoom; // world-space stroke must invert zoom to stay visible at consistent thickness
    ctx.beginPath();
    ctx.arc(0, 0, discR * 0.92, 0, Math.PI * 2);
    ctx.stroke();

    // BETA 20.98 — REMOVED the 20.72 gold sunburst rays ("wired triangles") +
    // coin glints. Dylan: "i liked the blue" — restored to the clean blue disc
    // + ring. The rays/glints also added clutter over the loot in the aura.
    ctx.globalAlpha = 1.0;

    ctx.restore();
  }
}

// ═══════════════════════════════════════════════════════════════
//  HUD HELPERS
// ═══════════════════════════════════════════════════════════════

/** Get reload progress for a weapon (0 to 1, 1 = ready) */
export function getReloadProgress(weapon: WeaponInstance): number {
  if (!weapon.isReloading) return 1;
  const def = getWeaponDef(weapon.definitionId);
  if (!def) return 1;
  return 1 - (weapon.reloadTimer / weapon.reloadTime);
}

/** Get ammo display string (e.g., "4 / 6") */
export function getAmmoDisplay(weapon: WeaponInstance): string {
  return `${weapon.currentAmmo} / ${weapon.projectileCount}`;
}

/** Get DPS estimate for a weapon */
export function getEstimatedDPS(weapon: WeaponInstance): number {
  const def = getWeaponDef(weapon.definitionId);
  if (!def) return 0;

  const burstTime = weapon.projectileCount * def.burstDelay;
  const cycleTime = burstTime + weapon.reloadTime;
  const avgDamagePerShot = weapon.damage * (1 + weapon.critChance * weapon.critDamage);
  const shotsPerCycle = weapon.projectileCount;

  return Math.round((avgDamagePerShot * shotsPerCycle) / cycleTime);
}

/** Get weapon stats formatted for UI display */
export function getWeaponStatsForUI(weapon: WeaponInstance): {
  name: string;
  level: number;
  maxLevel: number;
  rarity: WeaponRarity;
  damage: number;
  critChance: string;
  critDamage: string;
  projectileCount: number;
  reloadTime: string;
  bounces: number;
  dps: number;
} {
  const def = getWeaponDef(weapon.definitionId);
  return {
    name: def?.name ?? 'Unknown',
    level: weapon.level,
    maxLevel: def?.maxLevel ?? 40,
    rarity: weapon.rarity,
    damage: weapon.damage,
    critChance: `${Math.round(weapon.critChance * 100)}%`,
    critDamage: `+${Math.round(weapon.critDamage * 100)}%`,
    projectileCount: weapon.projectileCount,
    reloadTime: `${weapon.reloadTime.toFixed(2)}s`,
    bounces: weapon.projectileBounces,
    dps: getEstimatedDPS(weapon),
  };
}
